
import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useSyncExternalStore, useCallback } from 'react';
import { FEATURES } from '../services/features';
import { dropPlaceholderUsers } from '../services/placeholderUsers';
import type { StoreTempManager, TransactionReceipt, PackingHouse, PackingHouseMember, PackingClaim, RunWarning, ProductUnitKind } from '../types';
import { closeGapPlan, nextStopTag, stopPhrase, STOP_STATUSES, type StopOrder } from '../services/stopNumbers';
import { markDeliveredBlocker, markDeliveredErrorText } from '../services/orderDelivery';
import { sbPackingHouseFromRow, sbPackingHouseMemberFromRow, isHouseMember, canJoinPackingHouse } from '../services/packingHouses';
import { CLAIMS_KEEP_DAYS, claimRefusalText, claimsLiveFilter, sbClaimFromRow } from '../services/packingClaims';
import { fmtQty } from '../pages/warehouseCopy';
import { sbStoreTempManagerFromRow, tempManagerIdsForStore, isTempManagerOf, storesManagedBy, withManagerAdded, withManagersRemoved, legacyMirrorFor } from '../services/tempManagers';
import { hasStoreAccess, noStoreAccessMessage, isStoreAccessRefusal } from '../services/storeAccess';
import { AppState, User, Product, StoreStock, DriverStock, StockTransaction, Order, OrderItem, UserRole, TransactionType, TransactionStatus, OrderStatus, Toast, WarehouseHistoryAction, WarehouseHistoryEntry, StoreHistoryAction, StoreHistoryEntry, DriverHistoryAction, DriverHistoryEntry, MovementType, Movement, MessageChannel, TwilioNumber, ChatRoom, InboundLot, LotOrigin, ProductionRun, ProductionOutput, LotStatus, LotAssignment, AssignmentStatus, InputSource, RunStatus, LotReturn, ReturnKind, ReturnStatus } from '../types';
import { db, auth, firebaseConfig, ensureFirebaseInitialized } from '../services/firebase';
import { appTodayStr } from '../services/appTime';
import { PACKAGING_HOUSE_SHELF_DRAW, canReceivePackagingMaterial, canAssignDirectFromWarehouse } from '../services/warehousePolicy';
import { fmtLb } from '../pages/units';
import firebase from 'firebase/compat/app';
import { INITIAL_PRODUCTS } from '../services/mockData';
import { isSuperTier } from '../services/roles';

const AppContext = createContext<AppState | undefined>(undefined);

// ── Selective subscription (see useAppSelector / useAppActions below) ────────
// A plain useApp() consumer re-renders on EVERY Firestore snapshot because the
// context value object is rebuilt each render. These two extra contexts let a
// component subscribe to just one slice (useAppSelector) or to the stable
// action functions (useAppActions) so it re-renders only when it must — the fix
// for the whole-app re-render churn that makes navigation feel slow.
type AppStore = { subscribe: (cb: () => void) => () => void; getSnapshot: () => AppState };
const AppSelectorContext = createContext<AppStore | null>(null);
const AppActionsContext = createContext<AppState | null>(null);

// ── Live driver positions — a separate channel (see useDriverLocations) ──────
// users docs carry lastLocation, rewritten ~every 60s per on-duty driver. If
// positions stayed on the users array, every GPS ping would change its identity
// and re-render the whole app. Instead the users listener strips them into this
// external store; only useDriverLocations() subscribers (the delivery map)
// re-render on a ping. Mirrors the mobile app's DriverLocationsContext.
type DriverLocationMap = Record<string, NonNullable<User['lastLocation']>>;
const driverLocationsRef: { current: DriverLocationMap } = { current: {} };
const driverLocationListeners = new Set<() => void>();
const subscribeDriverLocations = (cb: () => void) => {
  driverLocationListeners.add(cb);
  return () => { driverLocationListeners.delete(cb); };
};
const publishDriverLocations = (next: DriverLocationMap) => {
  // Replaced wholesale on change, stable reference between changes — the
  // getSnapshot contract useSyncExternalStore needs (same pattern as storeRef).
  driverLocationsRef.current = next;
  driverLocationListeners.forEach((l) => l());
};

// Phone helpers — single source of truth (mirrors mobile services/phone.ts)
import { normalizePhone, toE164 } from '../services/phone';
import { visibleOrdersFor } from '../services/orderScope';
import { DETAILS_LOCKED_STATUSES, detailsOnly, driverEditText } from '../services/orderDetailsEdit';
import { sinceCashStart } from '../services/cashStart';
import { roleBindsTable, roleBindsWarehouse } from '../services/liveTablePolicy';
import { onWarehouseTablesWanted, warehouseTablesWanted } from '../services/warehouseTables';
import { markRoomDelivered } from '../services/roomActions';
import { createDeliveryReceiptQueue, type DeliveryReceiptQueue } from '../services/deliveryReceiptQueue';
import { USE_SUPABASE, supabase, subscribeTable, subscribeRow, subscribeJoined, channelResync, syncSupabaseCacheUser, msFromISO, msFromISO0, isoFromMs, numOr0, readListCache, writeListCache, fetchAllRows, mustAffectRows, PG_KEYSET_PAGE, refreshPolledTable, onEmptyRoster } from '../services/supabaseClient';
import { createLiveness } from '../services/chat/liveness';
import { roomSyncSince, roomsListSignature } from '../services/roomActionsParity';
import { USERS_SELECT_WITHOUT_GPS } from '../services/live/driverLocations';

// Rooms-list polling fallback (FEATURES.chatParity). Slower than the open
// room's: this one only has to notice that the list moved, and every signed-in
// tab in the fleet runs it.
// `users` is polled, not bound to Realtime (see services/supabaseClient "polled tables").
const USERS_POLL_MS = 30000;
// Profile bootstrap (see `sessionPending`): one bounded read, retried with backoff.
const PROFILE_READ_TIMEOUT_MS = 12000;
const PROFILE_RETRY_MIN_MS = 2000;
const PROFILE_RETRY_MAX_MS = 30000;
const ROOMS_POLL_TICK_MS = 5000;
const ROOMS_POLL_MIN_BACKOFF_MS = 15000;
const ROOMS_POLL_MAX_BACKOFF_MS = 60000;
// Silence tolerated from a SUBSCRIBED channel before probing. A healthy but
// quiet channel still costs one probe per window (silence is not proof of
// death — see services/chat/liveness.ts), and this one runs in every signed-in
// tab, so the window is longer than the open room's 45 s.
const ROOMS_POLL_SILENT_AFTER_MS = 120000;

// ═══ Supabase mode (USE_SUPABASE) — row mappers + write helpers ═════════════
// Postgres rows are snake_case with timestamptz; the app speaks camelCase with
// epoch-ms numbers. Every mapper below converts with msFromISO/isoFromMs.
// The Firestore code paths further down are untouched and remain the default.

const sbUserFromRow = (r: any): User => {
  const u: User = {
    id: r.id,
    name: r.name,
    role: r.role as UserRole,
    email: r.email,
    phone: r.phone ?? '',
    requiresPasswordReset: !!r.requires_password_reset,
    isArchived: !!r.is_archived,
    isOnDuty: !!r.is_on_duty,
  };
  if (r.address != null) u.address = r.address;
  if (r.designation) u.designation = r.designation; // 0093; absent before the migration
  if (r.placeholder) u.placeholder = true;
  if (r.temp_packer_driver_id != null) u.tempPackerDriverId = r.temp_packer_driver_id;
  // 0084. Absent before the migration is applied — hasStoreAccess() reads absent as true.
  if (typeof r.store_access === 'boolean') u.storeAccess = r.store_access;
  if (r.twilio_number != null) u.twilioNumber = r.twilio_number;
  if (r.last_location) u.lastLocation = r.last_location;
  if (r.last_opened_at) u.lastOpenedAt = r.last_opened_at;
  // 0090 (UPE-119) — absent on a database without the migration.
  if (r.gps_exempt) u.gpsExempt = true;
  if (r.out_of_shift) u.outOfShift = true;
  if (r.out_of_shift_at) u.outOfShiftAt = msFromISO0(r.out_of_shift_at);
  if (r.out_of_shift_by != null) u.outOfShiftBy = r.out_of_shift_by;
  if (r.gps_on_at) u.gpsOnAt = msFromISO0(r.gps_on_at);
  return u;
};

// Self-signup on the Supabase site: a GoTrue account plus its public.users
// row, and nothing in Firebase. Accounts born here follow the convention of
// netlify/functions/supabase-admin-users.js — id = auth_id = the GoTrue uuid.
// (Migrated rows keep their Firebase uid as id; every lookup goes through
// auth_id, so both shapes coexist.)
//
// The browser holds no service-role key, so a GoTrue account created here can
// never be deleted from here; a leftover from an earlier partial run is
// instead re-adopted by signing into it with the same credentials, which is
// what makes a retry work instead of dying on an email that is already taken.
const sbProvisionUser = async (u: {
  email: string; pass: string; name: string; phone: string;
  role: UserRole; requiresPasswordReset: boolean;
}): Promise<boolean> => {
  const { data, error } = await supabase().auth.signUp({ email: u.email, password: u.pass });
  let authId = data?.user?.id;
  if (error || !authId) {
    // Already registered (or a half-provisioned leftover): adopt it. Wrong
    // credentials fail here, so this cannot take over someone else's account.
    const { data: signedIn, error: signInErr } =
      await supabase().auth.signInWithPassword({ email: u.email, password: u.pass });
    if (signInErr || !signedIn?.user) throw new Error(error?.message || 'Supabase sign-up returned no user');
    authId = signedIn.user.id;
  } else if (!data.session) {
    // The users_insert policy resolves auth.uid(), so the profile row can only
    // be written from a live session — signUp returns one only when the project
    // has email confirmation off.
    const { data: signedIn, error: signInErr } =
      await supabase().auth.signInWithPassword({ email: u.email, password: u.pass });
    if (signInErr) throw new Error('Account created but must confirm its email before first sign-in');
    // A sessionless signUp on an ALREADY-registered email returns an obfuscated
    // user (GoTrue's enumeration defence) whose id is not the real one, so
    // auth_id has to come from the session that just authenticated.
    if (signedIn?.user?.id) authId = signedIn.user.id;
  }

  // users_insert (0018) admits a live session inserting the row it owns
  // (auth_id = auth.uid()). A failed insert leaves only the GoTrue account,
  // which the retry above re-adopts; sign out so the app does not sit on a
  // session with no profile behind it.
  const { error: insErr } = await supabase().from('users').insert({
    id: authId,
    auth_id: authId,
    name: u.name,
    email: u.email,
    role: u.role,
    phone: u.phone || null,
    requires_password_reset: u.requiresPasswordReset,
  });
  if (insErr) {
    await supabase().auth.signOut().catch(() => {});
    throw new Error(`users insert: ${insErr.message}`);
  }
  return true;
};

const sbProductFromRow = (r: any): Product => {
  const p: Product = {
    id: r.id,
    name: r.name,
    category: r.category,
    price: numOr0(r.price),
    warehouseStock: numOr0(r.warehouse_stock),
    isActive: !!r.is_active,
  };
  if (r.sku != null) p.sku = r.sku;
  const created = msFromISO(r.created_at);
  if (created != null) p.createdAt = created;
  // 0112: the unit only when the row has one (a database without 0112 simply has no unit).
  if (r.unit_kind != null && r.unit_amount != null) {
    p.unitKind = r.unit_kind;
    p.unitAmount = numOr0(r.unit_amount);
  }
  return p;
};

const sbProductRow = (p: Product): Record<string, unknown> => ({
  id: p.id,
  name: p.name,
  sku: p.sku ?? null,
  category: p.category,
  price: p.price,
  warehouse_stock: p.warehouseStock,
  is_active: p.isActive ?? true,
  created_at: isoFromMs(p.createdAt ?? null),
  // 0112: written only when the caller carries a unit — a save that does not know it (an older screen, the
  // phone, a deactivate) leaves the column as it is instead of clearing it.
  ...(p.unitKind !== undefined ? { unit_kind: p.unitKind ?? null, unit_amount: p.unitAmount ?? null } : {}),
});

// A database without 0112 has no unit columns: the save is retried without them, so a deploy that reaches a
// project before its migration never breaks adding or editing a product (the unit then waits for 0112).
const UNIT_COLUMN_MISSING = /unit_(kind|amount)|PGRST204|42703/i;
const withoutUnit = (row: Record<string, unknown>): Record<string, unknown> => {
  const { unit_kind: _k, unit_amount: _a, ...rest } = row;
  return rest;
};
const sbWriteProduct = async (
  write: (row: Record<string, unknown>) => PromiseLike<{ error: { message: string; code?: string } | null }>,
  row: Record<string, unknown>,
) => {
  const first = await write(row);
  if (!first.error || !('unit_kind' in row) || !UNIT_COLUMN_MISSING.test(`${first.error.code ?? ''} ${first.error.message}`)) return first;
  return write(withoutUnit(row));
};

const sbStoreStockFromRow = (r: any): StoreStock => ({
  storeId: r.store_id, productId: r.product_id, quantity: numOr0(r.quantity),
});
const sbDriverStockFromRow = (r: any): DriverStock => ({
  driverId: r.driver_id, productId: r.product_id, quantity: numOr0(r.quantity),
});

const sbTwilioFromRow = (r: any): TwilioNumber => {
  const n: TwilioNumber = {
    id: r.id,
    number: r.number,
    assignedDriverId: r.assigned_driver_id ?? null,
    isDefault: !!r.is_default,
    addedAt: msFromISO0(r.added_at),
  };
  if (r.label != null) n.label = r.label;
  if (r.added_by != null) n.addedBy = r.added_by;
  return n;
};

// Expects `order_items(product_id, quantity)` and `order_photos(kind, url)`
// embedded on the row (PostgREST relation embedding).
const sbOrderFromRow = (r: any): Order => {
  const o: Order = {
    id: r.id,
    customerName: r.customer_name,
    address: r.address,
    items: (r.order_items || []).map((i: any) => ({ productId: i.product_id, quantity: numOr0(i.quantity) })),
    status: r.status as OrderStatus,
    requiresCash: !!r.requires_cash,
    cashAmount: numOr0(r.cash_amount),
    requiresETransfer: !!r.requires_etransfer,
    eTransferAmount: numOr0(r.etransfer_amount),
    createdAt: msFromISO0(r.created_at),
  };
  if (r.order_number != null) o.orderNumber = r.order_number;
  if (r.address_verified != null) o.addressVerified = r.address_verified;
  if (r.address_kept != null) o.addressKept = r.address_kept;
  if (r.formatted_address != null) o.formattedAddress = r.formatted_address;
  if (r.address_lat != null) o.addressLat = r.address_lat;
  if (r.address_lng != null) o.addressLng = r.address_lng;
  if (r.address_place_id != null) o.addressPlaceId = r.address_place_id;
  if (r.store_id != null) o.storeId = r.store_id;
  if (r.driver_id != null) o.driverId = r.driver_id;
  if (r.bags != null) o.bags = numOr0(r.bags);
  if (r.priority != null) o.priority = r.priority;
  if (r.store_confirmed_pickup != null) o.storeConfirmedPickup = r.store_confirmed_pickup;
  if (r.order_type != null) o.orderType = r.order_type;
  if (r.require_photo != null) o.requirePhoto = r.require_photo;
  if (r.source != null) o.source = r.source;
  if (r.caller_phone != null) o.callerPhone = r.caller_phone;
  if (r.call_id != null) o.callId = r.call_id;
  if (r.customer_phone != null) o.customerPhone = r.customer_phone;
  if (r.customer_email != null) o.customerEmail = r.customer_email;
  if (r.board_card_id != null) o.boardCardId = r.board_card_id;
  if (r.board_order_number != null) o.boardOrderNumber = r.board_order_number;
  const cancelledAt = msFromISO(r.cancelled_at); // 0097
  if (cancelledAt != null) o.cancelledAt = cancelledAt;
  if (r.cancelled_by != null) o.cancelledBy = r.cancelled_by;
  if (r.is_night) o.isNight = true; // 0096/0098 — the NIGHT tag (the form's tick, or the night card it came from)
  if (r.notes != null) o.notes = r.notes;
  const deliveredAt = msFromISO(r.delivered_at);
  if (deliveredAt != null) o.deliveredAt = deliveredAt;
  if (r.delivered_by != null) o.deliveredBy = r.delivered_by; // 0105: the office marked it delivered
  if (r.driver_return_confirmed != null) o.driverReturnConfirmed = r.driver_return_confirmed;
  if (r.dispatch_id != null) o.dispatchId = r.dispatch_id;
  if (r.created_by != null) o.createdBy = r.created_by;
  if (r.transfer_pending != null) o.transferPending = r.transfer_pending;
  if (r.transfer_from_driver_id != null) o.transferFromDriverId = r.transfer_from_driver_id;
  if (r.transfer_to_driver_id != null) o.transferToDriverId = r.transfer_to_driver_id;
  if (r.transfer_from_approved != null) o.transferFromApproved = r.transfer_from_approved;
  if (r.transfer_to_approved != null) o.transferToApproved = r.transfer_to_approved;
  const tApproved = msFromISO(r.transfer_approved_at);
  if (tApproved != null) o.transferApprovedAt = tApproved;
  if (r.fulfilled_from_driver != null) o.fulfilledFromDriver = r.fulfilled_from_driver;
  const photos: Array<{ kind: string; url: string }> = r.order_photos || [];
  const handoffs = photos.filter(p => p.kind === 'handoff').map(p => p.url);
  const delivery = photos.find(p => p.kind === 'delivery');
  if (delivery) o.deliveryPhoto = delivery.url;
  if (handoffs.length) { o.handoffPhoto = handoffs[0]; o.handoffPhotos = handoffs; }
  return o;
};

const sbOrderRow = (o: Order): Record<string, unknown> => ({
  id: o.id,
  order_number: o.orderNumber ?? null,
  customer_name: o.customerName,
  address: o.address,
  address_verified: o.addressVerified ?? null,
  address_kept: o.addressKept ?? null,
  formatted_address: o.formattedAddress ?? null,
  address_lat: o.addressLat ?? null,
  address_lng: o.addressLng ?? null,
  address_place_id: o.addressPlaceId ?? null,
  status: o.status,
  store_id: o.storeId || null,
  driver_id: o.driverId || null,
  requires_cash: !!o.requiresCash,
  cash_amount: o.cashAmount ?? 0,
  requires_etransfer: !!o.requiresETransfer,
  etransfer_amount: o.eTransferAmount ?? 0,
  bags: o.bags ?? null,
  priority: o.priority ?? null,
  store_confirmed_pickup: o.storeConfirmedPickup ?? null,
  order_type: o.orderType ?? null,
  require_photo: o.requirePhoto ?? null,
  source: o.source ?? null,
  caller_phone: o.callerPhone ?? null,
  call_id: o.callId ?? null,
  customer_phone: o.customerPhone ?? null,
  customer_email: o.customerEmail ?? null,
  notes: o.notes ?? null,
  created_at: isoFromMs(o.createdAt) ?? isoFromMs(Date.now()),
  delivered_at: isoFromMs(o.deliveredAt ?? null),
  driver_return_confirmed: o.driverReturnConfirmed ?? null,
  dispatch_id: o.dispatchId || null,
  created_by: o.createdBy || null,
  transfer_pending: o.transferPending ?? null,
  transfer_from_driver_id: o.transferFromDriverId || null,
  transfer_to_driver_id: o.transferToDriverId || null,
  transfer_from_approved: o.transferFromApproved ?? null,
  transfer_to_approved: o.transferToApproved ?? null,
  transfer_approved_at: isoFromMs(o.transferApprovedAt ?? null),
  fulfilled_from_driver: o.fulfilledFromDriver ?? null,
  // 0095: only an order created from a board card carries the link; the database validates it,
  // copies the card's number into board_order_number and links the card back. Never null-ed here.
  ...(o.boardCardId ? { board_card_id: o.boardCardId } : {}),
  // 0098: the New Order form's Night tick (written only when set — a whole-row write never clears
  // it; the Edit form's change goes through the changed-fields patch).
  ...(o.isNight ? { is_night: true } : {}),
});

// Write the orders row and replace its order_items (rule: items live in
// order_items; Firestore stored them inline on the doc).
// create:true INSERTs (dispatch-tier action); otherwise a plain UPDATE — the
// orders RLS lets the assigned driver / the order's store update, but INSERT
// is dispatch-only, so an upsert here failed the store handoff on the
// insert WITH CHECK even though the update itself was allowed.
const sbWriteOrder = async (o: Order, opts?: { create?: boolean }): Promise<void> => {
  const sb = supabase();
  if (opts?.create) {
    const { error } = await sb.from('orders').insert(sbOrderRow(o));
    if (error) throw new Error(`orders insert: ${error.message}`);
    // A new order has no items or photos to clear: insert only (two empty DELETE round trips saved,
    // Phase 1 2026-09-30 — they made New Order feel slow).
    await sbReplaceOrderItems(o.id, o.items, { fresh: true });
    await sbReplaceOrderPhotos(o, { fresh: true });
    return;
  } else {
    const { id: _id, created_at: _created, ...row } = sbOrderRow(o) as Record<string, unknown>;
    const { error } = await sb.from('orders').update(row).eq('id', o.id);
    if (error) throw new Error(`orders update: ${error.message}`);
  }
  await sbReplaceOrderItems(o.id, o.items);
  await sbReplaceOrderPhotos(o);
};

// Replace an order's order_items rows with `items` (delete + insert). Only
// called when the caller actually changed the items — every extra call opens a
// moment with zero rows that a concurrent re-read can catch (and re-save).
const sbReplaceOrderItems = async (orderId: string, items: OrderItem[], opts?: { fresh?: boolean }): Promise<void> => {
  const sb = supabase();
  if (!opts?.fresh) {
    const { error: delErr } = await sb.from('order_items').delete().eq('order_id', orderId);
    if (delErr) throw new Error(`order_items delete: ${delErr.message}`);
  }
  if (items.length) {
    // PK is (order_id, product_id) — merge duplicate productIds Firestore allowed.
    const merged = new Map<string, number>();
    for (const it of items) merged.set(it.productId, (merged.get(it.productId) || 0) + it.quantity);
    const { error: insErr } = await sb.from('order_items').insert(
      Array.from(merged, ([product_id, quantity]) => ({ order_id: orderId, product_id, quantity }))
    );
    if (insErr) throw new Error(`order_items insert: ${insErr.message}`);
  }
};

// Photos: Firestore kept them inline on the doc, so a full-object write was
// also the photo write. Mirror that by replacing the order_photos rows with
// what the object carries (handoff photos from StoreHub, proof-of-delivery).
const sbReplaceOrderPhotos = async (o: Order, opts?: { fresh?: boolean }): Promise<void> => {
  const sb = supabase();
  const photoUrls = new Set<string>();
  const photoRows: Array<{ order_id: string; kind: string; url: string }> = [];
  const addPhoto = (kind: string, url?: string | null) => {
    if (url && !photoUrls.has(`${kind}|${url}`)) {
      photoUrls.add(`${kind}|${url}`);
      photoRows.push({ order_id: o.id, kind, url });
    }
  };
  addPhoto('delivery', o.deliveryPhoto);
  for (const url of o.handoffPhotos?.length ? o.handoffPhotos : [o.handoffPhoto]) addPhoto('handoff', url);
  if (!opts?.fresh) {
    const { error: phDelErr } = await sb.from('order_photos').delete().eq('order_id', o.id);
    if (phDelErr) throw new Error(`order_photos delete: ${phDelErr.message}`);
  }
  if (photoRows.length) {
    const { error: phInsErr } = await sb.from('order_photos').insert(photoRows);
    if (phInsErr) throw new Error(`order_photos insert: ${phInsErr.message}`);
  }
};

// camelCase Order field → orders column (partial-update mapper)
const SB_ORDER_COLS: Record<string, string> = {
  orderNumber: 'order_number', customerName: 'customer_name', address: 'address',
  addressVerified: 'address_verified', addressKept: 'address_kept',
  formattedAddress: 'formatted_address', addressLat: 'address_lat',
  addressLng: 'address_lng', addressPlaceId: 'address_place_id',
  status: 'status', storeId: 'store_id', driverId: 'driver_id',
  requiresCash: 'requires_cash', cashAmount: 'cash_amount',
  requiresETransfer: 'requires_etransfer', eTransferAmount: 'etransfer_amount',
  bags: 'bags', priority: 'priority', storeConfirmedPickup: 'store_confirmed_pickup',
  orderType: 'order_type', requirePhoto: 'require_photo', source: 'source',
  callerPhone: 'caller_phone', callId: 'call_id', customerPhone: 'customer_phone',
  customerEmail: 'customer_email', notes: 'notes',
  deliveredAt: 'delivered_at', driverReturnConfirmed: 'driver_return_confirmed',
  dispatchId: 'dispatch_id', createdBy: 'created_by',
  transferPending: 'transfer_pending', transferFromDriverId: 'transfer_from_driver_id',
  transferToDriverId: 'transfer_to_driver_id', transferFromApproved: 'transfer_from_approved',
  transferToApproved: 'transfer_to_approved', transferApprovedAt: 'transfer_approved_at',
  fulfilledFromDriver: 'fulfilled_from_driver',
  cancelledAt: 'cancelled_at', cancelledBy: 'cancelled_by', // 0097 soft delete
  isNight: 'is_night', // 0098: the NIGHT tag — the form's tick; shared with the order's board card
};
const SB_ORDER_TS_FIELDS = new Set(['deliveredAt', 'transferApprovedAt', 'cancelledAt']);

// Local echo of a confirmed order save (Umer, 2026-09-28): whoever assigns, unassigns, confirms or
// returns an order sees it on their own screen at once, instead of waiting for the live update (which
// lags under load — a store assignment showed as Pending for a long while). Nothing extra reaches the
// database or Realtime: the same UPDATE returns only the id, and only a save the database confirmed
// (a row came back) is echoed. The live row replaces the echo when it arrives. The provider listens.
let orderFieldsSaved: ((orderId: string, patch: Record<string, any>) => void) | null = null;

// The echo is held over every live list until that list shows it (Umer, 2026-09-28: a busy or lagging
// feed would otherwise paint the old copy back when ANOTHER order's update arrives first). Per field:
// live = saved value → caught up; live = the value before the save → stale, keep the echo on top;
// anything else → someone changed it since, the database wins. Never held past ECHO_HOLD_MS.
// A value this tab saved EARLIER in the hold counts as stale too (2026-10-01: a tag typed in two bursts
// flashed back to the first burst when that save's live update arrived after the second save).
export const ECHO_HOLD_MS = 30_000;
export type OrderEcho = { patch: Record<string, any>; before: Record<string, any>; at: number; earlier?: Record<string, any[]> };
const echoValue = (k: string, v: any) => (v == null || (v === '' && (k.endsWith('Id') || k === 'createdBy')) ? undefined : v);
const sameValue = (a: any, b: any) =>
  a === b || (a == null && b == null) || (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
export const applyOrderEcho = (o: Order, patch: Record<string, any>): Order => {
  const next: Record<string, any> = { ...o };
  for (const [k, v] of Object.entries(patch)) {
    const x = echoValue(k, v);
    if (x === undefined) delete next[k]; else next[k] = x;
  }
  return next as Order;
};
/** 'seen' — the live row shows the save; 'stale' — it still shows the old values; 'other' — someone changed it since. */
export const echoState = (o: Order, e: Pick<OrderEcho, 'patch' | 'before' | 'earlier'>): 'seen' | 'stale' | 'other' => {
  let stale = false;
  for (const [k, v] of Object.entries(e.patch)) {
    const live = echoValue(k, (o as any)[k]);
    if (sameValue(live, echoValue(k, v))) continue;
    if (sameValue(live, echoValue(k, e.before[k]))) { stale = true; continue; }
    if (e.earlier?.[k]?.some((x) => sameValue(live, echoValue(k, x)))) { stale = true; continue; }
    return 'other';
  }
  return stale ? 'stale' : 'seen';
};
/** A live list with the held echoes on top; drops the ones caught up, overtaken or expired. */
export const withOrderEchoes = (rows: Order[], echoes: Map<string, OrderEcho>, now: number): Order[] => {
  if (!echoes.size) return rows;
  let out = rows;
  for (const [id, e] of [...echoes]) {
    if (now - e.at > ECHO_HOLD_MS) { echoes.delete(id); continue; }
    const i = out.findIndex((o) => o.id === id);
    if (i < 0) continue;
    const state = echoState(out[i], e);
    if (state !== 'stale') { echoes.delete(id); continue; }
    if (out === rows) out = rows.slice();
    out[i] = applyOrderEcho(out[i], e.patch);
  }
  return out;
};

/** A save the database did not make (Phase 1, 2026-09-30): the row changed a moment ago, is not yours to
 *  change, or the condition in the WHERE clause no longer holds. Never shown as success. */
export class OrderNotSavedError extends Error { name = 'OrderNotSavedError'; }
export const ORDER_NOT_SAVED = 'The order was not saved — it changed a moment ago or you can no longer change it. Refresh and try again.';

/** Conditions the row must still meet for the write to land (the precondition goes in the WHERE clause,
 *  never a check on this tab's copy): column → value, `null` = IS NULL; `not` = must differ. */
export type OrderWriteGuard = { eq?: Record<string, string | boolean | null>; not?: Record<string, string>; notIn?: Record<string, readonly string[]>; notTrue?: string[]; notSaved?: string };

/** A guarded write landed no row: did its own condition fail (someone got there first — the action's own
 *  sentence is true), or was the row gone or no longer this user's to change (then the general one)? One read,
 *  only on this failure path. Before (Phase 1 audit, 2026-09-30) a driver taken off an order heard "delivered
 *  already" for an order nobody had delivered. */
const guardFailed = async (orderId: string, guard: OrderWriteGuard): Promise<boolean> => {
  const cols = [...new Set([...Object.keys(guard.eq || {}), ...Object.keys(guard.not || {}), ...Object.keys(guard.notIn || {}), ...(guard.notTrue || [])])];
  if (!cols.length) return false;
  try {
    const { data } = await supabase().from('orders').select(cols.join(',')).eq('id', orderId).maybeSingle();
    if (!data) return false;
    const r = data as unknown as Record<string, unknown>;
    return Object.entries(guard.eq || {}).some(([c, v]) => (v === null ? r[c] != null : r[c] !== v))
      || Object.entries(guard.not || {}).some(([c, v]) => r[c] === v)
      || Object.entries(guard.notIn || {}).some(([c, vs]) => vs.includes(r[c] as string))
      || (guard.notTrue || []).some((c) => r[c] === true);
  } catch {
    return false;
  }
};

// Partial camelCase patch → orders UPDATE (+ order_photos deletes when a
// photo field is nulled, e.g. reverseOrder clearing proof-of-delivery).
// A write that returns no row throws OrderNotSavedError (Phase 1, 2026-09-30): it used to return normally,
// so every caller showed success for a save that never happened.
const sbUpdateOrderFields = async (orderId: string, patch: Record<string, any>, guard?: OrderWriteGuard): Promise<void> => {
  const sb = supabase();
  const row: Record<string, unknown> = {};
  const photoKinds: string[] = [];
  for (const [k, v] of Object.entries(patch)) {
    if (k === 'deliveryPhoto') { if (v == null) photoKinds.push('delivery'); continue; }
    if (k === 'handoffPhoto' || k === 'handoffPhotos') { if (v == null) photoKinds.push('handoff'); continue; }
    const col = SB_ORDER_COLS[k];
    if (!col) continue;
    if (SB_ORDER_TS_FIELDS.has(k)) row[col] = isoFromMs(v);
    // FK columns: Firestore code writes '' for "unset" (e.g. dispatchId) — null in Postgres.
    else if (v === '' && (col.endsWith('_id') || col === 'created_by')) row[col] = null;
    else row[col] = v ?? null;
  }
  if (Object.keys(row).length) {
    let q = sb.from('orders').update(row).eq('id', orderId);
    for (const [col, v] of Object.entries(guard?.eq || {})) q = v === null ? q.is(col, null) : q.eq(col, v);
    for (const [col, v] of Object.entries(guard?.not || {})) q = q.neq(col, v);
    for (const [col, vs] of Object.entries(guard?.notIn || {})) q = q.not(col, 'in', `(${vs.join(',')})`);
    for (const col of guard?.notTrue || []) q = q.not(col, 'is', true);   // false or NULL
    const { data, error } = await q.select('id');
    if (error) throw new Error(`orders update: ${error.message}`);
    if (!Array.isArray(data) || !data.length) {
      throw new OrderNotSavedError(guard?.notSaved && await guardFailed(orderId, guard) ? guard.notSaved : ORDER_NOT_SAVED);
    }
    orderFieldsSaved?.(orderId, patch);
  }
  for (const kind of photoKinds) {
    const { error } = await sb.from('order_photos').delete().eq('order_id', orderId).eq('kind', kind);
    if (error) throw new Error(`order_photos delete: ${error.message}`);
  }
};

// Columns updateOrder never writes. Assignment / ownership / transfer state is
// owned by the assign, unassign, transfer and return helpers (partial patches);
// identity and creation stamps are immutable. A caller's spread copy of the
// order may predate any of those changes and must not undo them.
const ORDER_PROTECTED_KEYS = new Set<string>([
  'id', 'createdAt', 'orderNumber',
  'storeId', 'driverId', 'dispatchId', 'createdBy', 'fulfilledFromDriver',
  'storeConfirmedPickup', 'driverReturnConfirmed',
  'transferPending', 'transferFromDriverId', 'transferToDriverId',
  'transferFromApproved', 'transferToApproved', 'transferApprovedAt',
  'boardCardId', 'boardOrderNumber', // 0095: the board link is write-once
]);
const ORDER_PHOTO_KEYS = ['deliveryPhoto', 'handoffPhoto', 'handoffPhotos'];
const NO_PRODUCTS_TO_ASSIGN = 'This order has no products — edit it and add a product first.';

const sameOrderItems = (a: OrderItem[] | undefined, b: OrderItem[] | undefined): boolean => {
  const x = a ?? [], y = b ?? [];
  return x.length === y.length && x.every((it, i) => it.productId === y[i].productId && it.quantity === y[i].quantity);
};
const sameList = (a: unknown, b: unknown): boolean =>
  JSON.stringify(Array.isArray(a) ? a : a == null ? [] : [a]) === JSON.stringify(Array.isArray(b) ? b : b == null ? [] : [b]);

// The fields of `next` that differ from `prev` (the row we hold), minus the
// protected columns. Without a held row (an order outside the loaded window)
// every unprotected field is written, as the whole-row write used to.
// `priority: undefined` on a cleared pill counts as a change to null.
export const orderChangedFields = (prev: Order | undefined, next: Order): Record<string, any> => {
  const patch: Record<string, any> = {};
  for (const [k, v] of Object.entries(next)) {
    if (ORDER_PROTECTED_KEYS.has(k)) continue;
    if (!prev) { patch[k] = v; continue; }
    const pv = (prev as any)[k];
    if (k === 'items') { if (!sameOrderItems(pv, v as OrderItem[])) patch.items = v; continue; }
    if (k === 'handoffPhotos') { if (!sameList(pv, v)) patch[k] = v; continue; }
    if ((pv ?? null) !== (v ?? null)) patch[k] = v;
  }
  return patch;
};

// Expects `transaction_items(product_id, quantity, received_quantity)` embedded on the row.
const sbTxFromRow = (r: any): StockTransaction => {
  const t: StockTransaction = {
    id: r.id,
    type: r.type as TransactionType,
    status: r.status as TransactionStatus,
    storeId: r.store_id ?? '',
    items: (r.transaction_items || []).map((i: any) => {
      const item: StockTransaction['items'][number] = { productId: i.product_id, quantity: numOr0(i.quantity) };
      if (i.received_quantity != null) item.receivedQuantity = numOr0(i.received_quantity);
      return item;
    }),
    timestamp: msFromISO0(r.created_at),
  };
  if (r.driver_id != null) t.driverId = r.driver_id;
  if (r.from_driver_id != null) t.fromDriverId = r.from_driver_id;
  if (r.to_driver_id != null) t.toDriverId = r.to_driver_id;
  if (r.cash_amount != null) t.cashAmount = numOr0(r.cash_amount);
  if (r.created_by != null) t.createdBy = r.created_by;
  if (r.source_run_id != null) t.sourceRunId = r.source_run_id;
  if (r.discrepancy) t.discrepancy = true;
  if (r.discrepancy_note != null) t.discrepancyNote = r.discrepancy_note;
  return t;
};

const sbWriteTransaction = async (t: StockTransaction): Promise<void> => {
  const sb = supabase();
  const { error } = await sb.from('transactions').upsert({
    id: t.id,
    type: t.type,
    status: t.status,
    store_id: t.storeId || null,
    driver_id: t.driverId || null,
    from_driver_id: t.fromDriverId || null,
    to_driver_id: t.toDriverId || null,
    cash_amount: t.cashAmount ?? null,
    created_by: t.createdBy ?? null,
    created_at: isoFromMs(t.timestamp) ?? isoFromMs(Date.now()),
    // Set when these packs came off a lot directly (0056) — kept on a re-send
    // after a refusal so the transfer keeps its "Direct from warehouse" trail.
    source_run_id: t.sourceRunId ?? null,
  });
  if (error) throw new Error(`transactions upsert: ${error.message}`);
  const { error: delErr } = await sb.from('transaction_items').delete().eq('transaction_id', t.id);
  if (delErr) throw new Error(`transaction_items delete: ${delErr.message}`);
  if (t.items.length) {
    const merged = new Map<string, number>();
    for (const it of t.items) merged.set(it.productId, (merged.get(it.productId) || 0) + it.quantity);
    const { error: insErr } = await sb.from('transaction_items').insert(
      Array.from(merged, ([product_id, quantity]) => ({ transaction_id: t.id, product_id, quantity }))
    );
    if (insErr) throw new Error(`transaction_items insert: ${insErr.message}`);
  }
};

// ── Warehouse pipeline rows ────────────────────────────────────────────────
// The jsonb columns (received_by / assigned_by / input_sources / outputs /
// derived_from / …) hold the app's camelCase shapes verbatim, so they pass
// straight through. Everything else is the usual snake_case + timestamptz
// conversion, and NULL maps back to "field absent" so the derivations that
// test for undefined (lot.countUnit, rt.status, run.rejectionReason) behave
// like they do on the Firestore docs.
const sbLotFromRow = (r: any): InboundLot => {
  const l: InboundLot = {
    id: r.id,
    name: r.name,
    totalGrams: numOr0(r.total_grams),
    remainingGrams: numOr0(r.remaining_grams),
    status: r.status as LotStatus,
    receivedAt: msFromISO0(r.received_at),
    receivedBy: r.received_by,
  };
  if (r.notes != null) l.notes = r.notes;
  if (r.pack_count != null) l.packCount = numOr0(r.pack_count);
  if (r.pack_size_grams != null) l.packSizeGrams = numOr0(r.pack_size_grams);
  if (r.count_unit != null) l.countUnit = r.count_unit;
  if (r.adjusted_grams != null && numOr0(r.adjusted_grams) !== 0) l.adjustedGrams = numOr0(r.adjusted_grams);
  if (r.box_count != null) l.boxCount = numOr0(r.box_count);
  if (r.units_per_box != null) l.unitsPerBox = numOr0(r.units_per_box);
  const closedAt = msFromISO(r.closed_at);
  if (closedAt != null) l.closedAt = closedAt;
  if (r.closed_by != null) l.closedBy = r.closed_by;
  if (r.variance_grams != null) l.varianceGrams = numOr0(r.variance_grams);
  if (r.origin != null) l.origin = r.origin as LotOrigin;
  if (r.derived_from != null) l.derivedFrom = r.derived_from;
  if (r.skips_packaging) l.skipsPackaging = true;
  return l;
};

const sbLotRow = (l: InboundLot): Record<string, unknown> => ({
  id: l.id,
  name: l.name,
  total_grams: l.totalGrams,
  remaining_grams: l.remainingGrams,
  skips_packaging: l.skipsPackaging ?? false,
  status: l.status,
  received_at: isoFromMs(l.receivedAt),
  received_by: l.receivedBy,
  notes: l.notes ?? null,
  pack_count: l.packCount ?? null,
  pack_size_grams: l.packSizeGrams ?? null,
  count_unit: l.countUnit ?? null,
  box_count: l.boxCount ?? null,
  units_per_box: l.unitsPerBox ?? null,
  origin: l.origin ?? null,
  derived_from: l.derivedFrom ?? null,
});

const sbAssignmentFromRow = (r: any): LotAssignment => {
  const a: LotAssignment = {
    id: r.id,
    lotId: r.lot_id,
    lotName: r.lot_name,
    workerId: r.worker_id,
    workerName: r.worker_name,
    grantedGrams: numOr0(r.granted_grams),
    remainingGrams: numOr0(r.remaining_grams),
    status: r.status as AssignmentStatus,
    assignedAt: msFromISO0(r.assigned_at),
    assignedBy: r.assigned_by,
  };
  if (r.count_unit != null) a.countUnit = r.count_unit;
  if (r.house_id != null) { a.houseId = r.house_id; a.houseName = r.house_name ?? r.worker_name; }
  const closedAt = msFromISO(r.closed_at);
  if (closedAt != null) a.closedAt = closedAt;
  if (r.notes != null) a.notes = r.notes;
  if (Array.isArray(r.target_product_ids) && r.target_product_ids.length) a.targetProductIds = r.target_product_ids.map(String);
  return a;
};

// Exported for test/context/warehouseRows.test.ts. Both builders feed only the warehouse RPCs, whose
// jsonb_populate_record ignores a key the database lacks — so the 0117 columns below are safe to send
// to a database without 0117, and must be sent to one with it: a key left out comes back NULL there,
// and 0117 made both columns NOT NULL ("null value in column warnings", Send to Store, 2026-10-07).
export const sbAssignmentRow = (a: LotAssignment): Record<string, unknown> => ({
  id: a.id,
  lot_id: a.lotId,
  lot_name: a.lotName,
  worker_id: a.workerId,
  worker_name: a.workerName,
  granted_grams: a.grantedGrams,
  remaining_grams: a.remainingGrams,
  count_unit: a.countUnit ?? null,
  status: a.status,
  assigned_at: isoFromMs(a.assignedAt),
  assigned_by: a.assignedBy,
  closed_at: isoFromMs(a.closedAt ?? null),
  notes: a.notes ?? null,
  house_id: a.houseId ?? null,
  house_name: a.houseName ?? null,
  target_product_ids: a.targetProductIds ?? [],
});

const sbRunFromRow = (r: any): ProductionRun => {
  const run: ProductionRun = {
    id: r.id,
    workerId: r.worker_id,
    workerName: r.worker_name,
    performedAt: msFromISO0(r.performed_at),
    performedBy: r.performed_by,
    inputSources: r.input_sources || [],
    totalGramsConsumed: numOr0(r.total_grams_consumed),
    outputs: r.outputs || [],
    crumbsGrams: numOr0(r.crumbs_grams),
    wasteGrams: numOr0(r.waste_grams),
    variance: numOr0(r.variance),
    status: r.status as RunStatus,
  };
  const confirmedAt = msFromISO(r.confirmed_at);
  if (confirmedAt != null) run.confirmedAt = confirmedAt;
  if (r.confirmed_by != null) run.confirmedBy = r.confirmed_by;
  const transferredAt = msFromISO(r.transferred_at);
  if (transferredAt != null) run.transferredAt = transferredAt;
  if (r.transferred_by != null) run.transferredBy = r.transferred_by;
  if (r.rejection_reason != null) run.rejectionReason = r.rejection_reason;
  if (r.notes != null) run.notes = r.notes;
  if (r.direct_assign != null) run.directAssign = r.direct_assign;
  if (r.house_id != null) { run.houseId = r.house_id; run.houseName = r.house_name ?? r.worker_name; }
  if (r.claim_id != null) run.claimId = r.claim_id;
  if (Array.isArray(r.warnings) && r.warnings.length) run.warnings = r.warnings;
  if (r.lot_adjust_grams != null) run.lotAdjustGrams = numOr0(r.lot_adjust_grams);
  return run;
};

export const sbRunRow = (r: ProductionRun): Record<string, unknown> => ({
  id: r.id,
  worker_id: r.workerId,
  worker_name: r.workerName,
  performed_at: isoFromMs(r.performedAt),
  performed_by: r.performedBy,
  input_sources: r.inputSources,
  total_grams_consumed: r.totalGramsConsumed,
  outputs: r.outputs,
  crumbs_grams: r.crumbsGrams,
  waste_grams: r.wasteGrams,
  variance: r.variance,
  status: r.status,
  confirmed_at: isoFromMs(r.confirmedAt ?? null),
  confirmed_by: r.confirmedBy ?? null,
  transferred_at: isoFromMs(r.transferredAt ?? null),
  transferred_by: r.transferredBy ?? null,
  rejection_reason: r.rejectionReason ?? null,
  notes: r.notes ?? null,
  direct_assign: r.directAssign ?? null,
  house_id: r.houseId ?? null,
  house_name: r.houseName ?? null,
  warnings: r.warnings ?? [],
});

const sbLotReturnFromRow = (r: any): LotReturn => {
  const rt: LotReturn = {
    id: r.id,
    workerId: r.worker_id,
    workerName: r.worker_name,
    lotId: r.lot_id,
    lotName: r.lot_name,
    kind: r.kind as ReturnKind,
    grams: numOr0(r.grams),
    returnedAt: msFromISO0(r.returned_at),
  };
  if (r.notes != null) rt.notes = r.notes;
  if (r.status != null) rt.status = r.status as ReturnStatus;
  const confirmedAt = msFromISO(r.confirmed_at);
  if (confirmedAt != null) rt.confirmedAt = confirmedAt;
  if (r.confirmed_by != null) rt.confirmedBy = r.confirmed_by;
  if (r.rejection_reason != null) rt.rejectionReason = r.rejection_reason;
  if (r.house_id != null) { rt.houseId = r.house_id; rt.houseName = r.house_name ?? r.worker_name; }
  return rt;
};

const sbLotReturnRow = (rt: LotReturn): Record<string, unknown> => ({
  id: rt.id,
  worker_id: rt.workerId,
  worker_name: rt.workerName,
  lot_id: rt.lotId,
  lot_name: rt.lotName,
  kind: rt.kind,
  grams: rt.grams,
  returned_at: isoFromMs(rt.returnedAt),
  notes: rt.notes ?? null,
  status: rt.status ?? null,
  confirmed_at: isoFromMs(rt.confirmedAt ?? null),
  confirmed_by: rt.confirmedBy ?? null,
  rejection_reason: rt.rejectionReason ?? null,
  house_id: rt.houseId ?? null,
  house_name: rt.houseName ?? null,
});

// The pipeline's multi-table steps are runTransaction/batch on Firestore. RLS
// runs under the caller (the functions are SECURITY INVOKER), so these are the
// same writes the client would make, just made indivisible — see
// supabase/migrations/0015_warehouse_functions.sql.
const sbWarehouseRpc = async <T,>(fn: string, args: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase().rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
};

// Postgres twin of the approve-transaction Netlify Function: the status flip
// and every per-item stock/history/movement write for a bulk transfer happen
// inside ONE function call, so a closed tab can no longer leave a transfer
// half-applied. Unlike the warehouse_* functions this one is SECURITY DEFINER
// (a store or driver confirming its own loadout may not write store_stocks
// under 0003's RLS) and does its own role check — see
// supabase/migrations/0031_approve_transaction.sql.
// `receipt` (0056) carries the receiver's counts when they differ from what
// was sent; the function stamps received_quantity + the discrepancy flag and
// credits the destination with what was actually counted.
//
// p_received is sent ONLY when there is a receipt, and that is deliberate:
// PostgREST resolves an RPC by the exact set of argument names in the body,
// so a call carrying p_received 404s against a database that has not run
// 0056 yet. Omitting it on the ordinary path means a clean confirm keeps
// working on both the old and the new function, and the deploy is therefore
// safe in either order — code first or migration first. Verified against
// production (2-arg call resolves, 3-arg call 404s) before the 2026-09-15
// release.
const sbApproveTransaction = async (
  txId: string,
  performedBy: { userId: string; userName: string; role: UserRole },
  receipt?: TransactionReceipt,
): Promise<{ ok: true; items?: number; alreadyApproved?: boolean; discrepancy?: boolean }> =>
  sbWarehouseRpc('approve_transaction', {
    p_tx_id: txId,
    p_performed_by: performedBy,
    ...(receipt ? { p_received: { items: receipt.received, note: receipt.note ?? null } } : {}),
  });

// History tables keep first-class columns + an `extra` jsonb for the
// enrichment fields (movementType/fromEntity/toEntity/…).
const sbHistoryExtra = (entry: Record<string, any>, known: string[]): Record<string, unknown> | null => {
  const extra: Record<string, unknown> = {};
  for (const k of Object.keys(entry)) {
    if (!known.includes(k) && entry[k] !== undefined) extra[k] = entry[k];
  }
  return Object.keys(extra).length ? extra : null;
};

// Multi-table live query — same debounced refetch contract as subscribeTable,
// but with a caller-supplied fetch (needed for PostgREST embeds/joins that
// subscribeTable's plain `select('*')` can't express) and change channels on
// every table the result depends on (e.g. orders + order_items). A table may
// carry a channel filter (`{ table, filter }`) when a row can never leave the
// filtered set — see subscribeTable's `channelFilter` for when that is safe.
const sbSubscribeQuery = <T,>(
  tables: Array<string | { table: string; filter?: string }>,
  fetchRows: () => Promise<T>,
  onData: (data: T) => void,
  onError?: (e: Error) => void,
  // Cache-first (stale-while-revalidate), same idea as subscribeTable: paint
  // the last-known result from localStorage the moment the page mounts, then
  // replace it with the network answer. Without it a cold visit to a site
  // (a fresh origin, a new browser) showed "No conversations" / empty lists
  // for the 3–4 s the first round-trips take from far away. Pass a key only
  // for list-shaped, JSON-safe results; the cache is scoped to the signed-in
  // user by syncSupabaseCacheUser.
  cacheKey?: string,
): (() => void) => {
  let dead = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  if (cacheKey) {
    const cached = readListCache(`q:${cacheKey}`);
    if (cached) { try { onData(cached as unknown as T); } catch { /* stale/malformed cache — ignore */ } }
  }
  const fetchNow = async () => {
    try {
      const data = await fetchRows();
      if (!dead) onData(data);
      if (cacheKey && Array.isArray(data)) writeListCache(`q:${cacheKey}`, data);
    } catch (e: any) {
      onError?.(e);
    }
  };
  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fetchNow, 250);
  };
  fetchNow();
  const channels = tables.map((t) => {
    const { table, filter } = typeof t === 'string' ? { table: t, filter: undefined } : t;
    return supabase()
      .channel(`sub_${table}_${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) }, schedule)
      // Same recovery policy as subscribeTable. This had no status handler at all,
      // so a list built on it silently stopped updating after a Realtime drop.
      .subscribe(channelResync(() => { if (!dead) schedule(); }));
  });
  return () => {
    dead = true;
    if (timer) clearTimeout(timer);
    channels.forEach((ch) => supabase().removeChannel(ch));
  };
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [storeStocks, setStoreStocks] = useState<StoreStock[]>([]);
  const [driverStocks, setDriverStocks] = useState<DriverStock[]>([]);
  const [transactions, setTransactions] = useState<StockTransaction[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  // The confirmed-save echo (sbUpdateOrderFields): patch that one order in place now, and hold it over
  // the live lists until they show it (withOrderEchoes). A null or '' id means "cleared".
  const orderEchoesRef = useRef(new Map<string, OrderEcho>());
  useEffect(() => {
    orderFieldsSaved = (orderId, patch) => setOrders((prev) => {
      const i = prev.findIndex((o) => o.id === orderId);
      if (i < 0) return prev;
      const held = orderEchoesRef.current.get(orderId);
      const before: Record<string, any> = {};
      for (const k of Object.keys(patch)) before[k] = (prev[i] as any)[k];
      const earlier: Record<string, any[]> = { ...(held?.earlier || {}) };
      for (const k of Object.keys(patch)) {
        if (held && k in held.patch) earlier[k] = [...(earlier[k] || []), held.patch[k]]; // this tab's earlier save
      }
      orderEchoesRef.current.set(orderId, {
        patch: { ...(held?.patch || {}), ...patch },
        before: { ...before, ...(held?.before || {}) },   // the first save's "before" wins
        at: Date.now(),
        earlier,
      });
      const copy = prev.slice();
      copy[i] = applyOrderEcho(prev[i], patch);
      return copy;
    });
    return () => { orderFieldsSaved = null; };
  }, []);
  // First-load signal for the Orders page: true once the orders subscription
  // (Supabase or Firestore) has delivered its first snapshot, so the page can
  // show a skeleton instead of the "Showing 0 of 0" empty state while fetching.
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [twilioNumbers, setTwilioNumbers] = useState<TwilioNumber[]>([]);
  const [storeTempManagers, setStoreTempManagers] = useState<StoreTempManager[]>([]);
  // Packing Houses + members (0080). Supabase only; empty in Firestore mode.
  const [packingHouses, setPackingHouses] = useState<PackingHouse[]>([]);
  const [packingHouseMembers, setPackingHouseMembers] = useState<PackingHouseMember[]>([]);
  // Claims (0117). Ready once the table answered; before the migration it never does.
  const [packingClaims, setPackingClaims] = useState<PackingClaim[]>([]);
  const [packingClaimsReady, setPackingClaimsReady] = useState(false);
  // Group rooms the current user is a member of (WhatsApp-style group chat).
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [inboundLots, setInboundLots] = useState<InboundLot[]>([]);
  const [lotAssignments, setLotAssignments] = useState<LotAssignment[]>([]);
  const [productionRuns, setProductionRuns] = useState<ProductionRun[]>([]);
  const [lotReturns, setLotReturns] = useState<LotReturn[]>([]);

  const [loading, setLoading] = useState(true);
  // A Supabase session exists but the profile row has not been read yet — the
  // read is in flight, or it failed for a reason that is NOT "no such user" (the
  // database is busy, the network dropped). While this is true the app shows a
  // "Reconnecting…" screen and keeps retrying; it must never fall through to the
  // login page, because the session is alive and a password would change nothing
  // (incident 2026-09-18..20: a Realtime stall sent the whole office to Login).
  const [sessionPending, setSessionPending] = useState(false);
  const retrySessionRef = useRef<() => void>(() => {});
  const retrySession = useCallback(() => retrySessionRef.current(), []);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [dayZeroTimestamp, setDayZeroTimestamp] = useState<number | null>(null);
  const [ordersDateRange, setOrdersDateRange] = useState<{ start: number; end: number } | null>(null);
  const [transactionsDateRange, setTransactionsDateRange] = useState<{ start: number; end: number } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info', opts?: { title?: string; onClick?: () => void }) => {
    const id = Date.now().toString() + Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id, message, type, ...(opts?.title ? { title: opts.title } : {}), ...(opts?.onClick ? { onClick: opts.onClick } : {}) }]);
    setTimeout(() => {
        removeToast(id);
    }, 5000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Dedup fingerprints for the users listener below: JSON keys of the last
  // published stable users slice / locations map / own user doc, so churny
  // snapshot emissions (GPS pings, read-marks) skip setState entirely and the
  // provider never re-renders for them.
  const usersKeyRef = useRef<string | null>(null);
  const locationsKeyRef = useRef<string | null>(null);
  const selfKeyRef = useRef<string | null>(null);
  // Signed-in uid mirror (null when logged out) so the users listener can
  // locate the own doc WITHOUT reading currentUser inside a setState updater —
  // updaters must stay pure (StrictMode double-invokes them in dev, so a ref
  // write inside one makes the second invocation see "unchanged" and lose the
  // update). Kept in lockstep with every setCurrentUser call below.
  const selfIdRef = useRef<string | null>(null);
  // Supabase mode only: the last mapped self users-row (sans GPS) and the
  // user_read_state overlay (lastReadOrders/lastReadChat live in their own
  // table in Postgres, but the app reads them off currentUser). currentUser
  // is republished as {...base, ...overlay} whenever either side changes,
  // deduped through selfKeyRef exactly like the Firestore self-sync below.
  const sbSelfBaseRef = useRef<User | null>(null);
  const sbReadStateRef = useRef<Pick<User, 'lastReadOrders' | 'lastReadChat'>>({});

  useEffect(() => {
    // ── Supabase mode: session restore + realtime lists ─────────────────────
    // Mirrors the Firebase flow below: an auth listener that (re)builds the
    // full listener set per signed-in session and tears it down on sign-out.
    if (USE_SUPABASE) {
      let cleanupListeners: (() => void) | null = null;
      let wiredAuthUid: string | null = null;
      // Bootstrap generation. The profile read below is async, and the old
      // guard only checked `cleanupListeners` — which is assigned AFTER that
      // read. A second auth event for the same user (INITIAL_SESSION then
      // SIGNED_IN, a token refresh, StrictMode's double effect in dev) landing
      // during the read slipped through and wired the whole listener set a
      // second time: every list fetched twice, the first set never torn down.
      // Now: same-uid events are ignored outright, every event bumps the
      // generation, and a bootstrap that finishes for a stale generation (or
      // after this effect was cleaned up) tears its own listeners down again.
      let generation = 0;
      let disposed = false;

      // Republish currentUser from base row + read-state overlay (dedup via selfKeyRef).
      const publishSelf = () => {
        const base = sbSelfBaseRef.current;
        if (!base) return;
        const updatedSelf = { ...base, ...sbReadStateRef.current } as User;
        const selfKey = JSON.stringify(updatedSelf);
        if (selfKeyRef.current !== selfKey) {
          selfKeyRef.current = selfKey;
          setCurrentUser(updatedSelf);
        }
      };

      const buildListeners = (selfId: string, role: UserRole | undefined): (() => void) => {
        orderEchoesRef.current.clear(); // a held echo belongs to the user who saved it
        const onError = (e: Error) => { console.error('Supabase Listener Error:', e); };
        const unsubs: Array<() => void> = [];
        const thirtyDaysMs = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const thirtyDaysIso = isoFromMs(thirtyDaysMs)!;

        // users — strip the high-churn GPS blob into the driver-locations
        // channel and dedup, exactly like the Firestore users listener.
        const onUserRows = (allRows: any[]) => {
          // No rows at all = no live login (RLS answers an empty list, not an
          // error). Never show an empty roster: check the login; a dead one
          // goes to the Login screen, otherwise keep what we have.
          if (allRows.length === 0) {
            console.warn('[AppContext] users read returned no rows — checking the login');
            void onEmptyRoster();
            return;
          }
          // Migration placeholders ("Deleted driver (…)") are dropped here, at
          // the source, so no screen ever lists, picks, counts or names them —
          // see services/placeholderUsers.ts. Their rows stay in Postgres.
          const rows = dropPlaceholderUsers(allRows);
          const nextUsers: User[] = [];
          const nextLocations: DriverLocationMap = {};
          rows.forEach((r) => {
            const { lastLocation, lastOpenedAt: _oa, ...stable } = sbUserFromRow(r);
            nextUsers.push(stable as User);
            if (lastLocation) nextLocations[stable.id] = lastLocation;
          });
          const locKey = JSON.stringify(nextLocations);
          if (locationsKeyRef.current !== locKey) {
            locationsKeyRef.current = locKey;
            publishDriverLocations(nextLocations);
          }
          const usersKey = JSON.stringify(nextUsers);
          if (usersKeyRef.current !== usersKey) {
            usersKeyRef.current = usersKey;
            setUsers(nextUsers);
          }
          const selfRow = selfIdRef.current ? rows.find((r) => r.id === selfIdRef.current) : undefined;
          if (selfRow) {
            // Keep lastOpenedAt on self (Groups unread badge); strip only GPS.
            const { lastLocation: _sl, ...selfStable } = sbUserFromRow(selfRow);
            sbSelfBaseRef.current = selfStable as User;
            publishSelf();
          }
        };
        // POLLED, not live: see "polled tables" in services/supabaseClient.ts.
        // Roster changes are rare; driver GPS arrives on the same poll.
        if (!FEATURES.liveV2Locations) {
          unsubs.push(subscribeTable<any>('users', (r) => r, onUserRows, { onError, live: false, pollMs: USERS_POLL_MS }));
        } else {
          // Live v2: GPS is only read by the Delivery Map, which now gets it on
          // demand (services/live/driverLocations) — so this poll, run by every
          // tab of every role, names its columns and leaves last_location out.
          // A named column this database lacks must never cost the roster: a
          // schema-gap error swaps in the plain select for good.
          let usersUnsub = subscribeTable<any>('users', (r) => r, onUserRows, {
            live: false, pollMs: USERS_POLL_MS, select: USERS_SELECT_WITHOUT_GPS,
            onError: (e) => {
              if (!/does not exist|42703/i.test(String(e?.message || e))) { onError(e); return; }
              usersUnsub();
              usersUnsub = subscribeTable<any>('users', (r) => r, onUserRows, { onError, live: false, pollMs: USERS_POLL_MS });
            },
          });
          unsubs.push(() => usersUnsub());
        }

        // The signed-in user's OWN row stays live, through a channel filtered to
        // that one row (writes to other users never reach it): a role change, an
        // archive or lastOpenedAt from another of their tabs lands at once. It
        // only ever applies the row the event carries — it never fetches; the
        // poll above is the safety net after a dropped channel.
        {
          const selfChannel = supabase()
            .channel(`self_users_${Math.random().toString(36).slice(2, 8)}`)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'users', filter: `id=eq.${selfId}` }, (payload: any) => {
              const row = payload?.new;
              if (!row || row.id !== selfId) return;
              const { lastLocation: _sl, ...selfStable } = sbUserFromRow(row);
              sbSelfBaseRef.current = selfStable as User;
              publishSelf();
            })
            .subscribe();
          unsubs.push(() => { supabase().removeChannel(selfChannel); });
        }

        // Read-state overlay (was users.lastReadOrders / users.lastReadChat).
        // The channel is filtered to THIS user's rows: a read mark is one row
        // per (user, conversation) that never changes hands, and the query
        // below only ever asks for mine — so another person's read marks
        // (written on every chat they open, fleet-wide) no longer re-run this
        // fetch in every tab.
        unsubs.push(sbSubscribeQuery(
          [{ table: 'user_read_state', filter: `user_id=eq.${selfId}` }],
          async () => {
            // Paged: a busy dispatcher's read marks can pass PostgREST's 1000-row cap.
            const data = await fetchAllRows(() => supabase()
              .from('user_read_state')
              .select('conversation_id, channel, last_read_at')
              .eq('user_id', selfId)
              .order('conversation_id'),
              PG_KEYSET_PAGE, { column: 'conversation_id', ascending: true });
            return data || [];
          },
          (rows: any[]) => {
            const lastReadOrders: Record<string, number> = {};
            const lastReadChat: Record<string, Record<string, number>> = {};
            for (const r of rows) {
              const at = msFromISO0(r.last_read_at);
              if (!r.channel) lastReadOrders[r.conversation_id] = at;
              else (lastReadChat[r.conversation_id] = lastReadChat[r.conversation_id] || {})[r.channel] = at;
            }
            sbReadStateRef.current = { lastReadOrders, lastReadChat };
            publishSelf();
          },
          onError,
        ));

        // Per-role table set (services/liveTablePolicy.ts): a role binds only
        // the tables its pages read. Realtime runs a table's policy once per
        // subscriber per change, so a table nobody on this tab can see is pure
        // cost — a packager's tab used to follow orders, twilio numbers and
        // group rooms.
        if (roleBindsTable(role, 'products')) unsubs.push(subscribeTable<Product>('products', sbProductFromRow, setProducts, { onError }));
        if (roleBindsTable(role, 'store_stocks')) unsubs.push(subscribeTable<StoreStock>('store_stocks', sbStoreStockFromRow, setStoreStocks, { onError }));
        if (roleBindsTable(role, 'driver_stocks')) unsubs.push(subscribeTable<DriverStock>('driver_stocks', sbDriverStockFromRow, setDriverStocks, { onError }));
        // Admin Panel only.
        if (roleBindsTable(role, 'twilio_numbers')) unsubs.push(subscribeTable<TwilioNumber>('twilio_numbers', sbTwilioFromRow, setTwilioNumbers, { onError }));
        // Temporary store managers (0050): drivers to find their Store tab,
        // dispatch for the order-card chips, stores for packer attribution.
        if (roleBindsTable(role, 'store_temp_managers')) unsubs.push(subscribeTable<StoreTempManager>('store_temp_managers', sbStoreTempManagerFromRow, setStoreTempManagers, { onError }));
        // Packing Houses (0080): the hub's house switcher, the Warehouse hub's
        // hand-off picker, the Admin Panel card. Two tiny tables.
        if (roleBindsTable(role, 'packing_houses')) unsubs.push(subscribeTable<PackingHouse>('packing_houses', sbPackingHouseFromRow, setPackingHouses, { onError }));
        if (roleBindsTable(role, 'packing_house_members')) unsubs.push(subscribeTable<PackingHouseMember>('packing_house_members', sbPackingHouseMemberFromRow, setPackingHouseMembers, { onError }));

        // Transactions: last 30 days, items embedded from transaction_items.
        // Joined subscription: a change patches ONE transaction by id (items
        // re-read for just that parent) instead of re-downloading the window.
        if (roleBindsTable(role, 'transactions')) {
        const txSelect = '*, transaction_items(product_id, quantity, received_quantity)';
        unsubs.push(subscribeJoined<any, StockTransaction>({
          parent: 'transactions',
          children: [{ table: 'transaction_items', fk: 'transaction_id' }],
          // Paged: 30 days of production transfers can exceed the 1000-row cap.
          fetchAll: () => fetchAllRows(() => supabase()
            .from('transactions')
            .select(txSelect)
            .gt('created_at', thirtyDaysIso)
            .order('created_at', { ascending: false }),
            PG_KEYSET_PAGE, { column: 'created_at', ascending: false }),
          fetchByIds: async (ids) => {
            const { data, error } = await supabase().from('transactions').select(txSelect).in('id', ids);
            if (error) throw new Error(`transactions: ${error.message}`);
            return data || [];
          },
          map: sbTxFromRow,
          onRows: (txs) => setTransactions(txs.sort((a, b) => b.timestamp - a.timestamp)),
          onError,
          cacheKey: 'transactions',
          order: { column: 'created_at', ascending: false },
          accept: (r) => msFromISO0(r.created_at) > thirtyDaysMs,
          client: supabase(),
        }));
        } // roleBindsTable(role, 'transactions')

        // Orders: active + delivered-in-last-30-days (same window as the two
        // Firestore listeners), items/photos embedded. The stock manager gets
        // none (0047), nor do the packaging-house roles (no page reads them).
        if (roleBindsTable(role, 'orders')) {
          // Joined subscription (subscribeJoined): an orders payload patches
          // that one order; an order_items / order_photos payload re-reads
          // only its parent (by order_id). The old query re-downloaded every
          // order in the window on ANY change to any of the three tables.
          const orderSelect = '*, order_items(product_id, quantity), order_photos(kind, url)';
          unsubs.push(subscribeJoined<any, Order>({
            parent: 'orders',
            children: [{ table: 'order_items', fk: 'order_id' }, { table: 'order_photos', fk: 'order_id' }],
            // Paged: active + 30 days of delivered orders is >1000 rows on
            // production, and PostgREST would silently return the first 1000.
            // Keyset, not offset: OFFSET 1000 on this query timed out on
            // production (57014) and took the whole list down with it, so the
            // page showed "0 of 0 orders" with the rows sitting there intact.
            fetchAll: () => fetchAllRows(() => supabase()
              .from('orders')
              .select(orderSelect)
              // Live orders + the last 30 days of delivered and cancelled (0097) ones.
              .or(`and(status.neq.delivered,status.neq.cancelled),delivered_at.gt.${thirtyDaysIso},cancelled_at.gt.${thirtyDaysIso}`)
              .order('created_at', { ascending: false }),
              PG_KEYSET_PAGE, { column: 'created_at', ascending: false }),
            fetchByIds: async (ids) => {
              const { data, error } = await supabase().from('orders').select(orderSelect).in('id', ids);
              if (error) throw new Error(`orders: ${error.message}`);
              return data || [];
            },
            map: sbOrderFromRow,
            onRows: (rows) => { setOrders(withOrderEchoes(rows, orderEchoesRef.current, Date.now())); setOrdersLoaded(true); },
            onError,
            cacheKey: 'orders',
            order: { column: 'created_at', ascending: false },
            // The 30-day window, client-side: a patched order that is delivered
            // and older than the window drops out here; every full resync is
            // the server's word anyway.
            // Cancelled orders (0097) leave the window 30 days after they were cancelled, like delivered ones.
            accept: (r) => r.status === 'delivered' ? msFromISO0(r.delivered_at) > thirtyDaysMs
              : r.status === 'cancelled' ? msFromISO0(r.cancelled_at) > thirtyDaysMs
              : true,
            client: supabase(),
          }));
        }

        // Day Zero timestamp — settings key='dayZero'. Row-filtered on purpose:
        // the settings table also carries the SMS gateway heartbeat
        // (outbound-sms-queue.js stamps smsGateway.lastSeen on every router
        // poll, every few seconds), and the previous unfiltered channel
        // re-fetched dayZero on every one of those stamps. The filtered
        // channel only fires for this row, and its payload already holds the
        // value, so no re-fetch runs at all.
        unsubs.push(subscribeRow<any>(
          'settings',
          { column: 'key', value: 'dayZero' },
          (row) => row?.value ?? null,
          (v: any) => {
            if (v == null) return;
            const ts = typeof v.timestamp === 'number' ? v.timestamp : msFromISO(v.timestamp);
            setDayZeroTimestamp(ts || null);
          },
          { select: 'value', onError, client: supabase() },
        ));

        // Group rooms — membership lives in room_members rows. ONLY the super
        // admin follows every room; admins, like everyone else, see the rooms
        // they were added to (Umer, 2026-09-09: an admin could see every group).
        // Only the roles with a Chat page follow rooms at all (liveTablePolicy).
        if (roleBindsTable(role, 'rooms')) {
        const isAdminTier = isSuperTier(role);
        if (FEATURES.chatEngineV2) {
          // Phase 2b (chatEngineV2): per-row patch instead of the full-refetch
          // below. subscribeJoined patches ONE room on a rooms UPDATE and
          // re-reads ONE room's members (150ms-coalesced) on any membership
          // change — never recomputing the whole joined list. Correctness is
          // guaranteed by PostgREST RLS on fetchByIds (the authoritative
          // visibility re-check), NOT by realtime RLS; any ambiguous payload
          // degrades to a full refetch (the legacy behaviour). Rooms the caller
          // may no longer see resolve to [] from fetchByIds and are dropped.
          type RoomJoinedRow = Record<string, any> & {
            id: string;
            __members?: Array<{ user_id: string; is_admin: boolean }>;
          };
          const mapRoomRow = (r: RoomJoinedRow): ChatRoom => {
            const mem = r.__members ?? [];
            const room: ChatRoom = {
              id: r.id,
              name: r.name,
              createdBy: r.created_by ?? '',
              createdByName: r.created_by_name ?? '',
              createdAt: msFromISO0(r.created_at),
              memberIds: mem.map((m) => m.user_id),
              adminIds: mem.filter((m) => m.is_admin).map((m) => m.user_id),
            };
            const lastAt = msFromISO(r.last_message_at);
            if (lastAt != null) room.lastMessageAt = lastAt;
            if (r.last_message_body != null) room.lastMessageBody = r.last_message_body;
            if (r.last_message_sender_name != null) room.lastMessageSenderName = r.last_message_sender_name;
            if (r.last_message_sender_id != null) room.lastMessageSenderId = r.last_message_sender_id;
            if (r.delivered_at) room.deliveredAt = r.delivered_at;
            if (r.read_at) room.readAt = r.read_at;
            return room;
          };
          // Attach members to already-fetched room rows (one paged read).
          const attachMembers = async (rows: any[]): Promise<RoomJoinedRow[]> => {
            const ids = rows.map((r) => r.id);
            let members: any[] = [];
            if (ids.length) {
              members = await fetchAllRows(() =>
                supabase().from('room_members').select('room_id, user_id, is_admin').in('room_id', ids));
            }
            return rows.map((r) => ({ ...r, __members: members.filter((m) => m.room_id === r.id) }));
          };
          const fetchAll = async (): Promise<RoomJoinedRow[]> => {
            const sb = supabase();
            // Every read here is paged past PostgREST's silent 1000-row cap
            // (fetchAllRows) so no room or member is ever dropped.
            let roomsBuild = () => sb.from('rooms').select('*');
            if (!isAdminTier) {
              let mine: any[];
              try { mine = await fetchAllRows(() => sb.from('room_members').select('room_id').eq('user_id', selfId)); }
              catch (e: any) { throw new Error(`room_members: ${e?.message || e}`); }
              const myRoomIds = mine.map((m: any) => m.room_id);
              if (myRoomIds.length === 0) return [];
              roomsBuild = () => sb.from('rooms').select('*').in('id', myRoomIds);
            }
            let rows: any[];
            try { rows = await fetchAllRows(roomsBuild); }
            catch (e: any) { throw new Error(`rooms: ${e?.message || e}`); }
            return attachMembers(rows);
          };
          // Re-read named rooms; RLS silently drops any the caller may no longer
          // see, so subscribeJoined removes them (its got-set diff). This is what
          // makes "I left the room" and "a foreign room changed" both resolve to
          // a removal / no-op.
          const fetchByIds = async (ids: string[]): Promise<RoomJoinedRow[]> => {
            const rows = await fetchAllRows(() => supabase().from('rooms').select('*').in('id', ids));
            return rows.length ? attachMembers(rows) : [];
          };
          // ── Polling fallback for the rooms LIST (parity) ──────────────────
          // Same failure as the open room: the socket can go quiet while
          // reporting SUBSCRIBED, and the list then stops moving — no new
          // rooms, no preview updates, no unread bumps, no error. `liveness`
          // decides when a catch-up is due; `room_sync_since` answers whether
          // anything actually changed, and only then does the existing full
          // resync run (so a quiet fleet costs one cheap RPC per backoff).
          //
          // `roomIds` is what the poll asks about, kept in step with whatever
          // the subscription last published. A room I am ADDED to while the
          // socket is down is therefore invisible to the poll (it is not in the
          // list yet); it appears on the channel's next re-SUBSCRIBE, which
          // resyncs the whole list. Asking the server for the membership on
          // every poll would cost the fleet a full list read per backoff, which
          // is the load this fallback is specifically shaped to avoid.
          let roomIds: string[] = [];
          const roomsLive = FEATURES.chatParity ? createLiveness({
            silentAfterMs: ROOMS_POLL_SILENT_AFTER_MS,
            minBackoffMs: ROOMS_POLL_MIN_BACKOFF_MS,
            maxBackoffMs: ROOMS_POLL_MAX_BACKOFF_MS,
          }) : null;
          let roomsResync: (() => void) | null = null;
          let roomsSyncAt = Date.now();
          let roomsPolling = false;
          let roomsTimer: ReturnType<typeof setInterval> | null = null;
          // `room_sync_since` filters MESSAGES by the cursor but hands back
          // EVERY room asked about, changed or not — so `rooms.length` is not a
          // "something moved" signal, it is just the room count. Comparing the
          // list half against the previous poll's is: a rename or a preview bump
          // changes the signature, a quiet fleet does not (and then nothing runs
          // — one cheap RPC per backoff, fleet-wide).
          let roomsSig: string | null = null;
          const roomsPoll = async () => {
            if (!roomsLive || roomsPolling) return;
            const ids = roomIds;
            if (!ids.length) { roomsLive.onPolled(Date.now(), true); return; }
            roomsPolling = true;
            const startedAt = Date.now();
            try {
              // Missing 0060 → the fallback IS the existing full resync, so an
              // un-migrated database keeps working (throttled by the backoff).
              const res = await roomSyncSince(ids, Math.max(roomsSyncAt - 5000, 0), () => { roomsResync?.(); });
              if (res) {
                const sig = roomsListSignature(res.rooms);
                // First answer only seeds the signature; `messages` (which IS
                // cursor-filtered) still catches a message sent while the
                // socket was down.
                if (res.messages.length || (roomsSig !== null && sig !== roomsSig)) roomsResync?.();
                roomsSig = sig;
              }
              // The server's clock, not this browser's — see roomSyncSince.
              roomsSyncAt = res?.at || startedAt;
              // A missing RPC means the fallback just ran a FULL list refetch;
              // back off to the maximum rather than repeat that every 15 s.
              roomsLive.onPolled(Date.now(), res !== null);
            } catch {
              roomsLive.onPolled(Date.now(), false); // silent — background work
            } finally {
              roomsPolling = false;
            }
          };
          const roomsForeground = () => {
            roomsLive?.onForeground(typeof document === 'undefined' || document.visibilityState !== 'hidden', Date.now());
          };
          unsubs.push(subscribeJoined<RoomJoinedRow, ChatRoom>({
            parent: 'rooms',
            children: [{ table: 'room_members', fk: 'room_id' }],
            fetchAll,
            fetchByIds,
            map: mapRoomRow,
            onRows: (rs) => { roomIds = rs.map((r) => r.id); setRooms(rs); },
            onError,
            cacheKey: 'rooms', // → 'j:rooms' (no collision with 'orders')
            // NO `order` (RoomsList sorts by lastMessageAt itself) and NO
            // `accept` (no window filter — RLS + fetchByIds are the only gate).
            client: supabase(),
            live: roomsLive ? {
              onStatus: (status) => roomsLive.onStatus(status, Date.now()),
              onPayload: () => { roomsLive.onEvent(Date.now()); roomsSyncAt = Date.now(); },
              onResync: (resync) => { roomsResync = resync; },
            } : undefined,
          }));
          if (roomsLive) {
            roomsForeground();
            roomsTimer = setInterval(() => {
              const now = Date.now();
              const due = roomsLive.nextPollAt(now);
              if (due !== null && due <= now) void roomsPoll();
            }, ROOMS_POLL_TICK_MS);
            document.addEventListener('visibilitychange', roomsForeground);
            window.addEventListener('focus', roomsForeground);
            unsubs.push(() => {
              if (roomsTimer) clearInterval(roomsTimer);
              document.removeEventListener('visibilitychange', roomsForeground);
              window.removeEventListener('focus', roomsForeground);
            });
          }
        } else {
        unsubs.push(sbSubscribeQuery(
          ['rooms', 'room_members'],
          async () => {
            const sb = supabase();
            // Every read here is paged past PostgREST's silent 1000-row cap
            // (fetchAllRows) so no room or member is ever dropped.
            let roomsBuild = () => sb.from('rooms').select('*');
            if (!isAdminTier) {
              let mine: any[];
              try { mine = await fetchAllRows(() => sb.from('room_members').select('room_id').eq('user_id', selfId)); }
              catch (e: any) { throw new Error(`room_members: ${e?.message || e}`); }
              const myRoomIds = mine.map((m: any) => m.room_id);
              if (myRoomIds.length === 0) return [] as ChatRoom[];
              roomsBuild = () => sb.from('rooms').select('*').in('id', myRoomIds);
            }
            let rows: any[];
            try { rows = await fetchAllRows(roomsBuild); }
            catch (e: any) { throw new Error(`rooms: ${e?.message || e}`); }
            const ids = rows.map((r: any) => r.id);
            let members: any[] = [];
            if (ids.length) {
              try { members = await fetchAllRows(() => sb.from('room_members').select('room_id, user_id, is_admin').in('room_id', ids)); }
              catch (e: any) { throw new Error(`room_members: ${e?.message || e}`); }
            }
            return rows.map((r: any): ChatRoom => {
              const mem = members.filter((m) => m.room_id === r.id);
              const room: ChatRoom = {
                id: r.id,
                name: r.name,
                createdBy: r.created_by ?? '',
                createdByName: r.created_by_name ?? '',
                createdAt: msFromISO0(r.created_at),
                memberIds: mem.map((m) => m.user_id),
                adminIds: mem.filter((m) => m.is_admin).map((m) => m.user_id),
              };
              const lastAt = msFromISO(r.last_message_at);
              if (lastAt != null) room.lastMessageAt = lastAt;
              if (r.last_message_body != null) room.lastMessageBody = r.last_message_body;
              if (r.last_message_sender_name != null) room.lastMessageSenderName = r.last_message_sender_name;
              if (r.last_message_sender_id != null) room.lastMessageSenderId = r.last_message_sender_id;
              if (r.delivered_at) room.deliveredAt = r.delivered_at;
              if (r.read_at) room.readAt = r.read_at;
              return room;
            });
          },
          (rs) => setRooms(rs),
          onError,
        ));
        }
        } // roleBindsTable(role, 'rooms')

        // Warehouse pipeline (inbound_lots, lot_assignments, production_runs,
        // lot_returns): NOT bound here. These are whole-table follows that
        // grow forever; the warehouse effect below binds them only while a
        // warehouse page is mounted (services/warehouseTables.ts).

        setLoading(false);
        return () => { unsubs.forEach((u) => u()); };
      };

      let cancelBootstrapRetry: () => void = () => {};
      // Back online → try at once instead of waiting out the backoff.
      const onBackOnline = () => retrySessionRef.current();
      window.addEventListener('online', onBackOnline);
      const { data: authSub } = supabase().auth.onAuthStateChange((_event, session) => {
        if (session?.user) {
          // Ignore token refreshes / repeat events for the same signed-in
          // user — only (re)wire on an actual identity change. (Also while the
          // first bootstrap for this uid is still in flight — see `generation`.)
          if (wiredAuthUid === session.user.id) return;
          if (cleanupListeners) { cleanupListeners(); cleanupListeners = null; }
          cancelBootstrapRetry();
          wiredAuthUid = session.user.id;
          const myGeneration = ++generation;
          // Clear the render cache only if a DIFFERENT user signed in (kept warm
          // across same-user refreshes) so no viewer ever sees another's rows.
          syncSupabaseCacheUser(session.user.id);
          setSessionPending(true);
          const authUid = session.user.id;
          let attempt = 0;
          let retryTimer: ReturnType<typeof setTimeout> | null = null;
          const stillMine = () => !disposed && myGeneration === generation;
          const bootstrap = async (): Promise<void> => {
            if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
            if (!stillMine()) return;
            try {
              // maybeSingle, not single: "no such profile" must be told apart from
              // "the read failed". Bounded, so a hung database is a failure we can
              // retry rather than a spinner that never ends.
              const { data: row, error } = await supabase()
                .from('users').select('*').eq('auth_id', authUid)
                .abortSignal(AbortSignal.timeout(PROFILE_READ_TIMEOUT_MS)).maybeSingle();
              if (!stillMine()) return;
              if (error) throw new Error(error.message || 'profile read failed');
              if (!row) {
                // An auth account with no app profile: genuinely not a user here.
                console.error('[AppContext] no profile row for this auth account');
                wiredAuthUid = null;
                retrySessionRef.current = () => {};
                setSessionPending(false);
                setLoading(false);
                return;
              }
              const { lastLocation: _il, ...initialSelf } = sbUserFromRow(row);
              sbSelfBaseRef.current = initialSelf as User;
              // Seed read-marks BEFORE the first publish so unread badges never
              // flicker: the port loads lastReadChat/lastReadOrders from a separate
              // user_read_state query that used to resolve AFTER setCurrentUser,
              // leaving a window where every already-read chat briefly showed
              // unread. Fetch it up front and merge in — matching main, where the
              // read-marks live on the user row and are present immediately.
              try {
                const { data: rsRows } = await supabase()
                  .from('user_read_state')
                  .select('conversation_id, channel, last_read_at')
                  .eq('user_id', row.id);
                const lastReadOrders: Record<string, number> = {};
                const lastReadChat: Record<string, Record<string, number>> = {};
                for (const rr of (rsRows || [])) {
                  const at = msFromISO0(rr.last_read_at);
                  if (!rr.channel) lastReadOrders[rr.conversation_id] = at;
                  else (lastReadChat[rr.conversation_id] = lastReadChat[rr.conversation_id] || {})[rr.channel] = at;
                }
                sbReadStateRef.current = { lastReadOrders, lastReadChat };
              } catch { /* non-fatal: buildListeners' live user_read_state query fills it shortly after */ }
              // Superseded while we were reading (sign-out, another user, or
              // the effect itself was cleaned up)? Then wire nothing.
              if (disposed || myGeneration !== generation) return;
              const initialUser = { ...initialSelf, ...sbReadStateRef.current } as User;
              selfKeyRef.current = JSON.stringify(initialUser);
              selfIdRef.current = initialUser.id;
              setCurrentUser(initialUser);
              const cleanup = buildListeners(initialUser.id, initialUser.role);
              if (disposed || myGeneration !== generation) { cleanup(); return; }
              cleanupListeners = cleanup;
              setSessionPending(false);
              // Loaded: a later `online` event or a stray Retry must not bootstrap (and
              // wire every listener) a second time.
              retrySessionRef.current = () => {};
            } catch (err) {
              if (!stillMine()) return;
              // The session is alive; only the profile read failed. Stay on the
              // reconnecting screen and try again — 2 s, 4 s, 8 s … capped at 30 s,
              // jittered so a whole office recovering together does not retry in step.
              attempt += 1;
              const base = Math.min(PROFILE_RETRY_MAX_MS, PROFILE_RETRY_MIN_MS * 2 ** (attempt - 1));
              const delay = Math.round(base * (0.75 + Math.random() * 0.5));
              console.error(`[AppContext] profile read failed (session still alive) — retry ${attempt} in ${delay} ms:`, err);
              setLoading(false);
              retryTimer = setTimeout(() => { void bootstrap(); }, delay);
            }
          };
          cancelBootstrapRetry = () => { if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; } };
          retrySessionRef.current = () => { attempt = 0; void bootstrap(); };
          void bootstrap();
        } else {
          generation++; // any bootstrap still in flight must not wire listeners
          cancelBootstrapRetry();
          retrySessionRef.current = () => {};
          setSessionPending(false);
          if (cleanupListeners) { cleanupListeners(); cleanupListeners = null; }
          wiredAuthUid = null;
          selfIdRef.current = null;
          selfKeyRef.current = null;
          sbSelfBaseRef.current = null;
          sbReadStateRef.current = {};
          syncSupabaseCacheUser(null);
          setCurrentUser(null);
          setLoading(false);
        }
      });

      // 3 s is enough to stop the app-level spinner; it was also flipping
      // ordersLoaded, which made the Messages list show "No conversations"
      // whenever the first orders round-trip took longer than that (far-away
      // browsers, cold origins). ordersLoaded now falls back much later — it
      // exists only so a failed orders query can't leave a skeleton forever.
      const safetyTimeout = setTimeout(() => { setLoading(false); }, 3000);
      const ordersSafetyTimeout = setTimeout(() => { setOrdersLoaded(true); }, 15000);

      return () => {
        disposed = true;
        generation++;
        cancelBootstrapRetry();
        window.removeEventListener('online', onBackOnline);
        authSub.subscription.unsubscribe();
        if (cleanupListeners) { cleanupListeners(); cleanupListeners = null; }
        clearTimeout(safetyTimeout);
        clearTimeout(ordersSafetyTimeout);
      };
    }

    try {
      const { db: dbInstance, auth: authInstance } = ensureFirebaseInitialized();
      const errorHandler = (err: any) => {
          console.error("Firebase Listener Error:", err);
          setLoading(false);
      };

      let cleanupListeners: (() => void) | null = null;

      const unsubAuth = authInstance.onAuthStateChanged(async (firebaseUser) => {
          // Tear down listeners from any previous auth session before rebuilding.
          // onAuthStateChanged can fire again (e.g. logout→login without a full
          // page reload); without this, every re-login stacked another duplicate
          // set of ~14 full-collection listeners that were never unsubscribed.
          if (cleanupListeners) { cleanupListeners(); cleanupListeners = null; }
          if (firebaseUser) {
              try {
                  const userDocRef = dbInstance.collection('users').doc(firebaseUser.uid);
                  // The bootstrap profile read is a network call. A transient
                  // failure here (tab waking from Chrome's memory-saver discard,
                  // a network/VPN blip mid-shift) used to fall through to the
                  // catch below and null the user — bouncing a still-signed-in
                  // dispatcher to the Login page ("random logouts"). Retry
                  // briefly before giving up.
                  let userDocSnap = null as firebase.firestore.DocumentSnapshot | null;
                  for (let attempt = 0; attempt < 3; attempt++) {
                      try {
                          userDocSnap = await userDocRef.get();
                          break;
                      } catch (e) {
                          if (attempt === 2) throw e;
                          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
                      }
                  }
                  if (!userDocSnap) throw new Error('profile read failed');

                  if (userDocSnap.exists) {
                      // Strip the high-churn GPS/token fields from currentUser and
                      // seed the dedup key so the first snapshot doesn't force a
                      // vacuous update. lastOpenedAt is KEPT on self — the Groups
                      // "unread since I last opened Messages" badge (RoomsList)
                      // reads currentUser.lastOpenedAt.messages, and it only churns
                      // on tab-open, not on the ~60s GPS cadence.
                      const { lastLocation: _il, fcmTokens: _if, ...initialSelf } = userDocSnap.data() as User;
                      const initialUser = { ...initialSelf, id: userDocSnap.id } as User;
                      selfKeyRef.current = JSON.stringify(initialUser);
                      selfIdRef.current = userDocSnap.id;
                      setCurrentUser(initialUser);
                      const role = (userDocSnap.data() as User)?.role;

                      const unsubUsers = dbInstance.collection('users').onSnapshot((snap) => {
                          // The users docs mix stable identity fields with high-churn
                          // ones: lastLocation (written ~every 60s per on-duty driver),
                          // the lastReadOrders/lastReadChat/lastOpenedAt read-marks
                          // (written every time ANY user opens an order or chat), and
                          // fcmTokens. None of those are read from the `users` array —
                          // read-marks come from `currentUser`, positions from
                          // useDriverLocations() — so strip them here and skip the
                          // setState when the stable slice is unchanged. Without this,
                          // every ping/read-mark by anyone re-rendered the whole app.
                          // (Mirrors the mobile app's users listener.)
                          const nextUsers: User[] = [];
                          const nextLocations: DriverLocationMap = {};
                          snap.docs.forEach(d => {
                              const {
                                  lastLocation,
                                  lastReadOrders: _ro,
                                  lastReadChat: _rc,
                                  lastOpenedAt: _oa,
                                  fcmTokens: _ft,
                                  ...stable
                              } = d.data() as User;
                              nextUsers.push({ ...stable, id: d.id } as User);
                              if (lastLocation) nextLocations[d.id] = lastLocation;
                          });
                          const locKey = JSON.stringify(nextLocations);
                          if (locationsKeyRef.current !== locKey) {
                              locationsKeyRef.current = locKey;
                              publishDriverLocations(nextLocations);
                          }
                          const usersKey = JSON.stringify(nextUsers);
                          if (usersKeyRef.current !== usersKey) {
                              usersKeyRef.current = usersKey;
                              setUsers(nextUsers);
                          }
                          // Keep currentUser in sync with Firestore changes. The
                          // read-marks (lastReadOrders/lastReadChat) are KEPT here —
                          // currentUser is the narrow channel NotificationHandler and
                          // DriverApp read them from — but own GPS pings/tokens are
                          // stripped so they can't churn currentUser identity.
                          // The dedup decision is made HERE, before setState, and the
                          // ref is written outside the updater: StrictMode
                          // double-invokes updaters in dev, so a ref write inside one
                          // makes the second invocation see "unchanged" and lose the
                          // update, freezing currentUser for the session.
                          const selfId = selfIdRef.current;
                          const selfDoc = selfId ? snap.docs.find(d => d.id === selfId) : undefined;
                          if (selfDoc) {
                              // Keep lastOpenedAt on self (drives the Groups unread
                              // badge); strip only the high-churn GPS/token fields.
                              const { lastLocation: _sl, fcmTokens: _sf, ...selfStable } = selfDoc.data() as User;
                              const updatedSelf = { ...selfStable, id: selfDoc.id } as User;
                              // Only update if data actually changed to avoid unnecessary re-renders
                              const selfKey = JSON.stringify(updatedSelf);
                              if (selfKeyRef.current !== selfKey) {
                                  selfKeyRef.current = selfKey;
                                  setCurrentUser(updatedSelf);
                              }
                          }
                      }, errorHandler);

                      const unsubProducts = dbInstance.collection('products').onSnapshot((snap) => {
                          setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() } as Product)));
                      }, errorHandler);

                      const unsubStocks = dbInstance.collection('storeStocks').onSnapshot((snap) => {
                          setStoreStocks(snap.docs.map(d => ({ ...d.data() } as StoreStock)));
                      }, errorHandler);

                      const unsubDriverStocks = dbInstance.collection('driverStocks').onSnapshot((snap) => {
                          setDriverStocks(snap.docs.map(d => ({ ...d.data() } as DriverStock)));
                      }, errorHandler);

                      // Transactions: only load last 30 days by default (not all history)
                      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
                      const unsubTx = dbInstance.collection('transactions')
                          .where('timestamp', '>', thirtyDaysAgo)
                          .onSnapshot((snap) => {
                              const txs = snap.docs.map(d => ({ id: d.id, ...d.data() } as StockTransaction));
                              setTransactions(txs.sort((a,b) => b.timestamp - a.timestamp));
                          }, errorHandler);

                      // Orders: two listeners — active orders + recent delivered (30
                      // days). MANAGER sessions get neither: the role exists to work
                      // the catalog with zero order visibility, and firestore.rules
                      // denies the read anyway (subscribing would only surface
                      // permission errors). orders state stays [] for them.
                      const noopOrders = () => {};
                      let unsubActiveOrders = noopOrders, unsubDeliveredOrders = noopOrders;
                      if (role !== UserRole.STOCK_MANAGER) {
                      const activeStatuses = [
                        OrderStatus.PENDING, OrderStatus.ASSIGNED, OrderStatus.CONFIRMED,
                        OrderStatus.PICKED_UP, OrderStatus.RETURNING_TO_STORE
                      ];
                      unsubActiveOrders = dbInstance.collection('orders')
                          .where('status', 'in', activeStatuses)
                          .onSnapshot((snap) => {
                              const active = snap.docs.map(d => ({ id: d.id, ...d.data() } as Order));
                              setOrders(prev => {
                                const delivered = prev.filter(o => o.status === OrderStatus.DELIVERED);
                                return [...active, ...delivered];
                              });
                              setOrdersLoaded(true);
                          }, errorHandler);

                      unsubDeliveredOrders = dbInstance.collection('orders')
                          .where('status', '==', OrderStatus.DELIVERED)
                          .where('deliveredAt', '>', thirtyDaysAgo)
                          .onSnapshot((snap) => {
                              const recent = snap.docs.map(d => ({ id: d.id, ...d.data() } as Order));
                              setOrders(prev => {
                                const active = prev.filter(o => o.status !== OrderStatus.DELIVERED);
                                return [...active, ...recent];
                              });
                          }, errorHandler);
                      }

                      const unsubDayZero = dbInstance.collection('settings').doc('dayZero').onSnapshot((snap) => {
                          if (snap.exists) {
                              setDayZeroTimestamp(snap.data()?.timestamp || null);
                          }
                      }, errorHandler);

                      const unsubTwilioNumbers = dbInstance.collection('twilioNumbers').onSnapshot((snap) => {
                          setTwilioNumbers(snap.docs.map(d => ({ id: d.id, ...d.data() } as TwilioNumber)));
                      }, errorHandler);

                      // Group rooms. Only the SUPER ADMIN follows EVERY room;
                      // admins and everyone else get only their own via one
                      // array-contains listener (mirrors mobile
                      // contexts/AppContext.tsx; Umer 2026-09-09). If the
                      // all-rooms read is rejected, fall back to the member
                      // view so the list never goes dark.
                      const subscribeMemberRooms = () => dbInstance.collection('rooms')
                          .where('memberIds', 'array-contains', firebaseUser.uid)
                          .onSnapshot((snap) => {
                              setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() } as ChatRoom)));
                          }, errorHandler);
                      let unsubRooms = isSuperTier(role)
                          ? dbInstance.collection('rooms').onSnapshot((snap) => {
                              setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() } as ChatRoom)));
                          }, () => { unsubRooms = subscribeMemberRooms(); })
                          : subscribeMemberRooms();

                      // Warehouse pipeline collections (lots / assignments / runs /
                      // returns) are full-collection listeners that grow forever and
                      // are streamed to every client. Only warehouse, packager and
                      // admin roles ever read them — drivers, stores and dispatch used
                      // to download the entire history for nothing. Gate them to the
                      // roles that actually consume the data.
                      const WAREHOUSE_ROLES: UserRole[] = [
                        UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.WAREHOUSE_MANAGER,
                        UserRole.WORKER, UserRole.PACKAGER,
                        UserRole.MANAGER,
                      ];
                      const noop = () => {};
                      let unsubLots = noop, unsubAssignments = noop, unsubRuns = noop, unsubReturns = noop;
                      if (role && WAREHOUSE_ROLES.includes(role)) {
                          unsubLots = dbInstance.collection('inboundLots').onSnapshot((snap) => {
                              const lots = snap.docs.map(d => ({ id: d.id, ...d.data() } as InboundLot));
                              setInboundLots(lots.sort((a, b) => b.receivedAt - a.receivedAt));
                          }, errorHandler);

                          unsubAssignments = dbInstance.collection('lotAssignments').onSnapshot((snap) => {
                              const items = snap.docs.map(d => ({ id: d.id, ...d.data() } as LotAssignment));
                              setLotAssignments(items.sort((a, b) => b.assignedAt - a.assignedAt));
                          }, errorHandler);

                          unsubRuns = dbInstance.collection('productionRuns').onSnapshot((snap) => {
                              const runs = snap.docs.map(d => ({ id: d.id, ...d.data() } as ProductionRun));
                              setProductionRuns(runs.sort((a, b) => b.performedAt - a.performedAt));
                          }, errorHandler);

                          unsubReturns = dbInstance.collection('lotReturns').onSnapshot((snap) => {
                              const items = snap.docs.map(d => ({ id: d.id, ...d.data() } as LotReturn));
                              setLotReturns(items.sort((a, b) => b.returnedAt - a.returnedAt));
                          }, errorHandler);
                      }

                      setLoading(false);
                      cleanupListeners = () => {
                          unsubUsers(); unsubProducts(); unsubStocks(); unsubDriverStocks(); unsubTx(); unsubActiveOrders(); unsubDeliveredOrders(); unsubDayZero(); unsubTwilioNumbers(); unsubRooms(); unsubLots(); unsubAssignments(); unsubRuns(); unsubReturns();
                      };
                  } else {
                      selfIdRef.current = null;
                      selfKeyRef.current = null;
                      setCurrentUser(null);
                      setLoading(false);
                  }
              } catch (error) {
                  // Firebase auth is still signed in — we just couldn't read the
                  // profile even after retries. Don't null the user: mid-session
                  // that would kick a working dispatcher to the Login page, and
                  // on a cold load currentUser is already null so Login shows
                  // anyway. Leave state as-is and let the next auth tick or a
                  // manual reload recover.
                  console.error('[AppContext] profile bootstrap failed (auth still signed in):', error);
                  setLoading(false);
              }
          } else {
              selfIdRef.current = null;
              selfKeyRef.current = null;
              setCurrentUser(null);
              setLoading(false);
          }
      });

      const safetyTimeout = setTimeout(() => {
          setLoading(false);
          setOrdersLoaded(true);
      }, 3000);

      return () => {
          unsubAuth();
          if (cleanupListeners) cleanupListeners();
          clearTimeout(safetyTimeout);
      }
    } catch (error) {
      setLoading(false);
      return () => {};
    }
  }, []);

  // O(1) indexed lookups instead of O(n) .find() scans
  // Placed before early return to keep hook count stable across renders
  const storeStockMap = useMemo(() => {
      const map = new Map<string, number>();
      for (const s of storeStocks) {
          map.set(`${s.storeId}_${s.productId}`, s.quantity);
      }
      return map;
  }, [storeStocks]);

  const driverStockMap = useMemo(() => {
      const map = new Map<string, number>();
      for (const s of driverStocks) {
          map.set(`${s.driverId}_${s.productId}`, s.quantity);
      }
      return map;
  }, [driverStocks]);

  // Per-dispatch isolation, applied ONCE at the source (see services/orderScope).
  // Deps are id/role — NOT the currentUser object, whose identity churns on
  // every read-receipt write by the users listener. Internal actions below
  // close over the raw `orders` state, so mutations keep full visibility.
  const scopedOrders = useMemo(
      () => visibleOrdersFor(orders, currentUser),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [orders, currentUser?.id, currentUser?.role]
  );

  // ── Group-chat delivery receipts (the grey ✓✓) ────────────────────────────
  // WhatsApp's "delivered" means the recipient's DEVICE has the message, not
  // that they opened the chat. The web analogue is "this session has the doc",
  // which is exactly what the rooms listener above gives us — so the mark is
  // stamped here, app-wide, rather than in RoomChat (which only mounts once
  // the user actually opens a room). Read marks come from RoomChat.
  //
  // The write bumps `deliveredAt[me]` to the room's newest message timestamp,
  // which re-fires this effect once with the condition already satisfied, so
  // it settles immediately. `deliveredWritten` remembers what we've already
  // sent per room, so unrelated snapshots landing before the write echoes back
  // don't fire it a second time.
  // Written SPARINGLY — coalesced, jittered and re-checked so it is one write
  // per person per burst, not one per tab per message: see
  // services/deliveryReceiptQueue.ts for why that matters so much.
  const roomsLatestRef = useRef<ChatRoom[]>(rooms);
  roomsLatestRef.current = rooms;
  const deliveryQueueRef = useRef<DeliveryReceiptQueue | null>(null);
  useEffect(() => {
    const me = currentUser?.id;
    if (!me) return;
    const queue = createDeliveryReceiptQueue({
      me,
      getRoom: (id) => roomsLatestRef.current.find((r) => r.id === id),
      write: (roomId, at) => markRoomDelivered(roomId, me, at),
      onError: (e) => console.error('[AppContext] delivery receipt failed:', e),
    });
    deliveryQueueRef.current = queue;
    return () => { queue.dispose(); deliveryQueueRef.current = null; };
  }, [currentUser?.id]);
  useEffect(() => { deliveryQueueRef.current?.offer(rooms); }, [rooms, currentUser?.id]);

  // ── Warehouse pipeline tables: live only while a warehouse page is mounted ─
  // (REALTIME_REDESIGN_PLAN.md §4 step 1.) inbound_lots, lot_assignments,
  // production_runs and lot_returns are whole-table follows that grow forever;
  // binding them at app start cost every admin, manager and packaging-house
  // tab four policy checks per change for pages it may never open. Pages
  // declare themselves with useWarehouseTables(); the last one to unmount
  // releases the bindings. State keeps its last value between visits and
  // subscribeTable paints from its cache on the next mount. Supabase only —
  // the Firestore line still binds at login.
  const [warehouseWanted, setWarehouseWanted] = useState<boolean>(() => warehouseTablesWanted());
  useEffect(() => onWarehouseTablesWanted(setWarehouseWanted), []);
  const warehouseUid = currentUser?.id;
  const warehouseRole = currentUser?.role;
  useEffect(() => {
    if (!USE_SUPABASE || !warehouseUid || !warehouseWanted || !roleBindsWarehouse(warehouseRole)) return;
    const onError = (e: Error) => { console.error('Supabase Listener Error:', e); };
    const unsubs = [
      subscribeTable<InboundLot>('inbound_lots', sbLotFromRow, setInboundLots,
        { order: { column: 'received_at', ascending: false }, onError }),
      subscribeTable<LotAssignment>('lot_assignments', sbAssignmentFromRow, setLotAssignments,
        { order: { column: 'assigned_at', ascending: false }, onError }),
      subscribeTable<ProductionRun>('production_runs', sbRunFromRow, setProductionRuns,
        { order: { column: 'performed_at', ascending: false }, onError }),
      subscribeTable<LotReturn>('lot_returns', sbLotReturnFromRow, setLotReturns,
        { order: { column: 'returned_at', ascending: false }, onError }),
      // Claims (0117): a missing table (before the migration) is not an error — the Packaging House
      // then keeps its shared pile; the page reads packingClaimsReady. Followed only with the switch on
      // (off = the shared pile, so nothing reads them), and only the active claims plus the ones released
      // in the last CLAIMS_KEEP_DAYS — not every claim ever made (2026-10-07).
      ...(FEATURES.packingClaims ? [
        subscribeTable<PackingClaim>('packing_claims', sbClaimFromRow, (rows) => { setPackingClaims(rows); setPackingClaimsReady(true); },
          { order: { column: 'claimed_at', ascending: false },
            where: claimsLiveFilter(new Date(Date.now() - CLAIMS_KEEP_DAYS * 86_400_000).toISOString()),
            onError: (e) => { if (!/packing_claims/.test(e.message)) onError(e); } }),
      ] : []),
    ];
    return () => { unsubs.forEach((u) => u()); };
  }, [warehouseUid, warehouseRole, warehouseWanted]);

  // Selective-subscription plumbing. storeRef always holds the latest full
  // context value; after every commit we notify subscribers so each
  // useAppSelector re-reads its slice and re-renders only if it changed.
  // stableActions is a single object (created once) whose methods always
  // delegate to the latest impl, so action-only consumers never re-render.
  // Declared before the early return to keep hook order stable.
  const storeRef = useRef<AppState>({} as AppState);
  const selectorListeners = useRef(new Set<() => void>());
  const selectorStore = useMemo<AppStore>(() => ({
    subscribe: (cb) => { selectorListeners.current.add(cb); return () => { selectorListeners.current.delete(cb); }; },
    getSnapshot: () => storeRef.current,
  }), []);
  useEffect(() => { selectorListeners.current.forEach((l) => l()); });

  const actionWrapperCache = useRef<Record<string, any>>({});
  const stableActions = useMemo<AppState>(() => new Proxy({} as AppState, {
    get: (_t, prop: string) => {
      const v = (storeRef.current as any)[prop];
      if (typeof v !== 'function') return v;
      if (!actionWrapperCache.current[prop]) {
        actionWrapperCache.current[prop] = (...args: any[]) => (storeRef.current as any)[prop](...args);
      }
      return actionWrapperCache.current[prop];
    },
  }), []);

  const login = async (email: string, pass: string): Promise<boolean> => {
    if (USE_SUPABASE) {
        try {
            const { data, error } = await supabase().auth.signInWithPassword({ email, password: pass });
            if (error || !data.user) return false;
            // Same guard as the Firestore path: an auth account with no app
            // profile row must not enter the app.
            // Only a PROVABLY absent profile signs the account back out. A read
            // that FAILED (database busy, network) is not evidence of anything:
            // the password was right and the session is good, so keep it — the
            // bootstrap above shows "Reconnecting…" and retries. Signing out here
            // told people their password was wrong in the middle of an outage.
            const { data: row, error: rowErr } = await supabase()
                .from('users').select('id').eq('auth_id', data.user.id).maybeSingle();
            if (!rowErr && !row) {
                await supabase().auth.signOut();
                return false;
            }
            return true;
        } catch (e) {
            return false;
        }
    }
    try {
        const { auth: authInstance, db: dbInstance } = ensureFirebaseInitialized();
        const userCred = await authInstance.signInWithEmailAndPassword(email, pass);
        if (userCred.user) {
            const docSnap = await dbInstance.collection('users').doc(userCred.user.uid).get();
            if (!docSnap.exists) {
                await authInstance.signOut();
                throw new Error("Profile missing");
            }
        }
        return true;
    } catch (e) {
        return false;
    }
  };

  const signup = async (email: string, pass: string, name: string, phone: string): Promise<boolean> => {
    if (USE_SUPABASE) {
        try {
            return await sbProvisionUser({
                email, pass, name,
                phone: phone ? toE164(phone) : '',
                role: UserRole.STORE,
                requiresPasswordReset: false,
            });
        } catch (e: any) {
            showToast(e.message || "Signup failed", "error");
            return false;
        }
    }
    try {
        const { auth: authInstance, db: dbInstance } = ensureFirebaseInitialized();
        const res = await authInstance.createUserWithEmailAndPassword(email, pass);
        if (res.user) {
           const newUser: User = {
               id: res.user.uid,
               name,
               email,
               role: UserRole.STORE,
               // Always store E.164 so Twilio (SMS, voice, FCM lookups) can use
               // it without further parsing. Falsy stays empty.
               phone: phone ? toE164(phone) : '',
               requiresPasswordReset: false
           };
           await dbInstance.collection('users').doc(res.user.uid).set(newUser);
           return true;
        }
        return false;
    } catch (e: any) {
        showToast(e.message || "Signup failed", "error");
        return false;
    }
  };

  const adminCreateUser = async (email: string, pass: string, name: string, role: UserRole, phone?: string, storeAccess?: boolean, designation?: string): Promise<{success: boolean, error?: string}> => {
      if (USE_SUPABASE) {
          // Needs the service-role key (admin API) — never exposed to the
          // browser — so the work happens in the supabase-admin-users function.
          try {
              const { data: { session } } = await supabase().auth.getSession();
              const token = session?.access_token;
              const response = await fetch('/.netlify/functions/supabase-admin-users', {
                  method: 'POST',
                  headers: {
                      'Content-Type': 'application/json',
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  // storeAccess (0084): drivers only; new drivers start without it.
                  body: JSON.stringify({ action: 'create', name, email, role, password: pass, phone: phone || '', storeAccess: role === UserRole.DRIVER && storeAccess === true, ...(designation?.trim() ? { designation: designation.trim() } : {}) })
              });
              const data = await response.json();
              if (!response.ok) {
                  return { success: false, error: data.error || 'Failed to create user' };
              }
              // The users list is a 30 s poll, not a live channel: read the new
              // row back and show it now, so the Admin Panel does not need a
              // reload to list the member just created.
              if (data?.uid) {
                  try {
                      const { data: row } = await supabase().from('users').select('*').eq('id', data.uid).maybeSingle();
                      if (row) {
                          const { lastLocation: _ll, lastOpenedAt: _oa, ...stable } = sbUserFromRow(row);
                          setUsers(prev => prev.some(u => u.id === stable.id) ? prev : [...prev, stable as User]);
                      }
                  } catch (e) { console.warn('[adminCreateUser] could not read the new row back:', e); }
              }
              logWarehouseHistory({
                  productId: 'system',
                  productName: 'User Management',
                  action: WarehouseHistoryAction.USER_CREATED,
                  timestamp: Date.now(),
                  performedBy: getPerformedBy(),
                  quantityChange: 0,
                  quantityBefore: 0,
                  quantityAfter: 0,
                  description: `User created — ${name} (${role}) — email: ${email}`,
              });
              return { success: true };
          } catch (e: any) {
              return { success: false, error: e.message };
          }
      }
      let secondaryApp: firebase.app.App | null = null;
      try {
          if (!firebaseConfig) throw new Error("Firebase config missing");
          const { db: dbInstance } = ensureFirebaseInitialized();
          secondaryApp = firebase.initializeApp(firebaseConfig, "SecondaryApp");
          const secondaryAuth = secondaryApp.auth();
          const res = await secondaryAuth.createUserWithEmailAndPassword(email, pass);
          if (res.user) {
             const newUser: User = {
                 id: res.user.uid,
                 name,
                 email,
                 role,
                 phone: phone ? toE164(phone) : '',
                 requiresPasswordReset: true
             };
             await dbInstance.collection('users').doc(res.user.uid).set(newUser);
             await secondaryAuth.signOut();
             secondaryApp.delete();
             logWarehouseHistory({
                 productId: 'system',
                 productName: 'User Management',
                 action: WarehouseHistoryAction.USER_CREATED,
                 timestamp: Date.now(),
                 performedBy: getPerformedBy(),
                 quantityChange: 0,
                 quantityBefore: 0,
                 quantityAfter: 0,
                 description: `User created — ${name} (${role}) — email: ${email}`,
             });
             return { success: true };
          }
          return { success: false, error: "Failed to create auth user" };
      } catch (e: any) {
          if (secondaryApp) (secondaryApp as firebase.app.App).delete();
          return { success: false, error: e.message };
      }
  };

  const logout = () => {
    if (USE_SUPABASE) {
        // scope 'local': sign out THIS browser only. supabase-js defaults to
        // 'global', which revokes every session the account has — a dispatcher
        // logging out of one laptop was logging out their phone and their other
        // browsers too (Umer, 2026-09-21).
        return supabase().auth.signOut({ scope: 'local' }).then(({ error }) => {
            if (error) throw new Error(error.message);
        });
    }
    try {
      const { auth: authInstance } = ensureFirebaseInitialized();
      return authInstance.signOut();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  // Load older orders by date range (for reports)
  const loadOrdersByDateRange = async (startDate: number, endDate: number): Promise<Order[]> => {
    if (USE_SUPABASE) {
      try {
        const data = await fetchAllRows(() => supabase()
          .from('orders')
          .select('*, order_items(product_id, quantity), order_photos(kind, url)')
          .gte('created_at', isoFromMs(startDate)!)
          .lte('created_at', isoFromMs(endDate)!)
          .order('created_at', { ascending: false }));
        const fetched = (data || []).map(sbOrderFromRow);
        // Merge with existing orders (avoiding duplicates) — same as below.
        setOrders(prev => {
          const existingIds = new Set(prev.map(o => o.id));
          const newOnes = fetched.filter(o => !existingIds.has(o.id));
          return [...prev, ...newOnes];
        });
        setOrdersDateRange({ start: startDate, end: endDate });
        return fetched;
      } catch (e) {
        console.error('Failed to load orders by date range:', e);
        return [];
      }
    }
    try {
      const { db: dbInstance } = ensureFirebaseInitialized();
      const snap = await dbInstance.collection('orders')
        .where('createdAt', '>=', startDate)
        .where('createdAt', '<=', endDate)
        .get();
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() } as Order));
      // Merge with existing orders (avoiding duplicates)
      setOrders(prev => {
        const existingIds = new Set(prev.map(o => o.id));
        const newOnes = fetched.filter(o => !existingIds.has(o.id));
        return [...prev, ...newOnes];
      });
      setOrdersDateRange({ start: startDate, end: endDate });
      return fetched;
    } catch (e) {
      console.error('Failed to load orders by date range:', e);
      return [];
    }
  };

  // Load older transactions by date range (for reports)
  const loadTransactionsByDateRange = async (startDate: number, endDate: number): Promise<StockTransaction[]> => {
    if (USE_SUPABASE) {
      try {
        const data = await fetchAllRows(() => supabase()
          .from('transactions')
          .select('*, transaction_items(product_id, quantity, received_quantity)')
          .gte('created_at', isoFromMs(startDate)!)
          .lte('created_at', isoFromMs(endDate)!)
          .order('created_at', { ascending: false }));
        const fetched = (data || []).map(sbTxFromRow);
        // Merge with existing transactions (avoiding duplicates) — same as below.
        setTransactions(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newOnes = fetched.filter(t => !existingIds.has(t.id));
          return [...prev, ...newOnes].sort((a, b) => b.timestamp - a.timestamp);
        });
        setTransactionsDateRange({ start: startDate, end: endDate });
        return fetched;
      } catch (e) {
        console.error('Failed to load transactions by date range:', e);
        return [];
      }
    }
    try {
      const { db: dbInstance } = ensureFirebaseInitialized();
      const snap = await dbInstance.collection('transactions')
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .get();
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() } as StockTransaction));
      // Merge with existing transactions (avoiding duplicates)
      setTransactions(prev => {
        const existingIds = new Set(prev.map(t => t.id));
        const newOnes = fetched.filter(t => !existingIds.has(t.id));
        return [...prev, ...newOnes].sort((a, b) => b.timestamp - a.timestamp);
      });
      setTransactionsDateRange({ start: startDate, end: endDate });
      return fetched;
    } catch (e) {
      console.error('Failed to load transactions by date range:', e);
      return [];
    }
  };

  const resetData = () => {
      window.location.reload();
  };

  const seedDatabase = async () => {
      if (products.length === 0) {
          try {
              if (USE_SUPABASE) {
                  const { error } = await supabase().from('products').upsert(INITIAL_PRODUCTS.map(sbProductRow));
                  if (error) throw new Error(`products upsert: ${error.message}`);
                  return;
              }
              const { db: dbInstance } = ensureFirebaseInitialized();
              for (const p of INITIAL_PRODUCTS) {
                  await dbInstance.collection('products').doc(p.id).set(p);
              }
          } catch (e) {
              throw e;
          }
      }
  }

  const sendSMS = async (to: string, body: string): Promise<boolean> => {
      // Env kill switch (VITE_OUTBOUND_SMS=off): test sites stay in-app only —
      // no toast, no history row, nothing leaves the app. See services/features.ts.
      if (!FEATURES.outboundSms) return false;
      try {
          // send-sms is signed-in-staff only since 2026-09-11 (it was an open
          // relay before). Hand it whichever session this mode has; without
          // one the server answers 401 "Sign in required".
          let authHeaders: Record<string, string> = {};
          try {
              const token = USE_SUPABASE
                  ? (await supabase().auth.getSession()).data.session?.access_token
                  : await auth?.currentUser?.getIdToken();
              if (token) authHeaders = { Authorization: `Bearer ${token}` };
          } catch { /* no token — the server will say so */ }
          const response = await fetch('/.netlify/functions/send-sms', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders },
              body: JSON.stringify({ to, message: body })
          });
          const data = await response.json();
          if (!response.ok) {
              showToast(`SMS Failed: ${data.error || "Network Error"}`, "error");
              return false;
          } else {
              showToast("SMS Sent to Network", "success");
              logWarehouseHistory({
                  productId: 'system',
                  productName: 'Communication',
                  action: WarehouseHistoryAction.USER_UPDATED,
                  timestamp: Date.now(),
                  performedBy: getPerformedBy(),
                  quantityChange: 0,
                  quantityBefore: 0,
                  quantityAfter: 0,
                  description: `SMS sent to ${to} — "${body.length > 60 ? body.substring(0, 60) + '...' : body}"`,
              });
              return true;
          }
      } catch (e) {
          showToast("SMS Error: Failed to reach server", "error");
          return false;
      }
  };

  // Reusable: notify all parties responsible for an order via SMS
  const notifyOrderParties = (orderId: string, message: string, excludeUserId?: string) => {
      const order = orders.find(o => o.id === orderId);
      if (!order) return;
      const targets: { id: string; phone?: string }[] = [];
      // Driver
      if (order.driverId) {
          const driver = users.find(u => u.id === order.driverId);
          if (driver?.phone) targets.push({ id: driver.id, phone: driver.phone });
      }
      // Store
      if (order.storeId) {
          const store = users.find(u => u.id === order.storeId);
          if (store?.phone) targets.push({ id: store.id, phone: store.phone });
      }
      // Dispatch who assigned this order
      if (order.dispatchId) {
          const dispatcher = users.find(u => u.id === order.dispatchId);
          if (dispatcher?.phone) targets.push({ id: dispatcher.id, phone: dispatcher.phone });
      }
      // Send to each target except the excluded user (the person who performed the action)
      for (const t of targets) {
          if (t.id !== excludeUserId) {
              sendSMS(t.phone!, message).catch(() => {});
          }
      }
  };

  const sendCustomerMessage = async (orderId: string, messageBody: string): Promise<boolean> => {
      try {
          // Board-card texting (orderId `card_<id>`) is token-authed server-side;
          // the order path ignores the header, so always attaching it is safe.
          let authHeaders: Record<string, string> = {};
          try {
              // _email-auth routes on the token's issuer, so hand it whichever
              // session this mode actually has — a Firebase lookup returns
              // nothing in Postgres mode and the card path then 401s.
              const token = USE_SUPABASE
                  ? (await supabase().auth.getSession()).data.session?.access_token
                  : await auth?.currentUser?.getIdToken();
              if (token) authHeaders = { Authorization: `Bearer ${token}` };
          } catch { /* no token — order path still works via body auth */ }
          const response = await fetch('/.netlify/functions/send-customer-message', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders },
              body: JSON.stringify({ orderId, messageBody, driverId: currentUser?.id })
          });
          const data = await response.json();
          if (!response.ok) {
              showToast(`Message Failed: ${data.error || "Network Error"}`, "error");
              return false;
          }
          // The message is always recorded now, even if the carrier rejected it —
          // surface a delivery warning but keep it in the thread (return true so
          // the composer clears and the bubble stays).
          if (data.delivered === false) {
              showToast(`Saved to the chat, but SMS wasn't delivered: ${data.error || 'carrier error'}`, "error");
          } else {
              showToast("Message sent to customer", "success");
          }
          // Log customer SMS to store/driver history
          const smsOrder = orders.find(o => o.id === orderId);
          const smsOrderNum = smsOrder?.orderNumber || orderId.slice(-6).toUpperCase();
          const smsNow = Date.now();
          if (smsOrder?.storeId) {
              const store = users.find(u => u.id === smsOrder.storeId);
              logStoreHistory({
                  storeId: smsOrder.storeId,
                  storeName: store?.name || 'Unknown Store',
                  action: StoreHistoryAction.SMS_SENT,
                  timestamp: smsNow,
                  orderId,
                  orderNumber: smsOrderNum,
                  performedBy: getPerformedBy(),
                  description: `SMS to customer (${smsOrder.customerName}) on Order #${smsOrderNum} — "${messageBody.length > 60 ? messageBody.substring(0, 60) + '...' : messageBody}"`,
              });
          }
          if (smsOrder?.driverId) {
              const driver = users.find(u => u.id === smsOrder.driverId);
              logDriverHistory({
                  driverId: smsOrder.driverId,
                  driverName: driver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.SMS_SENT,
                  timestamp: smsNow,
                  orderId,
                  orderNumber: smsOrderNum,
                  performedBy: getPerformedBy(),
                  description: `SMS to customer (${smsOrder.customerName}) on Order #${smsOrderNum} — "${messageBody.length > 60 ? messageBody.substring(0, 60) + '...' : messageBody}"`,
              });
          }
          return true;
      } catch (e) {
          showToast("Message Error: Failed to reach server", "error");
          return false;
      }
  };

  const callCustomer = async (orderId: string): Promise<boolean> => {
      try {
          const response = await fetch('/.netlify/functions/call-customer', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orderId, driverId: currentUser?.id })
          });
          const data = await response.json();
          if (!response.ok) {
              showToast(`Call Failed: ${data.error || "Network Error"}`, "error");
              return false;
          }
          showToast("Calling... You will receive a call shortly.", "success");
          // Log call to history
          const callOrder = orders.find(o => o.id === orderId);
          const callOrderNum = callOrder?.orderNumber || orderId.slice(-6).toUpperCase();
          const callNow = Date.now();
          if (callOrder?.storeId) {
              const store = users.find(u => u.id === callOrder.storeId);
              logStoreHistory({
                  storeId: callOrder.storeId,
                  storeName: store?.name || 'Unknown Store',
                  action: StoreHistoryAction.CUSTOMER_CALLED,
                  timestamp: callNow,
                  orderId,
                  orderNumber: callOrderNum,
                  performedBy: getPerformedBy(),
                  description: `Customer called on Order #${callOrderNum} — ${callOrder.customerName}`,
              });
          }
          if (callOrder?.driverId) {
              const driver = users.find(u => u.id === callOrder.driverId);
              logDriverHistory({
                  driverId: callOrder.driverId,
                  driverName: driver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.CUSTOMER_CALLED,
                  timestamp: callNow,
                  orderId,
                  orderNumber: callOrderNum,
                  performedBy: getPerformedBy(),
                  description: `Customer called on Order #${callOrderNum} — ${callOrder.customerName}`,
              });
          }
          return true;
      } catch (e) {
          showToast("Call Error: Failed to reach server", "error");
          return false;
      }
  };

  const updateUser = async (user: User) => {
      try {
          const oldUser = users.find(u => u.id === user.id);
          // Normalize phone to E.164 on save so Twilio can use the stored
          // value directly. Empty stays empty.
          const normalized: User = { ...user, phone: user.phone ? toE164(user.phone) : '' };
          if (USE_SUPABASE) {
              // The login email lives in GoTrue (auth.users), not in this row.
              // Writing only public.users.email left the account answering to
              // the OLD address — "Invalid login credentials" after an admin
              // edit (client report, 2026-09-17). The service-role function
              // moves the sign-in account first and fails fast (address taken,
              // unlinked account) so the profile never disagrees with it.
              const nextEmail = (normalized.email || '').trim();
              const emailChanged = !!oldUser && nextEmail.toLowerCase() !== (oldUser.email || '').trim().toLowerCase();
              // Called on EVERY save that carries an email, not only on a change:
              // an account this bug already broke has a row that says the new
              // address while Auth still holds the old one, so "changed" can never
              // see it — a plain Save must be enough to repair it. On an unchanged
              // address the call is a best-effort re-sync; only a real change is
              // allowed to fail the save.
              if (nextEmail && oldUser) {
                  const { data: { session } } = await supabase().auth.getSession();
                  const token = session?.access_token;
                  const response = await fetch('/.netlify/functions/supabase-admin-users', {
                      method: 'POST',
                      headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      body: JSON.stringify({ action: 'update-email', uid: user.id, email: nextEmail }),
                  });
                  const data = await response.json().catch(() => ({}));
                  if (!response.ok) {
                      const msg = data.error || 'Could not change the login email';
                      if (emailChanged) {
                          showToast(`Email not changed: ${msg}`, 'error');
                          const err: any = new Error(msg);
                          err.toasted = true;
                          throw err;
                      }
                      // Unchanged address: a row with no linked sign-in account
                      // (409) or a role this caller may not manage (403) must not
                      // turn a name / phone edit into a failure it never was.
                      console.warn('[updateUser] login email re-sync skipped:', msg);
                  } else if (typeof data.email === 'string' && data.email) {
                      // GoTrue lower-cases; keep the row we write below identical.
                      normalized.email = data.email;
                  }
              }
              // Archive / Activate (2026-10-31): the flag alone grants or removes nothing — an archived
              // admin still signed in as admin. The service-role function blocks (or allows) the login
              // first and then flags the row; the save stops here if it refuses.
              if (oldUser && !!oldUser.isArchived !== !!normalized.isArchived) {
                  const { data: { session } } = await supabase().auth.getSession();
                  const token = session?.access_token;
                  const response = await fetch('/.netlify/functions/supabase-admin-users', {
                      method: 'POST',
                      headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      body: JSON.stringify({ action: 'set-archived', uid: user.id, archived: !!normalized.isArchived }),
                  });
                  const data = await response.json().catch(() => ({}));
                  if (!response.ok) {
                      const msg = data.error || `Could not ${normalized.isArchived ? 'archive' : 'activate'} the user`;
                      showToast(`${normalized.isArchived ? 'Not archived' : 'Not activated'}: ${msg}`, 'error');
                      const err: any = new Error(msg);
                      err.toasted = true;
                      throw err;
                  }
              }
              // store_access (0084) is written only when it actually changed: a plain
              // profile edit never touches it, so this save works on a database
              // that does not have the column yet. Turning it OFF makes the
              // database remove the driver from every store in the same statement.
              const storeAccessChanged = !!oldUser && hasStoreAccess(oldUser) !== hasStoreAccess(normalized);
              const lostStores = storeAccessChanged && !hasStoreAccess(normalized)
                  ? storesManagedBy(user.id, users, storeTempManagers)
                  : [];
              // designation (0093) likewise: written only when it changed, so a
              // profile edit still saves on a database without the column.
              const designationChanged = (oldUser?.designation || '') !== (normalized.designation || '');
              const { error } = await supabase().from('users').update({
                  ...(storeAccessChanged ? { store_access: hasStoreAccess(normalized) } : {}),
                  ...(designationChanged ? { designation: normalized.designation?.trim() || null } : {}),
                  name: normalized.name,
                  role: normalized.role,
                  email: normalized.email,
                  phone: normalized.phone ?? '',
                  address: normalized.address ?? null,
                  requires_password_reset: !!normalized.requiresPasswordReset,
                  is_archived: !!normalized.isArchived,
                  // temp_packer_driver_id is deliberately NOT written here: since
                  // 0050 it is a DB-maintained mirror of store_temp_managers, and a
                  // profile edit must never add or remove a manager.
                  twilio_number: normalized.twilioNumber || null,
                  is_on_duty: !!normalized.isOnDuty,
              }).eq('id', user.id);
              if (error) throw new Error(`users update: ${error.message}`);
              // The users list is a 30 s poll, not a live channel — show the
              // edit now instead of on the next poll or a reload.
              setUsers(prev => prev.map(u => u.id === user.id ? ({ ...u, ...normalized, id: user.id } as User) : u));
              // The database already removed the manager rows (0084); record why, the
              // same way a manual removal is recorded.
              for (const store of lostStores) {
                  logDriverHistory({
                      driverId: user.id,
                      driverName: user.name,
                      action: DriverHistoryAction.TEMP_PACKER_REMOVED,
                      timestamp: Date.now(),
                      performedBy: getPerformedBy(),
                      description: `Removed as temporary store manager at ${store.name} — store access turned off`,
                  });
                  logStoreHistory({
                      storeId: store.id,
                      storeName: store.name,
                      action: StoreHistoryAction.TEMP_PACKER_REMOVED,
                      timestamp: Date.now(),
                      performedBy: getPerformedBy(),
                      description: `Temporary store manager ${user.name} removed from store — store access turned off`,
                  });
              }
          } else {
          const { db: dbInstance } = ensureFirebaseInitialized();
          await dbInstance.collection('users').doc(user.id).update({ ...normalized });
          }
          const changes: string[] = [];
          if (oldUser) {
              if (oldUser.name !== user.name) changes.push(`name: ${oldUser.name} → ${user.name}`);
              if ((oldUser.email || '') !== (normalized.email || '')) changes.push(`email: ${oldUser.email} → ${normalized.email}`);
              if (oldUser.role !== user.role) changes.push(`role: ${oldUser.role} → ${user.role}`);
              if (oldUser.phone !== normalized.phone) changes.push(`phone updated`);
              if (oldUser.isArchived !== user.isArchived) changes.push(user.isArchived ? 'archived' : 'activated');
              if (hasStoreAccess(oldUser) !== hasStoreAccess(user)) changes.push(hasStoreAccess(user) ? 'store access on' : 'store access off');
          }
          logWarehouseHistory({
              productId: 'system',
              productName: 'User Management',
              action: WarehouseHistoryAction.USER_UPDATED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `User updated — ${user.name} (${user.role})${changes.length ? ' — ' + changes.join(', ') : ''}`,
          });
          showToast("User updated successfully", "success");
      } catch (e: any) {
          // The email step toasts its own reason ("already in use", …); the
          // generic line is for the row write only, never on top of it.
          if (!e?.toasted) showToast("Failed to update user", "error");
          throw e;
      }
  };

  const deleteUser = async (id: string) => {
      try {
          // Held-stock guard — STORE stock and DRIVER car stock alike. Deleting a
          // driver who still held car stock left ownerless driverStocks rows
          // behind: the product picker then showed "Driver 4" with nobody to
          // assign and Stock Overview counted units nobody could deliver
          // (client report 2026-09-02, "14G - Blonde Lebanese Hash"). The stock
          // must be returned to the warehouse before the account can go.
          const storeHeld = storeStocks.filter(ss => ss.storeId === id && ss.quantity > 0).reduce((acc, curr) => acc + curr.quantity, 0);
          const carHeld = driverStocks.filter(ds => ds.driverId === id && ds.quantity > 0).reduce((acc, curr) => acc + curr.quantity, 0);
          const totalItemsHeld = storeHeld + carHeld;
          if (totalItemsHeld > 0) {
              const where = carHeld > 0 && storeHeld > 0 ? 'in their car and at their store' : carHeld > 0 ? 'in their car' : 'at their store';
              showToast(`Cannot delete this user — they still hold ${totalItemsHeld} item(s) ${where}. Return the stock to the warehouse first.`, 'error');
              return;
          }
          const deletedUser = users.find(u => u.id === id);
          if (USE_SUPABASE) {
              // Profile row only, like the Firestore path — the auth account
              // outlives it there too (dropping it needs the service-role
              // key). The Firestore twin stays: messaging history references
              // it and this hybrid never deletes from Firestore.
              const { error } = await supabase().from('users').delete().eq('id', id);
              if (error) {
                  // Foreign keys have no Firestore equivalent: a user named on
                  // an order or transaction cannot be dropped, only archived.
                  if (error.code === '23503') {
                      showToast('Cannot delete user — their name is on past orders or transactions. Use Archive (the box icon) instead: it keeps their history and blocks their sign-in.', 'error');
                      return;
                  }
                  throw new Error(`users delete: ${error.message}`);
              }
              // Same 30 s poll — drop the row from the list right away.
              setUsers(prev => prev.filter(u => u.id !== id));
          } else {
          const { db: dbInstance } = ensureFirebaseInitialized();
          await dbInstance.collection('users').doc(id).delete();
          }
          logWarehouseHistory({
              productId: 'system',
              productName: 'User Management',
              action: WarehouseHistoryAction.USER_DELETED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `User deleted — ${deletedUser?.name || 'Unknown'} (${deletedUser?.role || 'unknown'}) — email: ${deletedUser?.email || 'unknown'}`,
          });
          showToast("User deleted successfully", "success");
      } catch (e) {
          showToast("Failed to delete user", "error");
          throw e;
      }
  };

  const logWarehouseHistory = async (entry: Omit<WarehouseHistoryEntry, 'id'>) => {
      try {
          if (USE_SUPABASE) {
              const id = `wh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const e: any = entry;
              const { error } = await supabase().from('warehouse_history').insert({
                  id,
                  product_id: e.productId ?? null,
                  product_name: e.productName ?? '',
                  action: e.action,
                  created_at: isoFromMs(e.timestamp) ?? isoFromMs(Date.now()),
                  performed_by: e.performedBy,
                  quantity_change: e.quantityChange ?? null,
                  quantity_before: e.quantityBefore ?? null,
                  quantity_after: e.quantityAfter ?? null,
                  store_name: e.storeName ?? null,
                  transaction_id: e.transactionId ?? null,
                  description: e.description ?? null,
                  extra: sbHistoryExtra(e, ['productId', 'productName', 'action', 'timestamp', 'performedBy', 'quantityChange', 'quantityBefore', 'quantityAfter', 'storeName', 'transactionId', 'description']),
              });
              if (error) throw new Error(`warehouse_history insert: ${error.message}`);
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const id = `wh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          await dbInstance.collection('warehouseHistory').doc(id).set({ id, ...entry });
      } catch (e) {
          console.error('Failed to log warehouse history:', e);
      }
  };

  const logStoreHistory = async (entry: Omit<StoreHistoryEntry, 'id'>) => {
      try {
          if (USE_SUPABASE) {
              const id = `sh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const e: any = entry;
              const { error } = await supabase().from('store_history').insert({
                  id,
                  store_id: e.storeId,
                  store_name: e.storeName ?? '',
                  action: e.action,
                  created_at: isoFromMs(e.timestamp) ?? isoFromMs(Date.now()),
                  product_id: e.productId ?? null,
                  product_name: e.productName ?? null,
                  quantity_change: e.quantityChange ?? null,
                  order_id: e.orderId ?? null,
                  order_number: e.orderNumber ?? null,
                  cash_amount: e.cashAmount ?? null,
                  performed_by: e.performedBy,
                  description: e.description ?? null,
                  extra: sbHistoryExtra(e, ['storeId', 'storeName', 'action', 'timestamp', 'productId', 'productName', 'quantityChange', 'orderId', 'orderNumber', 'cashAmount', 'performedBy', 'description']),
              });
              if (error) throw new Error(`store_history insert: ${error.message}`);
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const id = `sh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          await dbInstance.collection('storeHistory').doc(id).set({ id, ...entry });
      } catch (e) {
          console.error('Failed to log store history:', e);
      }
  };

  const logDriverHistory = async (entry: Omit<DriverHistoryEntry, 'id'>) => {
      try {
          if (USE_SUPABASE) {
              const id = `dh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const e: any = entry;
              const { error } = await supabase().from('driver_history').insert({
                  id,
                  driver_id: e.driverId,
                  driver_name: e.driverName ?? '',
                  action: e.action,
                  created_at: isoFromMs(e.timestamp) ?? isoFromMs(Date.now()),
                  product_id: e.productId ?? null,
                  product_name: e.productName ?? null,
                  quantity_change: e.quantityChange ?? null,
                  order_id: e.orderId ?? null,
                  order_number: e.orderNumber ?? null,
                  cash_amount: e.cashAmount ?? null,
                  performed_by: e.performedBy,
                  description: e.description ?? null,
                  extra: sbHistoryExtra(e, ['driverId', 'driverName', 'action', 'timestamp', 'productId', 'productName', 'quantityChange', 'orderId', 'orderNumber', 'cashAmount', 'performedBy', 'description']),
              });
              if (error) throw new Error(`driver_history insert: ${error.message}`);
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const id = `dh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          await dbInstance.collection('driverHistory').doc(id).set({ id, ...entry });
      } catch (e) {
          console.error('Failed to log driver history:', e);
      }
  };

  // Inventory Tracker: today's business date as YYYY-MM-DD, always Ottawa time
  // so movements written from any machine land in the same business day.
  const getBusinessDate = () => appTodayStr();

  // Inventory Tracker: create an immutable movement ledger record
  const createMovement = async (entry: Omit<Movement, 'id'>) => {
      try {
          if (USE_SUPABASE) {
              const id = `mv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const { error } = await supabase().from('movements').insert({
                  id,
                  date: entry.date,
                  product_id: entry.productId ?? null,
                  product_name: entry.productName,
                  type: entry.type,
                  from_entity: entry.fromEntity,
                  to_entity: entry.toEntity,
                  store_id: entry.storeId ?? null,
                  driver_id: entry.driverId ?? null,
                  to_driver_id: entry.toDriverId ?? null,
                  order_id: entry.orderId ?? null,
                  order_number: entry.orderNumber ?? null,
                  qty: entry.qty,
                  reason: entry.reason ?? null,
                  created_at: isoFromMs(entry.timestamp) ?? isoFromMs(Date.now()),
              });
              if (error) throw new Error(`movements insert: ${error.message}`);
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const id = `mv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          await dbInstance.collection('movements').doc(id).set({ id, ...entry });
      } catch (e) {
          console.error('Failed to create movement:', e);
      }
  };

  const getPerformedBy = () => ({
      userId: currentUser?.id || 'unknown',
      userName: currentUser?.name || 'Unknown',
      role: currentUser?.role || UserRole.SUPER_ADMIN,
  });

  const seedWarehouseHistory = async () => {
      try {
          if (USE_SUPABASE) {
              const { data: existing, error: existErr } = await supabase()
                  .from('warehouse_history').select('id').limit(1);
              if (existErr) throw new Error(`warehouse_history: ${existErr.message}`);
              if (existing && existing.length > 0) {
                  showToast('Warehouse history already initialized', 'info');
                  return;
              }
              const timestamp = Date.now();
              const performedBy = {
                  userId: currentUser?.id || 'system',
                  userName: currentUser?.name || 'System',
                  role: currentUser?.role || UserRole.SUPER_ADMIN,
              };
              const rows = products.map(product => ({
                  id: `wh_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
                  product_id: product.id,
                  product_name: product.name,
                  action: WarehouseHistoryAction.PRODUCT_CREATED,
                  created_at: isoFromMs(timestamp),
                  performed_by: performedBy,
                  quantity_change: product.warehouseStock,
                  quantity_before: 0,
                  quantity_after: product.warehouseStock,
                  description: `Initial snapshot: ${product.warehouseStock} units in warehouse`,
              }));
              if (rows.length > 0) {
                  const { error } = await supabase().from('warehouse_history').insert(rows);
                  if (error) throw new Error(`warehouse_history insert: ${error.message}`);
              }
              showToast(`Warehouse history initialized for ${products.length} products`, 'success');
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const existingSnap = await dbInstance.collection('warehouseHistory').limit(1).get();
          if (!existingSnap.empty) {
              showToast('Warehouse history already initialized', 'info');
              return;
          }
          const batch = dbInstance.batch();
          const timestamp = Date.now();
          for (const product of products) {
              const id = `wh_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
              const entry: WarehouseHistoryEntry = {
                  id,
                  productId: product.id,
                  productName: product.name,
                  action: WarehouseHistoryAction.PRODUCT_CREATED,
                  timestamp,
                  performedBy: {
                      userId: currentUser?.id || 'system',
                      userName: currentUser?.name || 'System',
                      role: currentUser?.role || UserRole.SUPER_ADMIN,
                  },
                  quantityChange: product.warehouseStock,
                  quantityBefore: 0,
                  quantityAfter: product.warehouseStock,
                  description: `Initial snapshot: ${product.warehouseStock} units in warehouse`,
              };
              batch.set(dbInstance.collection('warehouseHistory').doc(id), entry);
          }
          await batch.commit();
          showToast(`Warehouse history initialized for ${products.length} products`, 'success');
      } catch (e: any) {
          console.error('Warehouse history init error:', e);
          showToast(`Failed to initialize warehouse history: ${e.message || e}`, 'error');
      }
  };

  const seedStoreHistory = async () => {
      try {
          if (USE_SUPABASE) {
              const { data: existing, error: existErr } = await supabase()
                  .from('store_history').select('id').limit(1);
              if (existErr) throw new Error(`store_history: ${existErr.message}`);
              if (existing && existing.length > 0) {
                  showToast('Store history already has data', 'info');
                  return;
              }

              const storeUsers = users.filter(u => u.role === UserRole.STORE);
              if (storeUsers.length === 0 || products.length === 0) {
                  showToast('Need at least one store and products to seed history', 'error');
                  return;
              }

              const now = Date.now();
              const rows: Record<string, unknown>[] = [];
              const rowId = () => `sh_${now}_${Math.random().toString(36).substr(2, 9)}`;
              const performedBy = {
                  userId: currentUser?.id || 'system',
                  userName: currentUser?.name || 'System',
                  role: currentUser?.role || UserRole.SUPER_ADMIN,
              };

              for (const store of storeUsers) {
                  const storePerformedBy = {
                      userId: store.id,
                      userName: store.name,
                      role: UserRole.STORE,
                  };

                  const shuffled = [...products].sort(() => Math.random() - 0.5);
                  const storeProducts = shuffled.slice(0, Math.min(3 + Math.floor(Math.random() * 3), products.length));

                  for (const product of storeProducts) {
                      const qty = 5 + Math.floor(Math.random() * 20);
                      rows.push({
                          id: rowId(),
                          store_id: store.id,
                          store_name: store.name,
                          action: StoreHistoryAction.STOCK_RECEIVED,
                          created_at: isoFromMs(now - 3 * 24 * 60 * 60 * 1000 + Math.floor(Math.random() * 3600000)),
                          product_id: product.id,
                          product_name: product.name,
                          quantity_change: qty,
                          performed_by: performedBy,
                          description: `Received ${qty}x ${product.name} from warehouse`,
                      });
                  }

                  const orderNames = ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Wilson'];
                  for (let i = 0; i < 2 + Math.floor(Math.random() * 2); i++) {
                      const orderNum = `ORD-${1000 + Math.floor(Math.random() * 9000)}`;
                      const customer = orderNames[Math.floor(Math.random() * orderNames.length)];
                      const baseTime = now - 2 * 24 * 60 * 60 * 1000;

                      rows.push({
                          id: rowId(),
                          store_id: store.id,
                          store_name: store.name,
                          action: StoreHistoryAction.ORDER_ASSIGNED,
                          created_at: isoFromMs(baseTime + i * 3600000),
                          order_id: `order_${i}`,
                          order_number: orderNum,
                          performed_by: performedBy,
                          description: `Order #${orderNum} assigned — ${customer}`,
                      });

                      rows.push({
                          id: rowId(),
                          store_id: store.id,
                          store_name: store.name,
                          action: StoreHistoryAction.ORDER_DELIVERED,
                          created_at: isoFromMs(baseTime + i * 3600000 + 7200000),
                          order_id: `order_${i}`,
                          order_number: orderNum,
                          performed_by: storePerformedBy,
                          description: `Delivered Order #${orderNum} to ${customer}`,
                      });

                      if (Math.random() > 0.5) {
                          const cashAmt = parseFloat((20 + Math.random() * 180).toFixed(2));
                          rows.push({
                              id: rowId(),
                              store_id: store.id,
                              store_name: store.name,
                              action: StoreHistoryAction.CASH_COLLECTED,
                              created_at: isoFromMs(baseTime + i * 3600000 + 7200000 + 60000),
                              order_id: `order_${i}`,
                              order_number: orderNum,
                              cash_amount: cashAmt,
                              performed_by: storePerformedBy,
                              description: `Collected $${cashAmt.toFixed(2)} cash from ${customer}`,
                          });
                      }
                  }

                  const turnInAmount = parseFloat((50 + Math.random() * 200).toFixed(2));
                  rows.push({
                      id: rowId(),
                      store_id: store.id,
                      store_name: store.name,
                      action: StoreHistoryAction.CASH_TURNED_IN,
                      created_at: isoFromMs(now - 1 * 24 * 60 * 60 * 1000 + Math.floor(Math.random() * 3600000)),
                      cash_amount: turnInAmount,
                      performed_by: performedBy,
                      description: `Turned in $${turnInAmount.toFixed(2)} cash to warehouse`,
                  });

                  const returnProduct = storeProducts[0];
                  const returnQty = 2 + Math.floor(Math.random() * 5);
                  rows.push({
                      id: rowId(),
                      store_id: store.id,
                      store_name: store.name,
                      action: StoreHistoryAction.STOCK_RETURNED,
                      created_at: isoFromMs(now - 1 * 24 * 60 * 60 * 1000 + 3600000 + Math.floor(Math.random() * 3600000)),
                      product_id: returnProduct.id,
                      product_name: returnProduct.name,
                      quantity_change: -returnQty,
                      performed_by: performedBy,
                      description: `Returned ${returnQty}x ${returnProduct.name} to warehouse`,
                  });
              }

              const { error } = await supabase().from('store_history').insert(rows);
              if (error) throw new Error(`store_history insert: ${error.message}`);
              showToast(`Store history seeded: ${rows.length} entries for ${storeUsers.length} stores`, 'success');
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const existingSnap = await dbInstance.collection('storeHistory').limit(1).get();
          if (!existingSnap.empty) {
              showToast('Store history already has data', 'info');
              return;
          }

          const storeUsers = users.filter(u => u.role === UserRole.STORE);
          if (storeUsers.length === 0 || products.length === 0) {
              showToast('Need at least one store and products to seed history', 'error');
              return;
          }

          const batch = dbInstance.batch();
          const now = Date.now();
          let entryCount = 0;

          for (const store of storeUsers) {
              const performedBy = {
                  userId: currentUser?.id || 'system',
                  userName: currentUser?.name || 'System',
                  role: currentUser?.role || UserRole.SUPER_ADMIN,
              };
              const storePerformedBy = {
                  userId: store.id,
                  userName: store.name,
                  role: UserRole.STORE,
              };

              const shuffled = [...products].sort(() => Math.random() - 0.5);
              const storeProducts = shuffled.slice(0, Math.min(3 + Math.floor(Math.random() * 3), products.length));

              for (const product of storeProducts) {
                  const qty = 5 + Math.floor(Math.random() * 20);
                  const id = `sh_${now}_${Math.random().toString(36).substr(2, 9)}`;
                  const entry: StoreHistoryEntry = {
                      id,
                      storeId: store.id,
                      storeName: store.name,
                      action: StoreHistoryAction.STOCK_RECEIVED,
                      timestamp: now - 3 * 24 * 60 * 60 * 1000 + Math.floor(Math.random() * 3600000),
                      productId: product.id,
                      productName: product.name,
                      quantityChange: qty,
                      performedBy,
                      description: `Received ${qty}x ${product.name} from warehouse`,
                  };
                  batch.set(dbInstance.collection('storeHistory').doc(id), entry);
                  entryCount++;
              }

              const orderNames = ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Wilson'];
              for (let i = 0; i < 2 + Math.floor(Math.random() * 2); i++) {
                  const orderNum = `ORD-${1000 + Math.floor(Math.random() * 9000)}`;
                  const customer = orderNames[Math.floor(Math.random() * orderNames.length)];
                  const baseTime = now - 2 * 24 * 60 * 60 * 1000;

                  const assignId = `sh_${now}_${Math.random().toString(36).substr(2, 9)}`;
                  batch.set(dbInstance.collection('storeHistory').doc(assignId), {
                      id: assignId,
                      storeId: store.id,
                      storeName: store.name,
                      action: StoreHistoryAction.ORDER_ASSIGNED,
                      timestamp: baseTime + i * 3600000,
                      orderId: `order_${i}`,
                      orderNumber: orderNum,
                      performedBy,
                      description: `Order #${orderNum} assigned — ${customer}`,
                  });
                  entryCount++;

                  const deliverId = `sh_${now}_${Math.random().toString(36).substr(2, 9)}`;
                  batch.set(dbInstance.collection('storeHistory').doc(deliverId), {
                      id: deliverId,
                      storeId: store.id,
                      storeName: store.name,
                      action: StoreHistoryAction.ORDER_DELIVERED,
                      timestamp: baseTime + i * 3600000 + 7200000,
                      orderId: `order_${i}`,
                      orderNumber: orderNum,
                      performedBy: storePerformedBy,
                      description: `Delivered Order #${orderNum} to ${customer}`,
                  });
                  entryCount++;

                  if (Math.random() > 0.5) {
                      const cashAmt = parseFloat((20 + Math.random() * 180).toFixed(2));
                      const cashId = `sh_${now}_${Math.random().toString(36).substr(2, 9)}`;
                      batch.set(dbInstance.collection('storeHistory').doc(cashId), {
                          id: cashId,
                          storeId: store.id,
                          storeName: store.name,
                          action: StoreHistoryAction.CASH_COLLECTED,
                          timestamp: baseTime + i * 3600000 + 7200000 + 60000,
                          orderId: `order_${i}`,
                          orderNumber: orderNum,
                          cashAmount: cashAmt,
                          performedBy: storePerformedBy,
                          description: `Collected $${cashAmt.toFixed(2)} cash from ${customer}`,
                      });
                      entryCount++;
                  }
              }

              const cashTurnInId = `sh_${now}_${Math.random().toString(36).substr(2, 9)}`;
              const turnInAmount = parseFloat((50 + Math.random() * 200).toFixed(2));
              batch.set(dbInstance.collection('storeHistory').doc(cashTurnInId), {
                  id: cashTurnInId,
                  storeId: store.id,
                  storeName: store.name,
                  action: StoreHistoryAction.CASH_TURNED_IN,
                  timestamp: now - 1 * 24 * 60 * 60 * 1000 + Math.floor(Math.random() * 3600000),
                  cashAmount: turnInAmount,
                  performedBy,
                  description: `Turned in $${turnInAmount.toFixed(2)} cash to warehouse`,
              });
              entryCount++;

              const returnProduct = storeProducts[0];
              const returnQty = 2 + Math.floor(Math.random() * 5);
              const returnId = `sh_${now}_${Math.random().toString(36).substr(2, 9)}`;
              batch.set(dbInstance.collection('storeHistory').doc(returnId), {
                  id: returnId,
                  storeId: store.id,
                  storeName: store.name,
                  action: StoreHistoryAction.STOCK_RETURNED,
                  timestamp: now - 1 * 24 * 60 * 60 * 1000 + 3600000 + Math.floor(Math.random() * 3600000),
                  productId: returnProduct.id,
                  productName: returnProduct.name,
                  quantityChange: -returnQty,
                  performedBy,
                  description: `Returned ${returnQty}x ${returnProduct.name} to warehouse`,
              });
              entryCount++;
          }

          await batch.commit();
          showToast(`Store history seeded: ${entryCount} entries for ${storeUsers.length} stores`, 'success');
      } catch (e: any) {
          console.error('Store history seed error:', e);
          showToast(`Failed to seed store history: ${e.message || e}`, 'error');
      }
  };

  const seedDriverHistory = async () => {
      try {
          if (USE_SUPABASE) {
              const { data: existing, error: existErr } = await supabase()
                  .from('driver_history').select('id').limit(1);
              if (existErr) throw new Error(`driver_history: ${existErr.message}`);
              if (existing && existing.length > 0) {
                  showToast('Driver history already has data', 'info');
                  return;
              }

              const driverUsers = users.filter(u => u.role === UserRole.DRIVER);
              if (driverUsers.length === 0 || products.length === 0) {
                  showToast('Need at least one driver and products to seed history', 'error');
                  return;
              }

              const now = Date.now();
              const rows: Record<string, unknown>[] = [];
              const performedBy = {
                  userId: currentUser?.id || 'system',
                  userName: currentUser?.name || 'System',
                  role: currentUser?.role || UserRole.SUPER_ADMIN,
              };

              for (const driver of driverUsers) {
                  const shuffled = [...products].sort(() => Math.random() - 0.5);
                  const driverProducts = shuffled.slice(0, Math.min(2 + Math.floor(Math.random() * 3), products.length));

                  for (const product of driverProducts) {
                      const qty = 3 + Math.floor(Math.random() * 15);
                      rows.push({
                          id: `dh_${now}_${Math.random().toString(36).substr(2, 9)}`,
                          driver_id: driver.id,
                          driver_name: driver.name,
                          action: DriverHistoryAction.STOCK_RECEIVED,
                          created_at: isoFromMs(now - 3 * 24 * 60 * 60 * 1000 + Math.floor(Math.random() * 3600000)),
                          product_id: product.id,
                          product_name: product.name,
                          quantity_change: qty,
                          performed_by: performedBy,
                          description: `Received ${qty}x ${product.name} from warehouse`,
                          extra: {
                              movementType: MovementType.TOP_UP,
                              fromEntity: 'warehouse',
                              toEntity: driver.id,
                          },
                      });
                  }
              }

              const { error } = await supabase().from('driver_history').insert(rows);
              if (error) throw new Error(`driver_history insert: ${error.message}`);
              showToast(`Driver history seeded: ${rows.length} entries for ${driverUsers.length} drivers`, 'success');
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const existingSnap = await dbInstance.collection('driverHistory').limit(1).get();
          if (!existingSnap.empty) {
              showToast('Driver history already has data', 'info');
              return;
          }

          const driverUsers = users.filter(u => u.role === UserRole.DRIVER);
          if (driverUsers.length === 0 || products.length === 0) {
              showToast('Need at least one driver and products to seed history', 'error');
              return;
          }

          const batch = dbInstance.batch();
          const now = Date.now();
          let entryCount = 0;

          for (const driver of driverUsers) {
              const performedBy = {
                  userId: currentUser?.id || 'system',
                  userName: currentUser?.name || 'System',
                  role: currentUser?.role || UserRole.SUPER_ADMIN,
              };

              const shuffled = [...products].sort(() => Math.random() - 0.5);
              const driverProducts = shuffled.slice(0, Math.min(2 + Math.floor(Math.random() * 3), products.length));

              for (const product of driverProducts) {
                  const qty = 3 + Math.floor(Math.random() * 15);
                  const id = `dh_${now}_${Math.random().toString(36).substr(2, 9)}`;
                  const entry: DriverHistoryEntry = {
                      id,
                      driverId: driver.id,
                      driverName: driver.name,
                      action: DriverHistoryAction.STOCK_RECEIVED,
                      timestamp: now - 3 * 24 * 60 * 60 * 1000 + Math.floor(Math.random() * 3600000),
                      productId: product.id,
                      productName: product.name,
                      quantityChange: qty,
                      performedBy,
                      description: `Received ${qty}x ${product.name} from warehouse`,
                      movementType: MovementType.TOP_UP,
                      fromEntity: 'warehouse',
                      toEntity: driver.id,
                  };
                  batch.set(dbInstance.collection('driverHistory').doc(id), entry);
                  entryCount++;
              }
          }

          await batch.commit();
          showToast(`Driver history seeded: ${entryCount} entries for ${driverUsers.length} drivers`, 'success');
      } catch (e: any) {
          console.error('Driver history seed error:', e);
          showToast(`Failed to seed driver history: ${e.message || e}`, 'error');
      }
  };

  const addProduct = async (product: Product, source?: 'csv' | 'manual') => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          if (USE_SUPABASE) {
              const { error } = await sbWriteProduct((row) => supabase().from('products').upsert(row), sbProductRow(product));
              if (error) throw new Error(`products upsert: ${error.message}`);
          } else {
          await dbInstance.collection('products').doc(product.id).set(product);
          }
          showToast("Product added successfully", "success");
          logWarehouseHistory({
              productId: product.id,
              productName: product.name,
              action: source === 'csv' ? WarehouseHistoryAction.CSV_IMPORT_CREATED : WarehouseHistoryAction.PRODUCT_CREATED,
              timestamp: Date.now(),
              performedBy: {
                  userId: currentUser?.id || 'unknown',
                  userName: currentUser?.name || 'Unknown',
                  role: currentUser?.role || UserRole.SUPER_ADMIN,
              },
              quantityChange: product.warehouseStock,
              quantityBefore: 0,
              quantityAfter: product.warehouseStock,
              description: source === 'csv'
                  ? `CSV import: Product created with ${product.warehouseStock} units`
                  : `Product created with ${product.warehouseStock} warehouse units`,
          });
      } catch (e) {
          showToast("Failed to add product", "error");
          throw e;
      }
  };

  const updateProduct = async (product: Product, source?: 'csv' | 'manual') => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const oldProduct = products.find(p => p.id === product.id);
          const updatedProduct = { ...product };
          // Auto-deactivate ONLY on the moment stock crosses to zero (was > 0,
          // now <= 0). If stock was already <= 0 (e.g. admin is clicking
          // Reactivate while stock is still 0), respect what the user is
          // setting. Otherwise the Reactivate button is permanently broken
          // until the admin first refills stock.
          const wasInStock = (oldProduct?.warehouseStock ?? 0) > 0;
          const isOutNow = updatedProduct.warehouseStock <= 0;
          if (wasInStock && isOutNow) updatedProduct.isActive = false;
          if (USE_SUPABASE) {
              const { error } = await sbWriteProduct((row) => supabase().from('products').update(row).eq('id', product.id), sbProductRow(updatedProduct));
              if (error) throw new Error(`products update: ${error.message}`);
          } else {
          await dbInstance.collection('products').doc(product.id).update({ ...updatedProduct });
          }
          showToast("Product updated successfully", "success");
          if (oldProduct && oldProduct.warehouseStock !== product.warehouseStock) {
              const diff = product.warehouseStock - oldProduct.warehouseStock;
              await logWarehouseHistory({
                  productId: product.id,
                  productName: product.name,
                  action: source === 'csv' ? WarehouseHistoryAction.CSV_IMPORT_UPDATED : WarehouseHistoryAction.STOCK_EDITED,
                  timestamp: Date.now(),
                  performedBy: {
                      userId: currentUser?.id || 'unknown',
                      userName: currentUser?.name || 'Unknown',
                      role: currentUser?.role || UserRole.SUPER_ADMIN,
                  },
                  quantityChange: diff,
                  quantityBefore: oldProduct.warehouseStock,
                  quantityAfter: product.warehouseStock,
                  description: source === 'csv'
                      ? `CSV import: Stock changed from ${oldProduct.warehouseStock} to ${product.warehouseStock}`
                      : `Warehouse stock manually changed from ${oldProduct.warehouseStock} to ${product.warehouseStock}`,
              });
          }
      } catch (e) {
          showToast("Failed to update product", "error");
          throw e;
      }
  };

  const deleteProduct = (id: string): boolean => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const inStoreStock = storeStocks.some(ds => ds.productId === id && ds.quantity > 0);
          const inDriverStock = driverStocks.some(ds => ds.productId === id && ds.quantity > 0);
          if (inStoreStock || inDriverStock) return false;
          const product = products.find(p => p.id === id);
          if (USE_SUPABASE) {
              // Fire-and-forget to mirror the un-awaited Firestore delete.
              supabase().from('products').delete().eq('id', id).then(({ error }) => {
                  if (error) console.error('products delete:', error.message);
              });
          } else {
          dbInstance.collection('products').doc(id).delete();
          }
          showToast("Product deleted successfully", "success");
          if (product) {
              logWarehouseHistory({
                  productId: id,
                  productName: product.name,
                  action: WarehouseHistoryAction.PRODUCT_DELETED,
                  timestamp: Date.now(),
                  performedBy: {
                      userId: currentUser?.id || 'unknown',
                      userName: currentUser?.name || 'Unknown',
                      role: currentUser?.role || UserRole.SUPER_ADMIN,
                  },
                  quantityChange: -product.warehouseStock,
                  quantityBefore: product.warehouseStock,
                  quantityAfter: 0,
                  description: `Product deleted (had ${product.warehouseStock} warehouse units)`,
              });
          }
          return true;
      } catch (e) {
          showToast("Failed to delete product", "error");
          return false;
      }
  };

  const updateStoreStockQuantity = async (storeId: string, productId: string, delta: number) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const stockId = `${storeId}_${productId}`;
          const existing = storeStocks.find(ds => ds.storeId === storeId && ds.productId === productId);
          const newQty = (existing?.quantity || 0) + delta;
          if (USE_SUPABASE) {
              // Guarded: this is the path the 2026-09-09 re-up took. RLS denied
              // every write and this reported success, losing 687 units.
              await mustAffectRows('store_stocks upsert', () =>
                  supabase().from('store_stocks').upsert(
                      { store_id: storeId, product_id: productId, quantity: Math.max(0, newQty) },
                      { onConflict: 'store_id,product_id' }
                  )
              );
          } else {
          await dbInstance.collection('storeStocks').doc(stockId).set({ storeId, productId, quantity: Math.max(0, newQty) });
          }
      } catch (e) {
          throw e;
      }
  };

  const updateDriverStockQuantity = async (driverId: string, productId: string, delta: number) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const stockId = `${driverId}_${productId}`;
          const existing = driverStocks.find(ds => ds.driverId === driverId && ds.productId === productId);
          const newQty = (existing?.quantity || 0) + delta;
          if (USE_SUPABASE) {
              await mustAffectRows('driver_stocks upsert', () =>
                  supabase().from('driver_stocks').upsert(
                      { driver_id: driverId, product_id: productId, quantity: Math.max(0, newQty) },
                      { onConflict: 'driver_id,product_id' }
                  )
              );
          } else {
          await dbInstance.collection('driverStocks').doc(stockId).set({ driverId, productId, quantity: Math.max(0, newQty) });
          }
      } catch (e) {
          throw e;
      }
  };

  const updateWarehouseStock = async (productId: string, delta: number) => {
      const { db: dbInstance } = ensureFirebaseInitialized();
      const p = products.find(prod => prod.id === productId);
      if (p) {
          const newStock = p.warehouseStock + delta;
          const updates: any = { warehouseStock: newStock };
          if (newStock <= 0) updates.isActive = false;
          if (USE_SUPABASE) {
              const row: Record<string, unknown> = { warehouse_stock: newStock };
              if (newStock <= 0) row.is_active = false;
              const { error } = await supabase().from('products').update(row).eq('id', productId);
              if (error) throw new Error(`products update: ${error.message}`);
          } else {
          await dbInstance.collection('products').doc(productId).update(updates);
          }
      }
  };

  const createTransaction = async (t: StockTransaction) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          if (USE_SUPABASE) {
              await sbWriteTransaction(t);
          } else {
          await dbInstance.collection('transactions').doc(t.id).set(t);
          }
          const store = users.find(u => u.id === t.storeId);
          const itemSummary = t.items.map(i => {
              const p = products.find(pr => pr.id === i.productId);
              return `${i.quantity}x ${p?.name || 'Unknown'}`;
          }).join(', ');
          logWarehouseHistory({
              productId: 'system',
              productName: 'Transaction',
              action: WarehouseHistoryAction.TRANSACTION_CREATED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              storeName: store?.name,
              description: `Transaction created — ${t.type} for ${store?.name || 'Unknown Store'}${t.cashAmount ? ` — $${t.cashAmount.toFixed(2)}` : ` — ${itemSummary}`}`,
          });
      } catch (e) {
          showToast("Transaction failed", "error");
          throw e;
      }
  };

  // Tx types whose APPROVED transition runs server-side via the
  // approve-transaction Netlify Function. These are the bulk-transfer types
  // (5 ops × N items) that historically lost writes when the user closed the
  // tab mid-flight. Other types (cash_turn_in, store_to_driver,
  // driver_to_driver) still use the legacy client-side path below — they're
  // either single-write (cash) or low-volume.
  //
  // Postgres: the same four types go through the approve_transaction()
  // function (sbApproveTransaction) — one RPC, one database transaction,
  // same rows the cascade below writes. The cascade is still the path for
  // the other types in both backends.
  const SERVER_HANDLED_APPROVAL_TYPES: ReadonlySet<TransactionType> = new Set([
      TransactionType.ASSIGN_TO_STORE,
      TransactionType.RETURN_FROM_STORE,
      TransactionType.ASSIGN_TO_DRIVER,
      TransactionType.RETURN_FROM_DRIVER,
  ]);

  const updateTransactionStatus = async (id: string, newStatus: TransactionStatus, receipt?: TransactionReceipt) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const t = transactions.find(tr => tr.id === id);
          if (!t) return;

          // Server-authoritative path for bulk transfers — function handles
          // status flip + every per-item write atomically. Browser only sends
          // one HTTP request and is no longer in the loop.
          if (newStatus === TransactionStatus.APPROVED && SERVER_HANDLED_APPROVAL_TYPES.has(t.type)) {
              if (USE_SUPABASE) {
                  // approve_transaction() RPC — status flip + every per-item
                  // write in one database transaction (0031, receipt counts
                  // 0056). Same toasts and error contract as the Netlify
                  // branch: the function's message is what the user sees.
                  const performedBy = getPerformedBy();
                  let res: { discrepancy?: boolean } = {};
                  try {
                      res = await sbApproveTransaction(id, performedBy, receipt);
                  } catch (e: any) {
                      const errMsg = e?.message || 'Approval failed';
                      showToast(errMsg, 'error');
                      throw new Error(errMsg);
                  }
                  showToast(res.discrepancy
                      ? 'Received with a discrepancy — flagged for dispatch review'
                      : 'Transaction approved', res.discrepancy ? 'info' : 'success');
                  return;
              } else {
              if (receipt) {
                  // The Firestore fallback line has no receipt counts; the
                  // transfer is approved in full and the note is lost.
                  showToast('Received counts are only recorded on the Postgres backend — approving in full', 'info');
              }
              const performedBy = getPerformedBy();
              const res = await fetch('/.netlify/functions/approve-transaction', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ txId: id, performedBy }),
              });
              if (!res.ok) {
                  let errMsg = `Approval failed (HTTP ${res.status})`;
                  try {
                      const errBody = await res.json();
                      if (errBody?.error) errMsg = errBody.error;
                  } catch { /* keep default */ }
                  showToast(errMsg, 'error');
                  throw new Error(errMsg);
              }
              showToast('Transaction approved', 'success');
              return;
              }
          }

          if (USE_SUPABASE) {
              const { error } = await supabase().from('transactions').update({ status: newStatus }).eq('id', id);
              if (error) throw new Error(`transactions update: ${error.message}`);
          } else {
          await dbInstance.collection('transactions').doc(id).update({ status: newStatus });
          }
          const txStore = users.find(u => u.id === t.storeId);
          await logWarehouseHistory({
              productId: 'system',
              productName: 'Transaction',
              action: WarehouseHistoryAction.TRANSACTION_STATUS_CHANGED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              storeName: txStore?.name,
              description: `Transaction ${newStatus} — ${t.type} for ${txStore?.name || 'Unknown Store'} (was: ${t.status})`,
          });
          if (newStatus === TransactionStatus.APPROVED) {
            const store = users.find(u => u.id === t.storeId);
            const storeName = store?.name || 'Unknown Store';
            if (t.type === TransactionType.ASSIGN_TO_STORE) {
                t.items.forEach(item => {
                    const product = products.find(p => p.id === item.productId);
                    updateWarehouseStock(item.productId, -item.quantity);
                    updateStoreStockQuantity(t.storeId, item.productId, item.quantity);
                    if (product) {
                        logWarehouseHistory({
                            productId: item.productId,
                            productName: product.name,
                            action: WarehouseHistoryAction.STOCK_OUT_TO_STORE,
                            timestamp: Date.now(),
                            performedBy: getPerformedBy(),
                            quantityChange: -item.quantity,
                            quantityBefore: product.warehouseStock,
                            quantityAfter: product.warehouseStock - item.quantity,
                            storeName,
                            transactionId: t.id,
                            description: `${item.quantity} units sent to ${storeName}`,
                            movementType: MovementType.WAREHOUSE_TO_STORE,
                            fromEntity: 'warehouse',
                            toEntity: t.storeId,
                            storeId: t.storeId,
                        });
                        logStoreHistory({
                            storeId: t.storeId,
                            storeName,
                            action: StoreHistoryAction.STOCK_RECEIVED,
                            timestamp: Date.now(),
                            productId: item.productId,
                            productName: product.name,
                            quantityChange: item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Received ${item.quantity}x ${product.name} from warehouse`,
                            movementType: MovementType.WAREHOUSE_TO_STORE,
                            fromEntity: 'warehouse',
                            toEntity: t.storeId,
                        });
                        createMovement({
                            date: getBusinessDate(),
                            productId: item.productId,
                            productName: product.name,
                            type: MovementType.WAREHOUSE_TO_STORE,
                            fromEntity: 'Warehouse',
                            toEntity: storeName,
                            storeId: t.storeId,
                            driverId: null,
                            toDriverId: null,
                            orderId: null,
                            orderNumber: null,
                            qty: item.quantity,
                            reason: null,
                            timestamp: Date.now(),
                        });
                    }
                });
            } else if (t.type === TransactionType.RETURN_FROM_STORE) {
                t.items.forEach(item => {
                    const product = products.find(p => p.id === item.productId);
                    updateWarehouseStock(item.productId, item.quantity);
                    updateStoreStockQuantity(t.storeId, item.productId, -item.quantity);
                    if (product) {
                        logWarehouseHistory({
                            productId: item.productId,
                            productName: product.name,
                            action: WarehouseHistoryAction.STOCK_IN_FROM_STORE,
                            timestamp: Date.now(),
                            performedBy: getPerformedBy(),
                            quantityChange: item.quantity,
                            quantityBefore: product.warehouseStock,
                            quantityAfter: product.warehouseStock + item.quantity,
                            storeName,
                            transactionId: t.id,
                            description: `${item.quantity} units returned from ${storeName}`,
                            movementType: MovementType.STORE_TO_WAREHOUSE,
                            fromEntity: t.storeId,
                            toEntity: 'warehouse',
                            storeId: t.storeId,
                        });
                        logStoreHistory({
                            storeId: t.storeId,
                            storeName,
                            action: StoreHistoryAction.STOCK_RETURNED,
                            timestamp: Date.now(),
                            productId: item.productId,
                            productName: product.name,
                            quantityChange: -item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Returned ${item.quantity}x ${product.name} to warehouse`,
                            movementType: MovementType.STORE_TO_WAREHOUSE,
                            fromEntity: t.storeId,
                            toEntity: 'warehouse',
                        });
                        // Inventory Tracker: movement ledger
                        createMovement({
                            date: getBusinessDate(),
                            productId: item.productId,
                            productName: product.name,
                            type: MovementType.STORE_TO_WAREHOUSE,
                            fromEntity: storeName,
                            toEntity: 'Warehouse',
                            storeId: t.storeId,
                            driverId: null,
                            toDriverId: null,
                            orderId: null,
                            orderNumber: null,
                            qty: item.quantity,
                            reason: null,
                            timestamp: Date.now(),
                        });
                    }
                });
            } else if (t.type === TransactionType.CASH_TURN_IN) {
                logStoreHistory({
                    storeId: t.storeId,
                    storeName,
                    action: StoreHistoryAction.CASH_TURNED_IN,
                    timestamp: Date.now(),
                    cashAmount: t.cashAmount || 0,
                    performedBy: getPerformedBy(),
                    description: `Turned in $${(t.cashAmount || 0).toFixed(2)} cash to warehouse`,
                });
            } else if (t.type === TransactionType.ASSIGN_TO_DRIVER && t.driverId) {
                // Warehouse → Driver: deduct from warehouse, add to driver
                const driver = users.find(u => u.id === t.driverId);
                const driverName = driver?.name || 'Unknown Driver';
                t.items.forEach(item => {
                    const product = products.find(p => p.id === item.productId);
                    updateWarehouseStock(item.productId, -item.quantity);
                    updateDriverStockQuantity(t.driverId!, item.productId, item.quantity);
                    if (product) {
                        logWarehouseHistory({
                            productId: item.productId, productName: product.name,
                            action: WarehouseHistoryAction.STOCK_OUT_TO_DRIVER, timestamp: Date.now(),
                            performedBy: getPerformedBy(),
                            quantityChange: -item.quantity, quantityBefore: product.warehouseStock,
                            quantityAfter: product.warehouseStock - item.quantity,
                            storeName: driverName, transactionId: t.id,
                            description: `${item.quantity} units sent to driver ${driverName}`,
                            movementType: MovementType.TOP_UP,
                            fromEntity: 'warehouse',
                            toEntity: t.driverId!,
                            driverId: t.driverId!,
                        });
                        logDriverHistory({
                            driverId: t.driverId!, driverName,
                            action: DriverHistoryAction.STOCK_RECEIVED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Received ${item.quantity}x ${product.name} from warehouse`,
                            movementType: MovementType.TOP_UP,
                            fromEntity: 'warehouse',
                            toEntity: t.driverId!,
                        });
                        // Inventory Tracker: movement ledger
                        createMovement({
                            date: getBusinessDate(),
                            productId: item.productId,
                            productName: product.name,
                            type: MovementType.TOP_UP,
                            fromEntity: 'Warehouse',
                            toEntity: driverName,
                            storeId: null,
                            driverId: t.driverId!,
                            toDriverId: null,
                            orderId: null,
                            orderNumber: null,
                            qty: item.quantity,
                            reason: null,
                            timestamp: Date.now(),
                        });
                    }
                });
            } else if (t.type === TransactionType.RETURN_FROM_DRIVER && t.driverId) {
                // Driver → Warehouse: deduct from driver, add to warehouse
                const driver = users.find(u => u.id === t.driverId);
                const driverName = driver?.name || 'Unknown Driver';
                t.items.forEach(item => {
                    const product = products.find(p => p.id === item.productId);
                    updateWarehouseStock(item.productId, item.quantity);
                    updateDriverStockQuantity(t.driverId!, item.productId, -item.quantity);
                    if (product) {
                        logWarehouseHistory({
                            productId: item.productId, productName: product.name,
                            action: WarehouseHistoryAction.STOCK_IN_FROM_DRIVER, timestamp: Date.now(),
                            performedBy: getPerformedBy(),
                            quantityChange: item.quantity, quantityBefore: product.warehouseStock,
                            quantityAfter: product.warehouseStock + item.quantity,
                            storeName: driverName, transactionId: t.id,
                            description: `${item.quantity} units returned from driver ${driverName}`,
                        });
                        logDriverHistory({
                            driverId: t.driverId!, driverName,
                            action: DriverHistoryAction.STOCK_RETURNED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: -item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Returned ${item.quantity}x ${product.name} to warehouse`,
                        });
                        // Inventory Tracker: movement ledger
                        createMovement({
                            date: getBusinessDate(),
                            productId: item.productId,
                            productName: product.name,
                            type: MovementType.DRIVER_TO_WAREHOUSE,
                            fromEntity: driverName,
                            toEntity: 'Warehouse',
                            storeId: null,
                            driverId: t.driverId!,
                            toDriverId: null,
                            orderId: null,
                            orderNumber: null,
                            qty: item.quantity,
                            reason: null,
                            timestamp: Date.now(),
                        });
                    }
                });
            } else if (t.type === TransactionType.DRIVER_TO_DRIVER && t.fromDriverId && t.toDriverId) {
                // Driver → Driver: deduct from sender, add to receiver
                const fromDriver = users.find(u => u.id === t.fromDriverId);
                const toDriver = users.find(u => u.id === t.toDriverId);
                t.items.forEach(item => {
                    const product = products.find(p => p.id === item.productId);
                    updateDriverStockQuantity(t.fromDriverId!, item.productId, -item.quantity);
                    updateDriverStockQuantity(t.toDriverId!, item.productId, item.quantity);
                    if (product) {
                        logDriverHistory({
                            driverId: t.fromDriverId!, driverName: fromDriver?.name || 'Unknown',
                            action: DriverHistoryAction.STOCK_RETURNED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: -item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Transferred ${item.quantity}x ${product.name} to ${toDriver?.name || 'Unknown'}`,
                        });
                        logDriverHistory({
                            driverId: t.toDriverId!, driverName: toDriver?.name || 'Unknown',
                            action: DriverHistoryAction.STOCK_RECEIVED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Received ${item.quantity}x ${product.name} from ${fromDriver?.name || 'Unknown'}`,
                        });
                        // Inventory Tracker: movement ledger
                        createMovement({
                            date: getBusinessDate(),
                            productId: item.productId,
                            productName: product.name,
                            type: MovementType.DRIVER_TO_DRIVER,
                            fromEntity: fromDriver?.name || 'Unknown',
                            toEntity: toDriver?.name || 'Unknown',
                            storeId: t.storeId || null,
                            driverId: t.fromDriverId!,
                            toDriverId: t.toDriverId!,
                            orderId: null,
                            orderNumber: null,
                            qty: item.quantity,
                            reason: null,
                            timestamp: Date.now(),
                        });
                    }
                });
            } else if (t.type === TransactionType.STORE_TO_DRIVER && t.driverId) {
                // Store → Driver: deduct from store, add to driver
                const driver = users.find(u => u.id === t.driverId);
                const driverName = driver?.name || 'Unknown Driver';
                t.items.forEach(item => {
                    const product = products.find(p => p.id === item.productId);
                    updateStoreStockQuantity(t.storeId, item.productId, -item.quantity);
                    updateDriverStockQuantity(t.driverId!, item.productId, item.quantity);
                    if (product) {
                        logStoreHistory({
                            storeId: t.storeId, storeName,
                            action: StoreHistoryAction.STOCK_RETURNED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: -item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Gave ${item.quantity}x ${product.name} to driver ${driverName}`,
                        });
                        logDriverHistory({
                            driverId: t.driverId!, driverName,
                            action: DriverHistoryAction.STOCK_RECEIVED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Received ${item.quantity}x ${product.name} from ${storeName}`,
                        });
                        // Inventory Tracker: movement ledger
                        createMovement({
                            date: getBusinessDate(),
                            productId: item.productId,
                            productName: product.name,
                            type: MovementType.STORE_TO_DRIVER,
                            fromEntity: storeName,
                            toEntity: driverName,
                            storeId: t.storeId,
                            driverId: t.driverId!,
                            toDriverId: null,
                            orderId: null,
                            orderNumber: null,
                            qty: item.quantity,
                            reason: null,
                            timestamp: Date.now(),
                        });
                    }
                });
            } else if (t.type === TransactionType.DRIVER_TO_STORE && t.driverId) {
                // Driver → Store: deduct from driver, add to store
                const driver = users.find(u => u.id === t.driverId);
                const driverName = driver?.name || 'Unknown Driver';
                t.items.forEach(item => {
                    const product = products.find(p => p.id === item.productId);
                    updateDriverStockQuantity(t.driverId!, item.productId, -item.quantity);
                    updateStoreStockQuantity(t.storeId, item.productId, item.quantity);
                    if (product) {
                        logDriverHistory({
                            driverId: t.driverId!, driverName,
                            action: DriverHistoryAction.STOCK_RETURNED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: -item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Returned ${item.quantity}x ${product.name} to ${storeName}`,
                        });
                        logStoreHistory({
                            storeId: t.storeId, storeName,
                            action: StoreHistoryAction.STOCK_RECEIVED, timestamp: Date.now(),
                            productId: item.productId, productName: product.name,
                            quantityChange: item.quantity,
                            performedBy: getPerformedBy(),
                            description: `Received ${item.quantity}x ${product.name} from driver ${driverName}`,
                        });
                        // Inventory Tracker: movement ledger
                        createMovement({
                            date: getBusinessDate(),
                            productId: item.productId,
                            productName: product.name,
                            type: MovementType.DRIVER_TO_STORE,
                            fromEntity: driverName,
                            toEntity: storeName,
                            storeId: t.storeId,
                            driverId: t.driverId!,
                            toDriverId: null,
                            orderId: null,
                            orderNumber: null,
                            qty: item.quantity,
                            reason: null,
                            timestamp: Date.now(),
                        });
                    }
                });
            }
          }
          showToast(`Transaction ${newStatus}`, "success");
      } catch (e) {
          showToast("Failed to update transaction", "error");
          throw e;
      }
  };

  const deleteTransaction = async (id: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const t = transactions.find(tr => tr.id === id);
          const txStore = t ? users.find(u => u.id === t.storeId) : null;
          if (USE_SUPABASE) {
              // transaction_items cascade on delete.
              const { error } = await supabase().from('transactions').delete().eq('id', id);
              if (error) throw new Error(`transactions delete: ${error.message}`);
          } else {
          await dbInstance.collection('transactions').doc(id).delete();
          }
          logWarehouseHistory({
              productId: 'system',
              productName: 'Transaction',
              action: WarehouseHistoryAction.TRANSACTION_DELETED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              storeName: txStore?.name,
              description: `Transaction deleted — ${t?.type || 'unknown'} for ${txStore?.name || 'Unknown Store'} (status was: ${t?.status || 'unknown'})`,
          });
          showToast("Transaction removed", "success");
      } catch (e) {
          showToast("Failed to delete transaction", "error");
          throw e;
      }
  };

  const createOrder = async (order: Order) => {
      try {
          if (USE_SUPABASE) {
              // The caller's order number counts an in-memory 30-day window,
              // so two dispatchers can mint the same one and 0006 relaxed the
              // UNIQUE constraint. next_order_number() is the atomic source.
              // Stamped back onto the object because the caller reads
              // orderNumber after this resolves (order-arrival email).
              const { data: nextNumber, error } = await supabase().rpc('next_order_number');
              if (error) throw new Error(`next_order_number: ${error.message}`);
              if (nextNumber) order.orderNumber = nextNumber as string;
              await sbWriteOrder(order, { create: true });
          } else {
          const { db: dbInstance } = ensureFirebaseInitialized();
          await dbInstance.collection('orders').doc(order.id).set(order);
          }
          const orderNum = order.orderNumber || order.id.slice(-6).toUpperCase();
          const itemSummary = order.items.map(i => {
              const p = products.find(pr => pr.id === i.productId);
              return `${i.quantity}x ${p?.name || 'Unknown'}`;
          }).join(', ');
          // Log to store history if assigned to a store
          if (order.storeId) {
              const store = users.find(u => u.id === order.storeId);
              logStoreHistory({
                  storeId: order.storeId,
                  storeName: store?.name || 'Unknown Store',
                  action: StoreHistoryAction.ORDER_CREATED,
                  timestamp: Date.now(),
                  orderId: order.id,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `Order #${orderNum} created — ${order.customerName} — ${itemSummary}${order.source === 'call_agent' ? ' (via call agent)' : ''}`,
              });
          }
          // Always log to warehouse level
          logWarehouseHistory({
              productId: 'system',
              productName: 'Order Management',
              action: WarehouseHistoryAction.PRODUCT_CREATED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Order #${orderNum} created — ${order.customerName} — ${itemSummary}${order.source === 'call_agent' ? ' (via call agent)' : ''}`,
          });
      } catch (e) {
          throw e;
      }
  };

  // The stock and cash history of a delivery — that store, that driver — shared by the driver's own
  // delivery (updateOrder) and the office's Mark delivered (0105), which also names who marked it.
  const logDeliveredHistory = (order: Order, markedBy?: { userId: string; userName: string; role: UserRole }) => {
      const markedNote = markedBy ? ` — marked delivered by ${markedBy.userName}` : '';
      // Store history
      if (order.storeId) {
          const store = users.find(u => u.id === order.storeId);
          const storeName = store?.name || 'Unknown Store';
          const orderNum = order.orderNumber || order.id.slice(-6).toUpperCase();
          const deliverNow = Date.now();
          for (const item of order.items) {
              const product = products.find(p => p.id === item.productId);
              logStoreHistory({
                  storeId: order.storeId,
                  storeName,
                  action: StoreHistoryAction.ORDER_DELIVERED,
                  timestamp: deliverNow,
                  orderId: order.id,
                  orderNumber: orderNum,
                  productId: item.productId,
                  productName: product?.name || 'Unknown Product',
                  quantityChange: -item.quantity,
                  performedBy: markedBy || { userId: order.storeId, userName: storeName, role: UserRole.STORE },
                  description: `Delivered ${item.quantity}x ${product?.name || 'Unknown'} — Order #${orderNum} to ${order.customerName}${markedNote}`,
              });
          }
          if (order.requiresCash && order.cashAmount > 0) {
              logStoreHistory({
                  storeId: order.storeId,
                  storeName,
                  action: StoreHistoryAction.CASH_COLLECTED,
                  timestamp: Date.now(),
                  orderId: order.id,
                  orderNumber: orderNum,
                  cashAmount: order.cashAmount,
                  performedBy: markedBy || { userId: order.storeId, userName: storeName, role: UserRole.STORE },
                  description: `Collected $${order.cashAmount.toFixed(2)} cash from ${order.customerName}${markedNote}`,
              });
          }
      }

      // Driver history + the Inventory Tracker's movement
      if (order.driverId) {
          const driver = users.find(u => u.id === order.driverId);
          const driverName = driver?.name || 'Unknown Driver';
          const orderNum = order.orderNumber || order.id.slice(-6).toUpperCase();
          const deliverNow = Date.now();
          for (const item of order.items) {
              const product = products.find(p => p.id === item.productId);
              logDriverHistory({
                  driverId: order.driverId,
                  driverName,
                  action: DriverHistoryAction.ORDER_DELIVERED,
                  timestamp: deliverNow,
                  orderId: order.id,
                  orderNumber: orderNum,
                  productId: item.productId,
                  productName: product?.name || 'Unknown Product',
                  quantityChange: -item.quantity,
                  performedBy: markedBy || { userId: order.driverId, userName: driverName, role: UserRole.DRIVER },
                  description: `Delivered ${item.quantity}x ${product?.name || 'Unknown'} — Order #${orderNum} to ${order.customerName}${markedNote}`,
              });
              // Inventory Tracker: movement ledger (delivered to customer)
              createMovement({
                  date: getBusinessDate(),
                  productId: item.productId,
                  productName: product?.name || 'Unknown Product',
                  type: MovementType.DELIVERED,
                  fromEntity: driverName,
                  toEntity: order.customerName,
                  storeId: order.storeId || null,
                  driverId: order.driverId,
                  toDriverId: null,
                  orderId: order.id,
                  orderNumber: orderNum,
                  qty: item.quantity,
                  reason: null,
                  timestamp: deliverNow,
              });
          }
          if (order.requiresCash && order.cashAmount > 0) {
              logDriverHistory({
                  driverId: order.driverId,
                  driverName,
                  action: DriverHistoryAction.CASH_COLLECTED,
                  timestamp: deliverNow,
                  orderId: order.id,
                  orderNumber: orderNum,
                  cashAmount: order.cashAmount,
                  performedBy: markedBy || { userId: order.driverId, userName: driverName, role: UserRole.DRIVER },
                  description: `Collected $${order.cashAmount.toFixed(2)} cash from ${order.customerName}${markedNote}`,
              });
          }
      }
  };

  // Mark delivered (0105, Umer 2026-09-29): the office records a delivery the driver did not mark.
  // Only once the store has handed the order to its driver (services/orderDelivery). The order is read
  // fresh — this tab's copy may be old — and written ONCE, on the condition that it is still with that
  // store and driver, so a driver marking it at the same moment cannot be delivered (and logged) twice.
  // Then the same history as the driver's own delivery, and the driver, store and owner are told who
  // marked it. true = delivered; a refusal is a toast, never a throw.
  const markOrderDelivered = async (orderId: string): Promise<boolean> => {
      if (!USE_SUPABASE) { showToast('Mark delivered needs the Supabase backend.', 'error'); return false; }
      if (!currentUser) return false;
      try {
          const sb = supabase();
          const { data: row, error: readErr } = await sb.from('orders')
              .select('*, order_items(product_id, quantity)').eq('id', orderId).maybeSingle();
          if (readErr) throw new Error(readErr.message);
          if (!row) { showToast('That order is not there any more.', 'error'); return false; }
          const fresh = sbOrderFromRow(row);
          const num = fresh.orderNumber || fresh.id.slice(-6).toUpperCase();
          const why = markDeliveredBlocker(fresh);
          if (why) { showToast(`Order #${num} can't be marked delivered — ${why}`, 'error'); return false; }

          const at = Date.now();
          let write = sb.from('orders')
              .update({ status: OrderStatus.DELIVERED, delivered_at: isoFromMs(at), delivered_by: currentUser.id })
              .eq('id', orderId)
              .in('status', [OrderStatus.CONFIRMED, OrderStatus.PICKED_UP])
              .eq('driver_id', fresh.driverId!);
          write = fresh.storeId ? write.eq('store_id', fresh.storeId) : write.is('store_id', null);
          const { data: saved, error: writeErr } = await write.select('id');
          if (writeErr) throw new Error(writeErr.message);
          if (!Array.isArray(saved) || !saved.length) {
              showToast(`Order #${num} changed a moment ago — check it and try again.`, 'error');
              return false;
          }
          orderFieldsSaved?.(orderId, { status: OrderStatus.DELIVERED, deliveredAt: at, deliveredBy: currentUser.id });

          const delivered: Order = { ...fresh, status: OrderStatus.DELIVERED, deliveredAt: at };
          logDeliveredHistory(delivered, getPerformedBy());
          const driver = users.find(u => u.id === fresh.driverId);
          notifyOrderParties(orderId, `Order #${num} for ${fresh.customerName} was marked delivered by ${currentUser.name}.`, currentUser.id);
          showToast(`Order #${num} marked delivered — ${driver?.name || 'the driver'} is told you marked it.`, 'success');
          return true;
      } catch (e: any) {
          showToast(markDeliveredErrorText(e?.message), 'error');
          return false;
      }
  };

  const updateOrder = async (order: Order) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const oldOrder = orders.find(o => o.id === order.id);

          // Write ONLY what this caller changed. Every caller spreads a copy of
          // the order ({ ...order, priority }) and that copy can be stale: the
          // priority pill fires per keystroke, a driver/store screen holds a
          // snapshot from before dispatch unassigned it. A whole-row write from
          // such a copy (a) re-deleted and re-inserted order_items from the
          // stale list — which is how #20260914-035 lost its products — and
          // (b) silently wrote the old store_id / driver_id back. So: diff
          // against the row we hold, never touch the assignment columns, and
          // rewrite order_items only when the items themselves changed.
          const patch = orderChangedFields(oldOrder, order);
          const itemsChanged = 'items' in patch;
          if (itemsChanged && order.items.length === 0) {
              // The form blocks this; the only way here is a stale copy with an
              // empty list — writing it would erase the real products. Checked
              // before the stock reconciliation so nothing moves either.
              throw new Error('An order must keep at least one product.');
          }
          if (Object.keys(patch).length === 0) return;

          // Inventory Management Logic: adjust store/driver stock based on item changes
          // AND log every delta so the audit trail captures edits to reserved stock.
          if (oldOrder && oldOrder.status !== OrderStatus.DELIVERED) {
              const isStoreBacked = !!oldOrder.storeId;
              const isDriverBacked = !!oldOrder.fulfilledFromDriver && !!oldOrder.driverId;

              if (isStoreBacked || isDriverBacked) {
                  const editNum = order.orderNumber || order.id.slice(-6).toUpperCase();
                  const editNow = Date.now();

                  // Collect every productId delta: positive diff = stock returning to available, negative = stock now reserved
                  const deltas: Array<{ productId: string; diff: number; oldQty: number; newQty: number }> = [];

                  oldOrder.items.forEach(oldItem => {
                      const newItem = order.items.find(ni => ni.productId === oldItem.productId);
                      const newQty = newItem?.quantity || 0;
                      const diff = oldItem.quantity - newQty;
                      if (diff !== 0) {
                          deltas.push({ productId: oldItem.productId, diff, oldQty: oldItem.quantity, newQty });
                      }
                  });

                  order.items.forEach(newItem => {
                      const existsInOld = oldOrder.items.some(oi => oi.productId === newItem.productId);
                      if (!existsInOld) {
                          deltas.push({ productId: newItem.productId, diff: -newItem.quantity, oldQty: 0, newQty: newItem.quantity });
                      }
                  });

                  for (const { productId, diff, oldQty, newQty } of deltas) {
                      const product = products.find(p => p.id === productId);
                      const productName = product?.name || 'Unknown Product';

                      if (isStoreBacked) {
                          const storeId = oldOrder.storeId!;
                          await updateStoreStockQuantity(storeId, productId, diff);
                          const store = users.find(u => u.id === storeId);
                          logStoreHistory({
                              storeId,
                              storeName: store?.name || 'Unknown Store',
                              action: StoreHistoryAction.ORDER_EDITED,
                              timestamp: editNow,
                              orderId: order.id,
                              orderNumber: editNum,
                              productId,
                              productName,
                              quantityChange: diff,
                              performedBy: getPerformedBy(),
                              description: diff > 0
                                  ? `Order #${editNum} edited — ${diff}x ${productName} released back to store stock (${oldQty} → ${newQty})`
                                  : `Order #${editNum} edited — ${-diff}x ${productName} additionally reserved from store stock (${oldQty} → ${newQty})`,
                          });
                      } else if (isDriverBacked) {
                          const driverId = oldOrder.driverId!;
                          await updateDriverStockQuantity(driverId, productId, diff);
                          const driver = users.find(u => u.id === driverId);
                          logDriverHistory({
                              driverId,
                              driverName: driver?.name || 'Unknown Driver',
                              action: DriverHistoryAction.ORDER_EDITED,
                              timestamp: editNow,
                              orderId: order.id,
                              orderNumber: editNum,
                              productId,
                              productName,
                              quantityChange: diff,
                              performedBy: getPerformedBy(),
                              description: diff > 0
                                  ? `Order #${editNum} edited — ${diff}x ${productName} released back to car stock (${oldQty} → ${newQty})`
                                  : `Order #${editNum} edited — ${-diff}x ${productName} additionally reserved from car stock (${oldQty} → ${newQty})`,
                          });
                      }
                  }

                  if (deltas.length > 0) {
                      logWarehouseHistory({
                          productId: 'system',
                          productName: 'Order Management',
                          action: WarehouseHistoryAction.TRANSACTION_STATUS_CHANGED,
                          timestamp: editNow,
                          performedBy: getPerformedBy(),
                          quantityChange: 0,
                          quantityBefore: 0,
                          quantityAfter: 0,
                          description: `Order #${editNum} items edited — ${deltas.length} change(s) — ${order.customerName}`,
                      });
                  }
              }
          }

          if (USE_SUPABASE) {
              const photosChanged = ORDER_PHOTO_KEYS.some(k => k in patch);
              const scalar: Record<string, any> = {};
              for (const [k, v] of Object.entries(patch)) {
                  if (k === 'items' || ORDER_PHOTO_KEYS.includes(k)) continue;
                  scalar[k] = v;
              }
              // Delivering lands once (Phase 1, 2026-09-30): only while the order is not delivered yet, so a
              // second submit — or the office's Mark delivered at the same moment — never records it twice.
              const delivering = scalar.status === OrderStatus.DELIVERED && oldOrder?.status !== OrderStatus.DELIVERED;
              await sbUpdateOrderFields(order.id, scalar, delivering
                  ? { not: { status: OrderStatus.DELIVERED }, notSaved: 'This order is delivered already.' }
                  : undefined);
              if (itemsChanged) await sbReplaceOrderItems(order.id, order.items);
              if (photosChanged) await sbReplaceOrderPhotos(order);
          } else {
          await dbInstance.collection('orders').doc(order.id).update(patch);
          }

          // Stock and cash history for the store and the driver
          if (order.status === OrderStatus.DELIVERED && oldOrder?.status !== OrderStatus.DELIVERED) logDeliveredHistory(order);

          // SMS: notify order parties when delivered
          if (order.status === OrderStatus.DELIVERED && oldOrder?.status !== OrderStatus.DELIVERED) {
              const delOrderNum = order.orderNumber || order.id.slice(-6).toUpperCase();
              notifyOrderParties(order.id, `Order #${delOrderNum} for ${order.customerName} has been delivered.`, order.driverId);
          }
      } catch (e) {
          throw e;
      }
  };

  // Field-level order write: exactly the columns in `patch`, nothing else.
  // For single-field edits (the card's priority pill) — no items, no photos,
  // no assignment columns; those keep their own helpers.
  const patchOrder = async (orderId: string, patch: Partial<Order>) => {
      if (USE_SUPABASE) {
          await sbUpdateOrderFields(orderId, patch);
          return;
      }
      const { db: dbInstance } = ensureFirebaseInitialized();
      await dbInstance.collection('orders').doc(orderId).update({ ...patch } as any);
  };

  // Edit an order a store or driver already has (Phase 1, client 2026-10-14: "dispatch needs to update the
  // number, payment and address without waiting for the driver"). Only the order's own details are written
  // (services/orderDetailsEdit.ts) — never the products, the order type, the status or who holds it, so no
  // stock moves. ONE conditional write: it lands only while the order is still open (not delivered,
  // cancelled or on its way back); a delivery a moment earlier wins and nothing is changed. The driver's
  // text and the customer-phone routing follow only that write. Answers the changed field names.
  const updateOrderDetails = async (order: Order): Promise<string[]> => {
      const oldOrder = orders.find(o => o.id === order.id);
      if (!oldOrder) throw new OrderNotSavedError(ORDER_NOT_SAVED);
      const patch = detailsOnly(oldOrder, orderChangedFields(oldOrder, order));
      const changed = Object.keys(patch);
      if (changed.length === 0) return [];
      if (USE_SUPABASE) {
          await sbUpdateOrderFields(order.id, patch, {
              notIn: { status: DETAILS_LOCKED_STATUSES },
              notSaved: 'This order was delivered, cancelled or sent back to the store a moment ago — nothing was changed.',
          });
      } else {
          const { db: dbInstance } = ensureFirebaseInitialized();
          await dbInstance.collection('orders').doc(order.id).update(patch as any);
      }
      const next = { ...oldOrder, ...patch } as Order;
      // A new customer number: the customer's texts now find this order (the driver's thread).
      if ('customerPhone' in patch && next.driverId && next.customerPhone) {
          await updatePhoneToOrderMapping(next.id, next.driverId, next.customerPhone);
      }
      const text = next.driverId ? driverEditText(next, patch) : null;
      const driver = next.driverId ? users.find(u => u.id === next.driverId) : undefined;
      if (text && driver?.phone) sendSMS(driver.phone, text).catch(() => {});
      return changed;
  };

  // ── Stop numbers (services/stopNumbers, 2026-09-25) ──────────────────
  // A driver's stops are read FRESH from the database at the moment of the
  // change — one select on orders_driver_status_idx — rather than from this
  // tab's copy, so a second dispatcher's assignment a moment ago still
  // counts: no duplicate stop, no gap left behind. Firestore mode, and a
  // read that fails, fall back to what this tab holds.
  const driverOpenOrders = async (driverId: string): Promise<StopOrder[]> => {
      if (USE_SUPABASE) {
          try {
              const { data, error } = await supabase()
                  .from('orders')
                  .select('id, driver_id, status, priority, created_at')
                  .eq('driver_id', driverId)
                  .in('status', STOP_STATUSES as string[]);
              if (!error && Array.isArray(data)) {
                  return data.map((r: any) => ({
                      id: r.id, driverId: r.driver_id, status: r.status,
                      priority: r.priority ?? undefined, createdAt: Date.parse(r.created_at) || 0,
                  }));
              }
          } catch { /* fall back to this tab's copy */ }
      }
      return orders.filter(o => o.driverId === driverId);
  };
  /** A tag for an assignment: 'next' = the driver's next stop ("4-K9"), any
   *  other string = as typed, nothing = the tag is left alone. */
  const stopTagFor = async (driverId: string, want: string | undefined): Promise<string | undefined> =>
      want === 'next'
          ? nextStopTag(await driverOpenOrders(driverId), driverId, users.find(u => u.id === driverId)?.name)
          : want || undefined;
  // When an order leaves a driver, the driver's remaining stops close the gap
  // — 1, 2, 3 with nothing missing. One priority-only patch per tag that
  // actually changes; a failed rewrite never fails the move behind it.
  const closeDriverStopGap = async (driverId: string, leavingOrderId: string) => {
      const driver = users.find(u => u.id === driverId);
      for (const p of closeGapPlan(await driverOpenOrders(driverId), driverId, leavingOrderId, driver?.name)) {
          try { await patchOrder(p.orderId, { priority: p.priority }); } catch { /* the move stands; the tag is retyped by hand */ }
      }
  };

  // Update phoneToOrder mapping so incoming calls/SMS route to the right order+driver
  const updatePhoneToOrderMapping = async (orderId: string, driverId: string, customerPhone?: string) => {
      if (!customerPhone) return;
      try {
          const normalized = normalizePhone(customerPhone);
          if (!normalized) return;
          const canonical = toE164(customerPhone) || customerPhone;
          if (USE_SUPABASE) {
              // Not redundant with the index on orders.customer_phone:
              // _inbound-sms-router reads this row, and its last_activity is
              // the "last touched wins" tie-break that decides whether an
              // inbound text joins the order thread or the board-card thread.
              // Nothing else populates it (0011 gives the browser insert +
              // update policies and no select, hence the blind upsert).
              const { error } = await supabase().from('phone_to_order').upsert({
                  phone: normalized,
                  order_id: orderId,
                  driver_id: driverId || null,
                  customer_phone: canonical,
                  last_activity: Date.now(),
              }, { onConflict: 'phone' });
              if (error) throw new Error(`phone_to_order upsert: ${error.message}`);
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          await dbInstance.collection('phoneToOrder').doc(normalized).set({
              orderId,
              driverId,
              customerPhone: canonical,
              lastActivity: Date.now(),
          });
      } catch (e) {
          console.error('Failed to update phoneToOrder mapping:', e);
      }
  };

  const assignOrder = async (orderId: string, storeId: string) => {
      let stockFailed = false;
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (order && order.items.length === 0) { showToast(NO_PRODUCTS_TO_ASSIGN, 'error'); return; }
          // Claim first (Phase 1, 2026-09-30): the order write lands only while the order has no store, so a
          // second click or a colleague who got there first moves no stock. Stock, history, the toast and the
          // store's text follow only once the order is the store's. (Assigning a store CLAIMS the order for
          // this dispatch too — pool orders leave the shared queue on the first forward-moving action.)
          // Still Pending too (audit): a cancelled order, or one a colleague just filled from a driver's car,
          // also has no store — neither may be assigned from a stale screen.
          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, { storeId, status: OrderStatus.ASSIGNED, dispatchId: currentUser?.id || '' },
                  { eq: { store_id: null, status: OrderStatus.PENDING }, notSaved: 'This order already has a store, or is no longer Pending — nothing was changed. Refresh to see it.' });
          } else {
          await dbInstance.collection('orders').doc(orderId).update({ storeId, status: OrderStatus.ASSIGNED, dispatchId: currentUser?.id || '' });
          }
          if (order) {
              try {
                  for (const item of order.items) {
                      await updateStoreStockQuantity(storeId, item.productId, -item.quantity);
                  }
              } catch (stockErr) {
                  stockFailed = true;
                  showToast("The order is assigned, but the store's stock could not be updated — check the store's stock.", "error");
                  throw stockErr;
              }
              const assignedStore = users.find(u => u.id === storeId);
              const assignOrderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
              const assignItemSummary = order.items.map(i => {
                  const p = products.find(pr => pr.id === i.productId);
                  return `${i.quantity}x ${p?.name || 'Unknown'}`;
              }).join(', ');
              logStoreHistory({
                  storeId,
                  storeName: assignedStore?.name || 'Unknown Store',
                  action: StoreHistoryAction.ORDER_ASSIGNED,
                  timestamp: Date.now(),
                  orderId,
                  orderNumber: assignOrderNum,
                  performedBy: getPerformedBy(),
                  description: `Order #${assignOrderNum} assigned to ${assignedStore?.name || 'store'} — ${order.customerName} — ${assignItemSummary}`,
              });
              showToast("Order assigned to store. Stock deducted.", "success");
              // SMS: notify store (the only text for this — the screens no longer send their own)
              if (assignedStore?.phone) {
                  sendSMS(assignedStore.phone, `Order #${assignOrderNum} has been assigned to your store. Check your app.`).catch(() => {});
              }
          }
      } catch (e) {
          if (e instanceof OrderNotSavedError) showToast(e.message, "error");
          else if (!stockFailed) showToast("Failed to assign order", "error");
          throw e;
      }
  };

  // Fulfill order directly from driver's car stock (no store involved)
  const assignOrderToDriver = async (orderId: string, driverId: string, opts?: { stopTag?: string }) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (!order) return;
          if (order.items.length === 0) { showToast(NO_PRODUCTS_TO_ASSIGN, 'error'); return; }

          const driver = users.find(u => u.id === driverId);
          const driverName = driver?.name || 'Unknown Driver';
          const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
          const now = Date.now();

          // Validate driver has enough stock for all items
          for (const item of order.items) {
              const available = getDriverStock(driverId, item.productId);
              if (available < item.quantity) {
                  const product = products.find(p => p.id === item.productId);
                  showToast(`${driverName} doesn't have enough ${product?.name || 'product'} (has ${available}, needs ${item.quantity})`, 'error');
                  return;
              }
          }

          // Deduct from driver stock and log
          for (const item of order.items) {
              const product = products.find(p => p.id === item.productId);
              await updateDriverStockQuantity(driverId, item.productId, -item.quantity);

              logDriverHistory({
                  driverId,
                  driverName,
                  action: DriverHistoryAction.ORDER_ASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  productId: item.productId,
                  productName: product?.name || 'Unknown Product',
                  quantityChange: -item.quantity,
                  performedBy: getPerformedBy(),
                  description: `Fulfilling Order #${orderNum} from car stock — ${item.quantity}x ${product?.name || 'Unknown'}`,
              });

              // Movement ledger
              createMovement({
                  date: getBusinessDate(),
                  productId: item.productId,
                  productName: product?.name || 'Unknown Product',
                  type: MovementType.DRIVER_DIRECT,
                  fromEntity: driverName,
                  toEntity: order.customerName,
                  storeId: null,
                  driverId,
                  toDriverId: null,
                  orderId: order.id,
                  orderNumber: orderNum,
                  qty: item.quantity,
                  reason: null,
                  timestamp: now,
              });
          }

          // Update order: assign driver, skip store, mark as ready — and, when
          // asked, number the stop in the same write.
          const tag = await stopTagFor(driverId, opts?.stopTag);
          const stop = stopPhrase(tag);
          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, {
                  driverId,
                  storeId: null,
                  fulfilledFromDriver: true,
                  storeConfirmedPickup: true,
                  status: OrderStatus.CONFIRMED,
                  dispatchId: currentUser?.id || '',
                  ...(tag ? { priority: tag } : {}),
              });
          } else {
          await dbInstance.collection('orders').doc(orderId).update({
              driverId,
              storeId: null,
              fulfilledFromDriver: true,
              storeConfirmedPickup: true,
              status: OrderStatus.CONFIRMED,
              dispatchId: currentUser?.id || '',
              ...(tag ? { priority: tag } : {}),
          });
          }

          // Update phoneToOrder mapping for incoming calls
          await updatePhoneToOrderMapping(orderId, driverId, order.customerPhone);

          logWarehouseHistory({
              productId: 'system',
              productName: 'Order Management',
              action: WarehouseHistoryAction.TRANSACTION_CREATED,
              timestamp: now,
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Order #${orderNum} fulfilled from ${driverName}'s car stock`,
          });

          showToast(`Order assigned to ${driverName} from car stock. Ready to deliver.`, 'success');

          // SMS notify driver
          if (driver?.phone) {
              sendSMS(driver.phone, `WolfDrivers: Order #${orderNum}${stop ? ` (${stop})` : ''} assigned from your car stock. Ready to deliver. Check your app.`).catch(() => {});
          }
          return tag;
      } catch (e) {
          showToast('Failed to assign order to driver stock', 'error');
          throw e;
      }
  };

  const assignDriver = async (orderId: string, driverId: string, opts?: { stopTag?: string }) => {
      let carStockFailed = false;
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (order && order.items.length === 0) { showToast(NO_PRODUCTS_TO_ASSIGN, 'error'); return; }
          const oldDriverId = order?.driverId;
          const newDriver = users.find(u => u.id === driverId);
          const orderNum = order?.orderNumber || orderId.slice(-6).toUpperCase();
          const now = Date.now();

          // ─── Direct-from-car reassign — atomic stock rebalance ──────────
          // Web previously rewrote `driverId` without touching driver
          // stocks on a direct-from-car (`fulfilledFromDriver === true`)
          // reassignment. The old driver's car kept showing the order's
          // items as "reserved" and the new driver's car was never
          // debited — phantom stock accumulating over time on every
          // direct-fulfill swap.
          //
          // Mobile already does this rebalance in
          // `services/adminActions.ts → assignDriverToOrder`. This block
          // ports the same logic to web so both clients keep the driver-
          // stock ledger honest.
          //
          // Pre-flight validates the new driver has enough of every item
          // first; if not, throw before the order doc is touched so we
          // never leave a half-applied state. Store-fulfilled reassigns
          // (stock at store, not on driver) skip this — no stock to move.
          const isReassign = !!oldDriverId && oldDriverId !== driverId;
          const isDirectReassign = isReassign && order?.fulfilledFromDriver === true;
          if (isDirectReassign && order && oldDriverId) {
              for (const item of order.items) {
                  const available = getDriverStock(driverId, item.productId);
                  if (available < item.quantity) {
                      const product = products.find(p => p.id === item.productId);
                      const productName = product?.name || 'product';
                      showToast(
                          `${newDriver?.name || 'Driver'} only has ${available} of ${productName} (needs ${item.quantity}).`,
                          'error'
                      );
                      throw new Error(`Insufficient stock on new driver for ${productName}`);
                  }
              }
          }
          // The car-stock swap runs once the order is claimed for the new driver (below), never before it:
          // a colleague's change in between must not move stock (Phase 1 staging walk, 2026-09-30).
          const rebalanceCars = async () => {
              if (!(isDirectReassign && order && oldDriverId)) return;
              const oldDriver = users.find(u => u.id === oldDriverId);
              for (const item of order.items) {
                  const product = products.find(p => p.id === item.productId);
                  const productName = product?.name || 'Unknown Product';
                  // Refund old driver
                  await updateDriverStockQuantity(oldDriverId, item.productId, item.quantity);
                  logDriverHistory({
                      driverId: oldDriverId,
                      driverName: oldDriver?.name || 'Unknown Driver',
                      action: DriverHistoryAction.ORDER_UNASSIGNED,
                      timestamp: now,
                      productId: item.productId,
                      productName,
                      quantityChange: item.quantity,
                      orderId,
                      orderNumber: orderNum,
                      performedBy: getPerformedBy(),
                      description: `Order #${orderNum} reassigned to ${newDriver?.name || 'driver'} — ${item.quantity}x ${productName} returned to your car stock`,
                  });
                  // Debit new driver
                  await updateDriverStockQuantity(driverId, item.productId, -item.quantity);
                  logDriverHistory({
                      driverId,
                      driverName: newDriver?.name || 'Unknown Driver',
                      action: DriverHistoryAction.ORDER_ASSIGNED,
                      timestamp: now,
                      productId: item.productId,
                      productName,
                      quantityChange: -item.quantity,
                      orderId,
                      orderNumber: orderNum,
                      performedBy: getPerformedBy(),
                      description: `Order #${orderNum} reassigned to you — ${item.quantity}x ${productName} reserved from your car stock`,
                  });
              }
          };

          // The stop number, when asked for, goes in the same write.
          const tag = await stopTagFor(driverId, opts?.stopTag);
          const stop = stopPhrase(tag);
          if (USE_SUPABASE) {
              // Claim: only while the order still has the driver this screen saw (none, or the old one). Two
              // dispatchers picking different drivers at the same moment both "won" before (Phase 1 walk).
              await sbUpdateOrderFields(orderId, { driverId, dispatchId: currentUser?.id || '', ...(tag ? { priority: tag } : {}) },
                  { eq: { driver_id: oldDriverId ?? null }, notSaved: "This order's driver changed a moment ago — nothing was changed. Refresh to see it." });
          } else {
          await dbInstance.collection('orders').doc(orderId).update({ driverId, dispatchId: currentUser?.id || '', ...(tag ? { priority: tag } : {}) });
          }
          try {
              await rebalanceCars();
          } catch (stockErr) {
              carStockFailed = true;
              showToast("The driver is changed, but the car stock could not be moved — check both cars' stock.", "error");
              throw stockErr;
          }

          // Update phoneToOrder mapping for incoming calls
          await updatePhoneToOrderMapping(orderId, driverId, order?.customerPhone);

          // If there was a previous driver, this is a driver change
          if (oldDriverId && oldDriverId !== driverId) {
              const oldDriver = users.find(u => u.id === oldDriverId);
              await closeDriverStopGap(oldDriverId, orderId); // the old driver's stops close the gap
              logDriverHistory({
                  driverId: oldDriverId,
                  driverName: oldDriver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.DRIVER_CHANGED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `Driver changed on Order #${orderNum} — removed ${oldDriver?.name || 'driver'}, assigned to ${newDriver?.name || 'driver'}`,
              });
          }

          // Log for new driver
          logDriverHistory({
              driverId,
              driverName: newDriver?.name || 'Unknown Driver',
              action: oldDriverId && oldDriverId !== driverId ? DriverHistoryAction.DRIVER_CHANGED : DriverHistoryAction.DRIVER_ASSIGNED,
              timestamp: now,
              orderId,
              orderNumber: orderNum,
              performedBy: getPerformedBy(),
              description: oldDriverId && oldDriverId !== driverId
                  ? `Driver changed on Order #${orderNum} — now assigned to ${newDriver?.name || 'driver'} (was: ${users.find(u => u.id === oldDriverId)?.name || 'unknown'})`
                  : `Assigned to Order #${orderNum} — ${order?.customerName || 'Unknown'}`,
          });

          // Log to store history too
          if (order?.storeId) {
              const store = users.find(u => u.id === order.storeId);
              logStoreHistory({
                  storeId: order.storeId,
                  storeName: store?.name || 'Unknown Store',
                  action: oldDriverId && oldDriverId !== driverId ? StoreHistoryAction.DRIVER_CHANGED : StoreHistoryAction.DRIVER_ASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: oldDriverId && oldDriverId !== driverId
                      ? `Driver changed on Order #${orderNum} — ${users.find(u => u.id === oldDriverId)?.name || 'driver'} → ${newDriver?.name || 'driver'}`
                      : `Driver ${newDriver?.name || 'Unknown'} assigned to Order #${orderNum}`,
              });
          }

          showToast("Driver assigned to order.", "success");
          // SMS: notify new driver + store
          if (newDriver?.phone) {
              sendSMS(newDriver.phone, `Order #${orderNum} has been assigned to you${stop ? `, ${stop}` : ''}. Check your app.`).catch(() => {});
          }
          if (order?.storeId) {
              const store = users.find(u => u.id === order.storeId);
              if (store?.phone) {
                  sendSMS(store.phone, `Order #${orderNum}: Driver ${newDriver?.name || 'driver'} assigned. Check your app.`).catch(() => {});
              }
          }
          // SMS: notify old driver if reassigned
          if (oldDriverId && oldDriverId !== driverId) {
              const oldDriver = users.find(u => u.id === oldDriverId);
              if (oldDriver?.phone) {
                  sendSMS(oldDriver.phone, `Order #${orderNum} has been reassigned to another driver. Check your app.`).catch(() => {});
              }
          }
          return tag;
      } catch (e) {
          if (e instanceof OrderNotSavedError) showToast(e.message, "error");
          else if (!carStockFailed) showToast("Failed to assign driver", "error");
          throw e;
      }
  };

  // Deliberately does NOT clear dispatchId: unassigning a driver is a
  // mid-correction (wrong driver, re-plan) by the owning dispatch, not a
  // release back to the shared pool — silent pool-returns would invite two
  // dispatches double-handling the same customer.
  const unassignOrder = async (orderId: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          const oldDriver = order?.driverId ? users.find(u => u.id === order.driverId) : null;
          const orderNum = order?.orderNumber || orderId.slice(-6).toUpperCase();
          const now = Date.now();
          const wasFulfilledFromDriver = !!(order?.fulfilledFromDriver && order.driverId);

          // If the order was fulfilled from driver's car stock, return stock to driver
          if (wasFulfilledFromDriver && order?.driverId) {
              for (const item of order.items) {
                  const product = products.find(p => p.id === item.productId);
                  await updateDriverStockQuantity(order.driverId, item.productId, item.quantity);
                  logDriverHistory({
                      driverId: order.driverId,
                      driverName: oldDriver?.name || 'Unknown Driver',
                      action: DriverHistoryAction.ORDER_UNASSIGNED,
                      timestamp: now,
                      orderId,
                      orderNumber: orderNum,
                      productId: item.productId,
                      productName: product?.name || 'Unknown Product',
                      quantityChange: item.quantity,
                      performedBy: getPerformedBy(),
                      description: `Order #${orderNum} unassigned — ${item.quantity}x ${product?.name || 'Unknown'} returned to car stock`,
                  });
              }
          }

          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, {
                  driverId: null,
                  storeConfirmedPickup: false,
                  fulfilledFromDriver: false,
                  priority: null, // the stop number belonged to that driver (2026-09-25)
                  status: order?.storeId ? OrderStatus.ASSIGNED : OrderStatus.PENDING,
              });
          } else {
          await dbInstance.collection('orders').doc(orderId).update({
              driverId: null,
              storeConfirmedPickup: false,
              fulfilledFromDriver: false,
              priority: null,
              status: order?.storeId ? OrderStatus.ASSIGNED : OrderStatus.PENDING,
          } as any);
          }
          if (order?.driverId) await closeDriverStopGap(order.driverId, orderId);

          // Log driver history
          if (order?.driverId) {
              logDriverHistory({
                  driverId: order.driverId,
                  driverName: oldDriver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.DRIVER_UNASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: wasFulfilledFromDriver
                      ? `${oldDriver?.name || 'Driver'} unassigned from Order #${orderNum} — stock returned to car inventory, order back to pending`
                      : `${oldDriver?.name || 'Driver'} unassigned from Order #${orderNum}`,
              });
          }

          // Log store history
          if (order?.storeId) {
              const store = users.find(u => u.id === order.storeId);
              logStoreHistory({
                  storeId: order.storeId,
                  storeName: store?.name || 'Unknown Store',
                  action: StoreHistoryAction.DRIVER_UNASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `${oldDriver?.name || 'Driver'} unassigned from Order #${orderNum} — store assignment kept`,
              });
          }

          if (wasFulfilledFromDriver) {
              showToast("Driver unassigned. Stock returned to car inventory.", "info");
          } else {
              showToast("Driver unassigned. Store assignment kept.", "info");
          }
      } catch (e) {
          throw e;
      }
  };

  const unassignStore = async (orderId: string) => {
      let stockFailed = false;
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          // Safety net for every caller (2026-09-28): while the driver has the goods — or is bringing them
          // back — the store stays; its stock is not put back until the store confirms the return.
          if (order && (order.status === OrderStatus.RETURNING_TO_STORE
              || (order.storeConfirmedPickup && order.driverId && !order.fulfilledFromDriver))) {
              showToast('The driver has this order — they return it to the store first. Remove the store once the store confirms the return.', 'error');
              return;
          }
          const orderNum = order?.orderNumber || orderId.slice(-6).toUpperCase();
          const now = Date.now();
          // Claim first (Phase 1, 2026-09-30): the order leaves this store once — only while it is still this
          // store's — and only then does the stock go back. A second confirm used to return the stock twice.
          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, {
                  storeId: null,
                  driverId: null,
                  storeConfirmedPickup: false,
                  priority: null,
                  status: OrderStatus.PENDING,
              }, order?.storeId ? {
                  // The tab's copy may be old (audit): the driver-has-the-goods rule rides in the write too.
                  eq: { store_id: order.storeId },
                  not: { status: OrderStatus.RETURNING_TO_STORE },
                  notTrue: ['store_confirmed_pickup'],
                  notSaved: 'This order is no longer with that store, or its driver has it now — nothing was changed. Refresh to see it.',
              } : undefined);
          } else {
          await dbInstance.collection('orders').doc(orderId).update({
              storeId: null,
              driverId: null,
              storeConfirmedPickup: false,
              priority: null,
              status: OrderStatus.PENDING,
          } as any);
          }
          // Return stock to store
          if (order && order.storeId) {
              try {
                  for (const item of order.items) {
                      await updateStoreStockQuantity(order.storeId, item.productId, item.quantity);
                  }
              } catch (stockErr) {
                  stockFailed = true;
                  showToast("The store is removed, but its stock could not be put back — check the store's stock.", "error");
                  throw stockErr;
              }
          }
          if (order?.driverId) await closeDriverStopGap(order.driverId, orderId);

          // Log store history
          if (order?.storeId) {
              const store = users.find(u => u.id === order.storeId);
              logStoreHistory({
                  storeId: order.storeId,
                  storeName: store?.name || 'Unknown Store',
                  action: StoreHistoryAction.ORDER_UNASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `Order #${orderNum} unassigned from ${store?.name || 'store'} — stock returned, order back to pending`,
              });
          }

          // Log driver history if driver was assigned
          if (order?.driverId) {
              const driver = users.find(u => u.id === order.driverId);
              logDriverHistory({
                  driverId: order.driverId,
                  driverName: driver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.DRIVER_UNASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `${driver?.name || 'Driver'} unassigned from Order #${orderNum} — store also unassigned`,
              });
          }

          showToast("Store & driver unassigned. Stock returned.", "info");
      } catch (e) {
          if (e instanceof OrderNotSavedError) showToast(e.message, "error");
          else if (!stockFailed) showToast("Failed to unassign store", "error");
          throw e;
      }
  };

  const initiateDriverReturn = async (orderId: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (!order) return;

          // If driver hasn't picked up yet, just do normal unassign
          if (!order.storeConfirmedPickup) {
              await unassignOrder(orderId);
              return;
          }

          const driver = users.find(u => u.id === order.driverId);
          const store = users.find(u => u.id === order.storeId);
          const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
          const now = Date.now();

          // Log driver history: return initiated
          if (order.driverId) {
              logDriverHistory({
                  driverId: order.driverId,
                  driverName: driver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.ORDER_UNASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `Return initiated — Order #${orderNum} must be returned to ${store?.name || 'store'}`,
              });
          }

          // Log store history: expecting return
          if (order.storeId) {
              logStoreHistory({
                  storeId: order.storeId,
                  storeName: store?.name || 'Unknown Store',
                  action: StoreHistoryAction.STOCK_RETURNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `Return initiated — ${driver?.name || 'driver'} returning Order #${orderNum} to store`,
              });
          }

          // If driver has picked up, set status to RETURNING_TO_STORE
          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, { status: OrderStatus.RETURNING_TO_STORE });
          } else {
          await dbInstance.collection('orders').doc(orderId).update({
              status: OrderStatus.RETURNING_TO_STORE,
          } as any);
          }
          showToast("Driver has been notified to return the order.", "info");
          // SMS: notify driver and store about return
          if (driver?.phone) {
              sendSMS(driver.phone, `Order #${orderNum}: Return initiated. Please return to ${store?.name || 'store'}. Check your app.`).catch(() => {});
          }
          if (store?.phone) {
              sendSMS(store.phone, `Order #${orderNum}: ${driver?.name || 'Driver'} is returning this order to your store. Check your app.`).catch(() => {});
          }
      } catch (e) {
          showToast("Failed to initiate driver return", "error");
          throw e;
      }
  };

  // Driver confirms they arrived at store and returned stock — completes the return immediately
  const driverConfirmReturn = async (orderId: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (!order || order.status !== OrderStatus.RETURNING_TO_STORE) return;

          const driver = users.find(u => u.id === order.driverId);
          const store = users.find(u => u.id === order.storeId);
          const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
          const now = Date.now();

          // Log return in store and driver history for each item
          for (const item of order.items) {
              const product = products.find(p => p.id === item.productId);
              const productName = product?.name || 'Unknown Product';

              if (order.storeId) {
                  logStoreHistory({
                      storeId: order.storeId,
                      storeName: store?.name || 'Unknown Store',
                      action: StoreHistoryAction.STOCK_RETURNED,
                      timestamp: now,
                      orderId,
                      orderNumber: orderNum,
                      productId: item.productId,
                      productName,
                      quantityChange: item.quantity,
                      performedBy: getPerformedBy(),
                      description: `Order #${orderNum} returned by ${driver?.name || 'driver'} — ${item.quantity}x ${productName} back to store`,
                  });
              }

              if (order.driverId) {
                  logDriverHistory({
                      driverId: order.driverId,
                      driverName: driver?.name || 'Unknown Driver',
                      action: DriverHistoryAction.STOCK_RETURNED,
                      timestamp: now,
                      orderId,
                      orderNumber: orderNum,
                      productId: item.productId,
                      productName,
                      quantityChange: -item.quantity,
                      performedBy: getPerformedBy(),
                      description: `Returned ${item.quantity}x ${productName} to ${store?.name || 'store'} — Order #${orderNum}`,
                  });
                  // Inventory Tracker: movement ledger (returned to store)
                  createMovement({
                      date: getBusinessDate(),
                      productId: item.productId,
                      productName,
                      type: MovementType.RETURNED_TO_STORE,
                      fromEntity: driver?.name || 'Unknown Driver',
                      toEntity: store?.name || 'Unknown Store',
                      storeId: order.storeId || null,
                      driverId: order.driverId,
                      toDriverId: null,
                      orderId: order.id,
                      orderNumber: orderNum,
                      qty: item.quantity,
                      reason: null,
                      timestamp: now,
                  });
              }
          }

          // Complete the return: unassign driver, reset flags, set status back to ASSIGNED.
          // In Postgres mode this cannot be a direct client write: the orders
          // UPDATE policy re-checks its USING expression against the NEW row,
          // and once driver_id is null the driver no longer satisfies it, so
          // the write fails with 42501 unless the driver is also the store's
          // temp packer. The server does it with the service role after
          // confirming the caller really is this order's driver (or dispatch).
          if (USE_SUPABASE) {
              const { data: { session } } = await supabase().auth.getSession();
              const token = session?.access_token;
              const res = await fetch('/.netlify/functions/driver-confirm-return', {
                  method: 'POST',
                  headers: {
                      'Content-Type': 'application/json',
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  body: JSON.stringify({ orderId }),
              });
              if (!res.ok) {
                  let msg = `Return failed (HTTP ${res.status})`;
                  try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* keep default */ }
                  throw new Error(msg);
              }
          } else {
          await dbInstance.collection('orders').doc(orderId).update({
              driverId: null,
              storeConfirmedPickup: false,
              driverReturnConfirmed: false,
              status: OrderStatus.ASSIGNED,
          } as any);
          }
          showToast("Stock returned successfully. Order is ready for reassignment.", "success");
      } catch (e) {
          showToast("Failed to confirm return", "error");
          throw e;
      }
  };

  // The office takes an order back from its driver at once (Umer, 2026-10-02: drivers left orders
  // waiting for hours before tapping "Confirm return"). For an order whose goods the store handed to the
  // driver — out for delivery, or already Returning to Store — Dispatch, Admin or Super Admin confirm the
  // goods are back: the order returns to the store's queue (Assigned → Packing) without the driver, with
  // the same store / driver history and Inventory Tracker lines as the driver's own confirmation, signed by
  // whoever pressed it. Claim first: the save lands only while that driver still holds the order and it is
  // not delivered, so a second click, the driver's own confirmation or a delivery a moment earlier wins
  // once and nothing is logged twice. Returns true when the order was taken back.
  const officeTakeBack = async (orderId: string): Promise<boolean> => {
      const role = currentUser?.role;
      if (role !== UserRole.DISPATCH && role !== UserRole.ADMIN && !isSuperTier(role)) {
          showToast("Only Dispatch, Admin or Super Admin can take an order back.", "error");
          return false;
      }
      const order = orders.find(o => o.id === orderId);
      if (!order || !order.driverId || !order.storeId || !order.storeConfirmedPickup || order.fulfilledFromDriver
          || order.status === OrderStatus.DELIVERED || order.transferPending) {
          showToast("Nothing to take back — this order's driver does not hold its goods.", "info");
          return false;
      }
      const driverId = order.driverId;
      const driver = users.find(u => u.id === driverId);
      const store = users.find(u => u.id === order.storeId);
      const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
      const by = currentUser?.name || 'the office';
      const byRole = role === UserRole.DISPATCH ? 'Dispatch' : role === UserRole.ADMIN ? 'Admin' : 'Super Admin';
      // The driver was already bringing it back: this is the return confirmed for them.
      const wasReturning = order.status === OrderStatus.RETURNING_TO_STORE;
      const how = wasReturning ? `return confirmed by ${by} (${byRole})` : `taken back by ${by} (${byRole})`;
      try {
          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, {
                  driverId: null,
                  storeConfirmedPickup: false,
                  driverReturnConfirmed: false,
                  priority: null, // the stop number belonged to that driver (2026-09-25)
                  status: OrderStatus.ASSIGNED,
              }, {
                  eq: { driver_id: driverId, store_confirmed_pickup: true },
                  not: { status: OrderStatus.DELIVERED },
                  notTrue: ['transfer_pending'],
                  notSaved: "This order changed a moment ago (delivered, returned or moved) — nothing was changed. Refresh to see it.",
              });
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              await dbInstance.collection('orders').doc(orderId).update({
                  driverId: null, storeConfirmedPickup: false, driverReturnConfirmed: false, priority: null,
                  status: OrderStatus.ASSIGNED,
              } as any);
          }
      } catch (e) {
          showToast(e instanceof OrderNotSavedError ? e.message : "Could not take the order back. Try again.", "error");
          return false;
      }

      // Saved — the records follow, as for the driver's own confirmation.
      const now = Date.now();
      await closeDriverStopGap(driverId, orderId);
      logDriverHistory({
          driverId,
          driverName: driver?.name || 'Unknown Driver',
          action: DriverHistoryAction.ORDER_UNASSIGNED,
          timestamp: now,
          orderId,
          orderNumber: orderNum,
          performedBy: getPerformedBy(),
          description: `Order #${orderNum} ${how} — back at ${store?.name || 'the store'}, off the route`,
      });
      for (const item of order.items) {
          const productName = products.find(p => p.id === item.productId)?.name || 'Unknown Product';
          logStoreHistory({
              storeId: order.storeId,
              storeName: store?.name || 'Unknown Store',
              action: StoreHistoryAction.STOCK_RETURNED,
              timestamp: now,
              orderId,
              orderNumber: orderNum,
              productId: item.productId,
              productName,
              quantityChange: item.quantity,
              performedBy: getPerformedBy(),
              description: `Order #${orderNum} returned by ${driver?.name || 'the driver'}, ${how} — ${item.quantity}x ${productName} back to store`,
          });
          logDriverHistory({
              driverId,
              driverName: driver?.name || 'Unknown Driver',
              action: DriverHistoryAction.STOCK_RETURNED,
              timestamp: now,
              orderId,
              orderNumber: orderNum,
              productId: item.productId,
              productName,
              quantityChange: -item.quantity,
              performedBy: getPerformedBy(),
              description: `Returned ${item.quantity}x ${productName} to ${store?.name || 'store'} — Order #${orderNum} (${how})`,
          });
          createMovement({
              date: getBusinessDate(),
              productId: item.productId,
              productName,
              type: MovementType.RETURNED_TO_STORE,
              fromEntity: driver?.name || 'Unknown Driver',
              toEntity: store?.name || 'Unknown Store',
              storeId: order.storeId,
              driverId,
              toDriverId: null,
              orderId: order.id,
              orderNumber: orderNum,
              qty: item.quantity,
              reason: how,
              timestamp: now,
          });
      }
      showToast(wasReturning
          ? `Return confirmed — order #${orderNum} is back in ${store?.name || 'the store'}'s queue.`
          : `Order #${orderNum} is back at ${store?.name || 'the store'} — ${driver?.name || 'the driver'} no longer has it.`, "success");
      if (driver?.phone) {
          sendSMS(driver.phone, wasReturning
              ? `WolfDrivers: The return of Order #${orderNum} was confirmed by ${by} (${byRole}). It is off your route.`
              : `WolfDrivers: Order #${orderNum} was taken back by ${by} (${byRole}). No need to return it — it is off your route.`).catch(() => {});
      }
      if (store?.phone) {
          sendSMS(store.phone, `WolfDrivers: Order #${orderNum} is back in your queue — ${by} (${byRole}) confirmed ${driver?.name || 'the driver'} returned it.`).catch(() => {});
      }
      return true;
  };

  // Initiate driver-to-driver transfer (dispatch action)
  const initiateDriverTransfer = async (orderId: string, toDriverId: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (!order || !order.driverId) return;

          const fromDriver = users.find(u => u.id === order.driverId);
          const toDriver = users.find(u => u.id === toDriverId);
          const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
          const now = Date.now();

          // Log history for from-driver: transfer initiated
          logDriverHistory({
              driverId: order.driverId,
              driverName: fromDriver?.name || 'Unknown Driver',
              action: DriverHistoryAction.ORDER_UNASSIGNED,
              timestamp: now,
              orderId,
              orderNumber: orderNum,
              performedBy: getPerformedBy(),
              description: `Transfer initiated — Order #${orderNum} being transferred to ${toDriver?.name || 'driver'} (pending approval)`,
          });

          // Log history for to-driver: transfer initiated
          logDriverHistory({
              driverId: toDriverId,
              driverName: toDriver?.name || 'Unknown Driver',
              action: DriverHistoryAction.ORDER_ASSIGNED,
              timestamp: now,
              orderId,
              orderNumber: orderNum,
              performedBy: getPerformedBy(),
              description: `Transfer initiated — Order #${orderNum} incoming from ${fromDriver?.name || 'driver'} (pending approval)`,
          });

          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, {
                  transferPending: true,
                  transferFromDriverId: order.driverId,
                  transferToDriverId: toDriverId,
                  transferFromApproved: false,
                  transferToApproved: false,
              });
          } else {
          await dbInstance.collection('orders').doc(orderId).update({
              transferPending: true,
              transferFromDriverId: order.driverId,
              transferToDriverId: toDriverId,
              transferFromApproved: false,
              transferToApproved: false,
          } as any);
          }
          showToast(`Transfer initiated: ${fromDriver?.name} → ${toDriver?.name}`, "info");
          // SMS: notify both drivers about the transfer
          if (fromDriver?.phone) {
              sendSMS(fromDriver.phone, `Order #${orderNum}: Transfer initiated. Handing off to ${toDriver?.name || 'another driver'}. Check your app.`).catch(() => {});
          }
          if (toDriver?.phone) {
              sendSMS(toDriver.phone, `Order #${orderNum}: Transfer incoming from ${fromDriver?.name || 'another driver'}. Check your app to approve.`).catch(() => {});
          }
      } catch (e) {
          showToast("Failed to initiate transfer", "error");
          throw e;
      }
  };

  // Driver approves their side of the transfer
  const approveDriverTransfer = async (orderId: string, driverId: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (!order || !order.transferPending) return;

          const isFrom = order.transferFromDriverId === driverId;
          const isTo = order.transferToDriverId === driverId;
          if (!isFrom && !isTo) return;

          const updateData: any = {};
          if (isFrom) updateData.transferFromApproved = true;
          if (isTo) updateData.transferToApproved = true;

          // Check if both will be approved after this update
          const fromApproved = isFrom ? true : order.transferFromApproved;
          const toApproved = isTo ? true : order.transferToApproved;

          if (fromApproved && toApproved) {
              // Both approved — complete the transfer
              const fromDriver = users.find(u => u.id === order.transferFromDriverId);
              const toDriver = users.find(u => u.id === order.transferToDriverId);
              const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
              const now = Date.now();

              // Log in driver history for both drivers + update stock & movement ledger
              for (const item of order.items) {
                  const product = products.find(p => p.id === item.productId);
                  const productName = product?.name || 'Unknown Product';

                  if (order.transferFromDriverId) {
                      logDriverHistory({
                          driverId: order.transferFromDriverId,
                          driverName: fromDriver?.name || 'Unknown Driver',
                          action: DriverHistoryAction.STOCK_RETURNED,
                          timestamp: now,
                          orderId,
                          orderNumber: orderNum,
                          productId: item.productId,
                          productName,
                          quantityChange: -item.quantity,
                          performedBy: getPerformedBy(),
                          description: `Transferred ${item.quantity}x ${productName} to ${toDriver?.name || 'driver'} — Order #${orderNum}`,
                      });
                  }
                  if (order.transferToDriverId) {
                      logDriverHistory({
                          driverId: order.transferToDriverId,
                          driverName: toDriver?.name || 'Unknown Driver',
                          action: DriverHistoryAction.STOCK_RECEIVED,
                          timestamp: now,
                          orderId,
                          orderNumber: orderNum,
                          productId: item.productId,
                          productName,
                          quantityChange: item.quantity,
                          performedBy: getPerformedBy(),
                          description: `Received ${item.quantity}x ${productName} from ${fromDriver?.name || 'driver'} — Order #${orderNum}`,
                      });
                  }

                  // Update driverStocks only for car-stock orders (fulfilledFromDriver)
                  // For these orders, stock was already deducted from the from-driver at assignment,
                  // so we reverse that and commit from the to-driver instead.
                  // For store-based orders, driver stock is not involved — stock was deducted from the store.
                  if (order.fulfilledFromDriver && order.transferFromDriverId && order.transferToDriverId) {
                      updateDriverStockQuantity(order.transferFromDriverId, item.productId, item.quantity);   // give back to from-driver
                      updateDriverStockQuantity(order.transferToDriverId, item.productId, -item.quantity);    // commit from to-driver
                  }

                  // Inventory Tracker: record DRIVER_TO_DRIVER movement in ledger
                  if (order.transferFromDriverId && order.transferToDriverId && product) {
                      createMovement({
                          date: getBusinessDate(),
                          productId: item.productId,
                          productName: product.name,
                          type: MovementType.DRIVER_TO_DRIVER,
                          fromEntity: fromDriver?.name || 'Unknown Driver',
                          toEntity: toDriver?.name || 'Unknown Driver',
                          storeId: null,
                          driverId: order.transferFromDriverId,
                          toDriverId: order.transferToDriverId,
                          orderId,
                          orderNumber: orderNum,
                          qty: item.quantity,
                          reason: null,
                          timestamp: now,
                      });
                  }
              }

              // Log order-level reassignment history
              logDriverHistory({
                  driverId: order.transferFromDriverId!,
                  driverName: fromDriver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.ORDER_UNASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `Transfer complete — Order #${orderNum} handed off to ${toDriver?.name || 'driver'}`,
              });
              logDriverHistory({
                  driverId: order.transferToDriverId!,
                  driverName: toDriver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.ORDER_ASSIGNED,
                  timestamp: now,
                  orderId,
                  orderNumber: orderNum,
                  performedBy: getPerformedBy(),
                  description: `Transfer complete — Order #${orderNum} received from ${fromDriver?.name || 'driver'}`,
              });

              // Complete transfer: reassign driver, clear transfer fields
              updateData.driverId = order.transferToDriverId;
              updateData.transferPending = false;
              updateData.transferFromDriverId = null;
              updateData.transferToDriverId = null;
              updateData.transferFromApproved = null;
              updateData.transferToApproved = null;
              updateData.status = OrderStatus.CONFIRMED;

              if (USE_SUPABASE) {
                  await sbUpdateOrderFields(orderId, updateData);
              } else {
              await dbInstance.collection('orders').doc(orderId).update(updateData);
              }

              // Update phoneToOrder mapping so customer calls reach the new driver
              await updatePhoneToOrderMapping(orderId, order.transferToDriverId!, order.customerPhone);

              showToast("Transfer complete! Order reassigned.", "success");
          } else {
              if (USE_SUPABASE) {
                  await sbUpdateOrderFields(orderId, updateData);
              } else {
              await dbInstance.collection('orders').doc(orderId).update(updateData);
              }
              showToast("Your approval recorded. Waiting for the other driver.", "info");
          }
      } catch (e) {
          showToast("Failed to approve transfer", "error");
          throw e;
      }
  };

  // A committed manager change shows at once (2026-10-15). The list came only from the live feed (1–3 s,
  // a full re-read: the table has no id column) and the store's legacy mirror only from the 30 s users
  // poll, so after "Temp store manager removed" the chip stayed — on every order of the store — until both
  // caught up. The writer sets the store's mirror as the 0050 trigger just did and re-reads the users list,
  // so a poll already in flight cannot paint the old value back.
  const patchStoreMirror = (storeId: string, mirror: string | null) => {
      setUsers(prev => prev.map(u => u.id === storeId ? { ...u, tempPackerDriverId: mirror ?? undefined } : u));
      refreshPolledTable('users');
  };

  // Assign a driver as temporary store manager (full store access)
  const assignTempPacker = async (storeId: string, driverId: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          // Store-level assignment. Postgres: one row per (store, driver) in
          // store_temp_managers — several managers per store; the legacy
          // users.temp_packer_driver_id mirror is maintained by trigger.
          // Firestore (fallback line only): the single legacy field.
          if (USE_SUPABASE) {
              const candidate = users.find(u => u.id === driverId);
              if (!hasStoreAccess(candidate)) {
                  showToast(noStoreAccessMessage(candidate?.name), 'error');
                  return;
              }
              if (isTempManagerOf(driverId, storeId, users, storeTempManagers)) {
                  showToast(`${users.find(u => u.id === driverId)?.name || 'Driver'} is already a manager of this store`, 'info');
                  return;
              }
              const { error } = await supabase().from('store_temp_managers').insert({
                  store_id: storeId, driver_id: driverId, assigned_by: currentUser?.id ?? null,
              });
              if (error && isStoreAccessRefusal(error)) {
                  // The database is the last word (0084): an older list said yes.
                  showToast(noStoreAccessMessage(candidate?.name), 'error');
                  return;
              }
              if (error && error.code !== '23505') throw new Error(`store_temp_managers insert: ${error.message}`);
              // Committed: show it now. A duplicate (23505) was already a row — listed, mirror untouched.
              setStoreTempManagers(prev => withManagerAdded(prev, {
                  storeId, driverId, assignedBy: currentUser?.id ?? null, assignedAt: error ? 0 : Date.now(),
              }));
              if (!error) patchStoreMirror(storeId, driverId);
          } else {
          await dbInstance.collection('users').doc(storeId).update({
              tempPackerDriverId: driverId,
          });
          }

          const store = users.find(u => u.id === storeId);
          const driver = users.find(u => u.id === driverId);
          const now = Date.now();

          // Log driver history
          logDriverHistory({
              driverId,
              driverName: driver?.name || 'Unknown Driver',
              action: DriverHistoryAction.TEMP_PACKER_ASSIGNED,
              timestamp: now,
              performedBy: getPerformedBy(),
              description: `Assigned as temporary store manager at ${store?.name || 'store'} (full store access)`,
          });

          // Log store history
          logStoreHistory({
              storeId,
              storeName: store?.name || 'Unknown Store',
              action: StoreHistoryAction.TEMP_PACKER_ASSIGNED,
              timestamp: now,
              performedBy: getPerformedBy(),
              description: `${driver?.name || 'Driver'} assigned as temporary store manager (full store access)`,
          });

          showToast(`${driver?.name} now acting as store manager for ${store?.name}`, "success");
      } catch (e) {
          showToast("Failed to assign temp store manager", "error");
          throw e;
      }
  };

  // Remove a temp store manager. With `driverId`: that one manager (the
  // dispatch chip ✕, the driver's own Leave). Without it (legacy callers):
  // every manager of the store.
  const removeTempPacker = async (storeId: string, driverId?: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const store = users.find(u => u.id === storeId);
          const current = tempManagerIdsForStore(storeId, users, storeTempManagers);
          const targets = driverId ? [driverId] : current;

          if (USE_SUPABASE) {
              if (driverId) {
                  const { error } = await supabase().from('store_temp_managers').delete()
                      .eq('store_id', storeId).eq('driver_id', driverId);
                  if (error) throw new Error(`store_temp_managers delete: ${error.message}`);
                  // A manager known only through the legacy mirror (no row yet)
                  // is cleared the old way; the trigger keeps both in step.
                  if (store?.tempPackerDriverId === driverId && !storeTempManagers.some(a => a.storeId === storeId && a.driverId === driverId)) {
                      const { error: e2 } = await supabase().from('users').update({ temp_packer_driver_id: null }).eq('id', storeId);
                      if (e2) throw new Error(`users update: ${e2.message}`);
                  }
              } else {
                  const { error } = await supabase().from('store_temp_managers').delete().eq('store_id', storeId);
                  if (error) throw new Error(`store_temp_managers delete: ${error.message}`);
                  if (store?.tempPackerDriverId) {
                      const { error: e2 } = await supabase().from('users').update({ temp_packer_driver_id: null }).eq('id', storeId);
                      if (e2) throw new Error(`users update: ${e2.message}`);
                  }
              }
              // Committed: the chip leaves now, and the mirror becomes what the trigger just made it.
              setStoreTempManagers(prev => withManagersRemoved(prev, storeId, driverId));
              patchStoreMirror(storeId, legacyMirrorFor(storeId, withManagersRemoved(storeTempManagers, storeId, driverId)));
          } else {
          await dbInstance.collection('users').doc(storeId).update({
              tempPackerDriverId: null,
          });
          }

          const now = Date.now();

          // Log driver history — one row per removed manager
          for (const packerId of targets) {
              const driver = users.find(u => u.id === packerId);
              logDriverHistory({
                  driverId: packerId,
                  driverName: driver?.name || 'Unknown Driver',
                  action: DriverHistoryAction.TEMP_PACKER_REMOVED,
                  timestamp: now,
                  performedBy: getPerformedBy(),
                  description: `Removed as temporary store manager at ${store?.name || 'store'}`,
              });
          }

          // Log store history
          const removedNames = targets.map(id => users.find(u => u.id === id)?.name || 'driver').join(', ');
          logStoreHistory({
              storeId,
              storeName: store?.name || 'Unknown Store',
              action: StoreHistoryAction.TEMP_PACKER_REMOVED,
              timestamp: now,
              performedBy: getPerformedBy(),
              description: `Temporary store manager ${removedNames || 'driver'} removed from store`,
          });

          showToast("Temp store manager removed", "info");
      } catch (e) {
          showToast("Failed to remove temp store manager", "error");
          throw e;
      }
  };

  // ─── NOTIFICATION / READ TRACKING ───
  const markOrderRead = async (orderId: string) => {
      if (!currentUser) return;
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const now = Date.now();
          const existing = currentUser.lastReadOrders || {};
          const updatedOrders = { ...existing, [orderId]: now };
          // Single atomic write: clears the "NEW" flag (lastReadOrders) AND
          // marks every chat channel on this order read (lastReadChat).
          // Before this change, tapping the card only cleared NEW — unread
          // message counts on customer/store/dispatch channels persisted
          // until the driver explicitly opened each chat, which drivers
          // reasonably expected "opening the order" to handle.
          if (USE_SUPABASE) {
              // user_read_state rows: channel '' = order-level (lastReadOrders),
              // one row per chat channel (lastReadChat).
              const at = isoFromMs(now);
              const rows = ['', 'customer', 'admin_store', 'driver_store', 'dispatch'].map((channel) => ({
                  user_id: currentUser.id,
                  conversation_id: orderId,
                  channel,
                  last_read_at: at,
              }));
              const { error } = await supabase().from('user_read_state')
                  .upsert(rows, { onConflict: 'user_id,conversation_id,channel' });
              if (error) throw new Error(`user_read_state upsert: ${error.message}`);
          } else {
          await dbInstance.collection('users').doc(currentUser.id).update({
              lastReadOrders: updatedOrders,
              [`lastReadChat.${orderId}.customer`]: now,
              [`lastReadChat.${orderId}.admin_store`]: now,
              [`lastReadChat.${orderId}.driver_store`]: now,
              [`lastReadChat.${orderId}.dispatch`]: now,
          });
          }
      } catch (e) {
          console.error('Failed to mark order read:', e);
      }
  };

  const markChatRead = async (orderId: string, channel: MessageChannel) => {
      if (!currentUser) return;
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const now = Date.now();
          // Use dot-notation field path to update ONLY this specific channel.
          // Each write targets a single nested key without overwriting siblings.
          if (USE_SUPABASE) {
              const { error } = await supabase().from('user_read_state').upsert(
                  { user_id: currentUser.id, conversation_id: orderId, channel, last_read_at: isoFromMs(now) },
                  { onConflict: 'user_id,conversation_id,channel' }
              );
              if (error) throw new Error(`user_read_state upsert: ${error.message}`);
          } else {
          await dbInstance.collection('users').doc(currentUser.id).update({
              [`lastReadChat.${orderId}.${channel}`]: now
          });
          }
      } catch (e) {
          console.error('Failed to mark chat read:', e);
      }
  };

  // Batch mark multiple channels read in a SINGLE Firestore write.
  // Prevents flicker where persistent unread effect re-runs between
  // individual writes and briefly re-adds badges for un-written channels.
  const markChatReadBatch = async (orderId: string, channels: MessageChannel[]) => {
      if (!currentUser) return;
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const now = Date.now();
          const updates: Record<string, number> = {};
          for (const ch of channels) {
              updates[`lastReadChat.${orderId}.${ch}`] = now;
          }
          if (USE_SUPABASE) {
              const at = isoFromMs(now);
              const rows = channels.map((channel) => ({
                  user_id: currentUser.id,
                  conversation_id: orderId,
                  channel,
                  last_read_at: at,
              }));
              const { error } = await supabase().from('user_read_state')
                  .upsert(rows, { onConflict: 'user_id,conversation_id,channel' });
              if (error) throw new Error(`user_read_state upsert: ${error.message}`);
          } else {
          await dbInstance.collection('users').doc(currentUser.id).update(updates);
          }
      } catch (e) {
          console.error('Failed to batch mark chat read:', e);
      }
  };

  const getUnreadChatCounts = (orderId: string): { customer: number; store: number; dispatch: number; total: number } => {
      // This is a synchronous helper that returns 0s by default.
      // Actual unread counting happens in DriverApp via Firestore snapshot.
      // This is a placeholder exposed on the context for future use.
      return { customer: 0, store: 0, dispatch: 0, total: 0 };
  };

  // Tab-opened timestamp — drives the nav/tab unread badges. Stored via
  // dot-notation so we only touch one key, never clobbering sibling keys on
  // the user doc (mirrors mobile contexts/AppContext.tsx markTabOpened).
  const markTabOpened = async (
      tab: 'deliveries' | 'pickups' | 'transfers' | 'store' | 'orders' | 'messages'
  ): Promise<void> => {
      if (!currentUser) return;
      try {
          if (USE_SUPABASE) {
              // jsonb map on users — merge client-side, values stay epoch-ms.
              const { error } = await supabase().from('users').update({
                  last_opened_at: { ...(currentUser.lastOpenedAt || {}), [tab]: Date.now() },
              }).eq('id', currentUser.id);
              if (error) throw new Error(`users update: ${error.message}`);
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          await dbInstance.collection('users').doc(currentUser.id).update({
              [`lastOpenedAt.${tab}`]: Date.now(),
          });
      } catch (e) {
          console.error('Failed to mark tab opened:', e);
      }
  };

  // SOFT DELETE (0097, Umer 2026-09-25): "Delete" on the Orders page cancels instead. Only a Pending
  // order (no store, no driver — it holds no reserved stock) can be cancelled; it stays in the Cancelled
  // list and can be put back. The database moves its board card to Cancelled and back with it.
  const cancelOrder = async (id: string) => {
      const order = orders.find(o => o.id === id);
      if (order && order.status !== OrderStatus.PENDING) {
          showToast('Only a Pending order can be cancelled — unassign the store / driver first.', 'error');
          throw new Error('order not pending');
      }
      const num = order?.orderNumber || id.slice(-6).toUpperCase();
      try {
          await sbUpdateOrderFields(id, { status: OrderStatus.CANCELLED, cancelledAt: Date.now(), cancelledBy: currentUser?.id || '' });
          logWarehouseHistory({
              productId: 'system', productName: 'Order Management', action: WarehouseHistoryAction.PRODUCT_DELETED,
              timestamp: Date.now(), performedBy: getPerformedBy(), quantityChange: 0, quantityBefore: 0, quantityAfter: 0,
              description: `Order #${num} cancelled — ${order?.customerName || 'Unknown'}`,
          });
          showToast(`Order #${num} cancelled`, 'success');
      } catch (e) {
          showToast('Failed to cancel the order', 'error');
          throw e;
      }
  };

  const restoreOrder = async (id: string) => {
      const order = orders.find(o => o.id === id);
      const num = order?.orderNumber || id.slice(-6).toUpperCase();
      try {
          await sbUpdateOrderFields(id, { status: OrderStatus.PENDING, cancelledAt: null, cancelledBy: null });
          logWarehouseHistory({
              productId: 'system', productName: 'Order Management', action: WarehouseHistoryAction.PRODUCT_DELETED,
              timestamp: Date.now(), performedBy: getPerformedBy(), quantityChange: 0, quantityBefore: 0, quantityAfter: 0,
              description: `Order #${num} put back — ${order?.customerName || 'Unknown'}`,
          });
          showToast(`Order #${num} is back in Pending`, 'success');
      } catch (e) {
          showToast('Failed to put the order back', 'error');
          throw e;
      }
  };

  const reverseOrder = async (orderId: string, targetStatus: OrderStatus) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);
          if (!order) return;

          const wasDelivered = order.status === OrderStatus.DELIVERED;
          const isDriverBacked = !!order.fulfilledFromDriver && !!order.driverId;
          const store = order.storeId ? users.find(u => u.id === order.storeId) : null;
          const driver = order.driverId ? users.find(u => u.id === order.driverId) : null;
          const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
          const reverseNow = Date.now();

          // ── Decide where physical stock returns to ─────────────────────────
          // Driver-backed orders (fulfilledFromDriver): stock always goes back to the driver's car.
          // Store-backed orders:
          //   - target CONFIRMED → stock sits with the driver (they "re-have" it after the reversal)
          //   - target PENDING   → stock goes all the way back to the store
          //   - any other target (ASSIGNED, RETURNING_TO_STORE, etc.) and order was DELIVERED → back to store
          let returnTo: 'driver' | 'store' | 'none' = 'none';
          if (targetStatus === OrderStatus.CONFIRMED) {
              // Undo delivery → Confirmed: delivery moves NO stock (stock
              // leaves at ASSIGN time), so restore nothing — the units are
              // still on the driver's truck. Re-adding here would create
              // phantom inventory (the bug this fixes).
              returnTo = 'none';
          } else if (isDriverBacked) {
              returnTo = 'driver';
          } else if (wasDelivered && order.storeId) {
              returnTo = 'store';
          } else if (targetStatus === OrderStatus.PENDING && order.storeId) {
              returnTo = 'store';
          }

          // ── Apply stock restoration ────────────────────────────────────────
          if (returnTo === 'store' && order.storeId) {
              for (const item of order.items) {
                  await updateStoreStockQuantity(order.storeId, item.productId, item.quantity);
              }
          } else if (returnTo === 'driver' && order.driverId) {
              for (const item of order.items) {
                  await updateDriverStockQuantity(order.driverId, item.productId, item.quantity);
              }
          }

          // ── Persist order mutation ─────────────────────────────────────────
          const updates: any = { status: targetStatus };
          if (targetStatus === OrderStatus.PENDING) {
              updates.storeId = null;
              updates.driverId = null;
              updates.storeConfirmedPickup = false;
              updates.handoffPhoto = null;
              if (isDriverBacked) updates.fulfilledFromDriver = false;
          }
          if (targetStatus !== OrderStatus.DELIVERED) {
              updates.deliveredAt = null;
              updates.deliveryPhoto = null;  // clear stale proof-of-delivery
          }
          if (USE_SUPABASE) {
              await sbUpdateOrderFields(orderId, updates);
          } else {
          await dbInstance.collection('orders').doc(orderId).update(updates);
          }

          // ── History + movement ledger (only meaningful when reversing a delivery) ──
          if (wasDelivered) {
              for (const item of order.items) {
                  const product = products.find(p => p.id === item.productId);
                  const productName = product?.name || 'Unknown Product';

                  if (order.storeId) {
                      logStoreHistory({
                          storeId: order.storeId,
                          storeName: store?.name || 'Unknown Store',
                          action: StoreHistoryAction.DELIVERY_REVERSED,
                          timestamp: reverseNow,
                          orderId,
                          orderNumber: orderNum,
                          productId: item.productId,
                          productName,
                          quantityChange: returnTo === 'store' ? item.quantity : 0,
                          performedBy: getPerformedBy(),
                          description: returnTo === 'store'
                              ? `Delivery reversed — ${item.quantity}x ${productName} returned to store — Order #${orderNum}`
                              : `Delivery reversed — Order #${orderNum} rolled back to ${targetStatus} (stock stays with driver)`,
                      });
                  }

                  if (order.driverId) {
                      logDriverHistory({
                          driverId: order.driverId,
                          driverName: driver?.name || 'Unknown Driver',
                          action: DriverHistoryAction.DELIVERY_REVERSED,
                          timestamp: reverseNow,
                          orderId,
                          orderNumber: orderNum,
                          productId: item.productId,
                          productName,
                          quantityChange: returnTo === 'driver' ? item.quantity : 0,
                          performedBy: getPerformedBy(),
                          description: returnTo === 'driver'
                              ? `Delivery reversed — ${item.quantity}x ${productName} returned to car stock — Order #${orderNum}`
                              : `Delivery reversed — ${item.quantity}x ${productName} for Order #${orderNum} (stock returned to store)`,
                      });
                  }

                  // Movement ledger: compensating entry for the original DELIVERED movement.
                  // fromEntity is the customer; toEntity is whoever the stock now sits with.
                  const toName = returnTo === 'driver'
                      ? (driver?.name || 'Unknown Driver')
                      : returnTo === 'store'
                          ? (store?.name || 'Unknown Store')
                          : 'Unknown';
                  createMovement({
                      date: getBusinessDate(),
                      productId: item.productId,
                      productName,
                      type: MovementType.DELIVERY_REVERSED,
                      fromEntity: order.customerName,
                      toEntity: toName,
                      storeId: returnTo === 'store' ? (order.storeId || null) : null,
                      driverId: returnTo === 'driver' ? (order.driverId || null) : null,
                      toDriverId: null,
                      orderId,
                      orderNumber: orderNum,
                      qty: item.quantity,
                      reason: `reversed_to_${targetStatus.toLowerCase()}`,
                      timestamp: reverseNow,
                  });
              }

              logWarehouseHistory({
                  productId: 'system',
                  productName: 'Order Management',
                  action: WarehouseHistoryAction.TRANSACTION_STATUS_CHANGED,
                  timestamp: reverseNow,
                  performedBy: getPerformedBy(),
                  quantityChange: 0,
                  quantityBefore: 0,
                  quantityAfter: 0,
                  description: `Order #${orderNum} delivery reversed to ${targetStatus} — ${order.items.length} item(s) returned to ${returnTo}`,
              });
          }

          showToast("Order status reversed successfully", "success");
      } catch (e) {
          showToast("Failed to reverse order", "error");
          throw e;
      }
  };

  const changePassword = async (newPass: string, currentPass?: string) => {
      if (USE_SUPABASE) {
          const { error } = await supabase().auth.updateUser({ password: newPass });
          if (error) throw new Error(error.message);
          if (currentUser) {
              const { error: updErr } = await supabase().from('users')
                  .update({ requires_password_reset: false }).eq('id', currentUser.id);
              if (updErr) throw new Error(`users update: ${updErr.message}`);
              setCurrentUser({ ...currentUser, requiresPasswordReset: false });
          }
          await logWarehouseHistory({
              productId: 'system',
              productName: 'User Management',
              action: WarehouseHistoryAction.PASSWORD_CHANGED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Password changed by ${currentUser?.name || 'Unknown'} (${currentUser?.role || 'unknown'})`,
          });
          showToast("Password updated successfully", "success");
          return;
      }
      const { auth: authInstance, db: dbInstance } = ensureFirebaseInitialized();
      const user = authInstance.currentUser;
      if (!user) throw new Error("No user logged in");
      try {
          if (currentPass && user.email) {
              const credential = firebase.auth.EmailAuthProvider.credential(user.email, currentPass);
              await user.reauthenticateWithCredential(credential);
          }
          await user.updatePassword(newPass);
          if (currentUser) {
             await dbInstance.collection('users').doc(currentUser.id).update({ requiresPasswordReset: false });
             setCurrentUser({ ...currentUser, requiresPasswordReset: false });
          }
          await logWarehouseHistory({
              productId: 'system',
              productName: 'User Management',
              action: WarehouseHistoryAction.PASSWORD_CHANGED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Password changed by ${currentUser?.name || 'Unknown'} (${currentUser?.role || 'unknown'})`,
          });
          showToast("Password updated successfully", "success");
      } catch (e: any) {
          throw e;
      }
  };

  const adminResetUserPassword = async (userId: string, tempPass: string): Promise<boolean> => {
      if (USE_SUPABASE) {
          // Needs the service-role key (server-side only) — handled by the
          // supabase-admin-users function, which also sets
          // requires_password_reset on the target row.
          try {
              const { data: { session } } = await supabase().auth.getSession();
              const token = session?.access_token;
              const response = await fetch('/.netlify/functions/supabase-admin-users', {
                  method: 'POST',
                  headers: {
                      'Content-Type': 'application/json',
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  body: JSON.stringify({ action: 'reset-password', uid: userId, newPassword: tempPass })
              });
              const data = await response.json();
              if (!response.ok) {
                  throw new Error(data.error || 'Backend failed to update password');
              }
              const resetUser = users.find(u => u.id === userId);
              logWarehouseHistory({
                  productId: 'system',
                  productName: 'User Management',
                  action: WarehouseHistoryAction.PASSWORD_RESET,
                  timestamp: Date.now(),
                  performedBy: getPerformedBy(),
                  quantityChange: 0,
                  quantityBefore: 0,
                  quantityAfter: 0,
                  description: `Password reset — ${resetUser?.name || 'Unknown'} (${resetUser?.role || 'unknown'}) — temporary password set by admin`,
              });
              return true;
          } catch (e: any) {
              showToast(`Failed to reset password: ${e.message}`, "error");
              return false;
          }
      }
      try {
          // The function now verifies a Firebase ID token + enforces the role
          // matrix server-side, so send the caller's token.
          const { auth: authInstance } = ensureFirebaseInitialized();
          const idToken = await authInstance.currentUser?.getIdToken();
          const response = await fetch('/.netlify/functions/admin-reset-password', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
              },
              body: JSON.stringify({ uid: userId, newPassword: tempPass })
          });
          const data = await response.json();
          if (!response.ok) {
              throw new Error(data.error || "Backend failed to update password");
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          await dbInstance.collection('users').doc(userId).update({ requiresPasswordReset: true });
          const resetUser = users.find(u => u.id === userId);
          logWarehouseHistory({
              productId: 'system',
              productName: 'User Management',
              action: WarehouseHistoryAction.PASSWORD_RESET,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Password reset — ${resetUser?.name || 'Unknown'} (${resetUser?.role || 'unknown'}) — temporary password set by admin`,
          });
          return true;
      } catch (e: any) {
          showToast(`Failed to reset password: ${e.message}`, "error");
          return false;
      }
  };

  const clearSystemData = async () => {
      try {
          if (USE_SUPABASE) {
              const sb = supabase();
              // PostgREST refuses an unfiltered delete, so every wipe carries a
              // match-everything filter. order_items/order_photos and
              // transaction_items go with their parent (ON DELETE CASCADE).
              const { error: ordErr } = await sb.from('orders').delete().neq('id', '');
              if (ordErr) throw new Error(`orders delete: ${ordErr.message}`);
              const { error: txErr } = await sb.from('transactions').delete().neq('id', '');
              if (txErr) throw new Error(`transactions delete: ${txErr.message}`);
              const { error: ssErr } = await sb.from('store_stocks').delete().neq('store_id', '');
              if (ssErr) throw new Error(`store_stocks delete: ${ssErr.message}`);

              const resetTimestamp = Date.now();
              for (const product of products) {
                  if (product.warehouseStock > 0) {
                      logWarehouseHistory({
                          productId: product.id,
                          productName: product.name,
                          action: WarehouseHistoryAction.STOCK_RESET,
                          timestamp: resetTimestamp,
                          performedBy: {
                              userId: currentUser?.id || 'unknown',
                              userName: currentUser?.name || 'Unknown',
                              role: currentUser?.role || UserRole.SUPER_ADMIN,
                          },
                          quantityChange: -product.warehouseStock,
                          quantityBefore: product.warehouseStock,
                          quantityAfter: 0,
                          description: `System cleared — stock reset from ${product.warehouseStock} to 0`,
                      });
                  }
              }

              const { error: prodErr } = await sb.from('products').update({ warehouse_stock: 0 }).neq('id', '');
              if (prodErr) throw new Error(`products update: ${prodErr.message}`);

              logWarehouseHistory({
                  productId: 'system',
                  productName: 'System',
                  action: WarehouseHistoryAction.SYSTEM_CLEARED,
                  timestamp: Date.now(),
                  performedBy: getPerformedBy(),
                  quantityChange: 0,
                  quantityBefore: 0,
                  quantityAfter: 0,
                  description: `System data cleared — all orders, transactions, and store stocks deleted`,
              });
              showToast("System data cleared successfully.", "success");
              window.location.reload();
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const ordersSnap = await dbInstance.collection('orders').get();
          const batch1 = dbInstance.batch();
          ordersSnap.docs.forEach(doc => batch1.delete(doc.ref));
          await batch1.commit();

          const txSnap = await dbInstance.collection('transactions').get();
          const batch2 = dbInstance.batch();
          txSnap.docs.forEach(doc => batch2.delete(doc.ref));
          await batch2.commit();

          const stockSnap = await dbInstance.collection('storeStocks').get();
          const batch3 = dbInstance.batch();
          stockSnap.docs.forEach(doc => batch3.delete(doc.ref));
          await batch3.commit();

          const resetTimestamp = Date.now();
          for (const product of products) {
              if (product.warehouseStock > 0) {
                  logWarehouseHistory({
                      productId: product.id,
                      productName: product.name,
                      action: WarehouseHistoryAction.STOCK_RESET,
                      timestamp: resetTimestamp,
                      performedBy: {
                          userId: currentUser?.id || 'unknown',
                          userName: currentUser?.name || 'Unknown',
                          role: currentUser?.role || UserRole.SUPER_ADMIN,
                      },
                      quantityChange: -product.warehouseStock,
                      quantityBefore: product.warehouseStock,
                      quantityAfter: 0,
                      description: `System cleared — stock reset from ${product.warehouseStock} to 0`,
                  });
              }
          }

          const prodSnap = await dbInstance.collection('products').get();
          const batch4 = dbInstance.batch();
          prodSnap.docs.forEach(doc => batch4.update(doc.ref, { warehouseStock: 0 }));
          await batch4.commit();

          logWarehouseHistory({
              productId: 'system',
              productName: 'System',
              action: WarehouseHistoryAction.SYSTEM_CLEARED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `System data cleared — all orders, transactions, and store stocks deleted`,
          });
          showToast("System data cleared successfully.", "success");
          window.location.reload();
      } catch (e) {
          showToast("Failed to clear system data.", "error");
      }
  };

  const performDayZero = async () => {
      try {
          if (USE_SUPABASE) {
              const sb = supabase();
              const timestamp = Date.now();

              const { error: setErr } = await sb.from('settings')
                  .upsert({ key: 'dayZero', value: { timestamp } });
              if (setErr) throw new Error(`settings upsert: ${setErr.message}`);

              const { error: ssErr } = await sb.from('store_stocks').update({ quantity: 0 }).neq('store_id', '');
              if (ssErr) throw new Error(`store_stocks update: ${ssErr.message}`);

              const { error: prodErr } = await sb.from('products').update({ warehouse_stock: 0 }).neq('id', '');
              if (prodErr) throw new Error(`products update: ${prodErr.message}`);

              const resetTimestamp = Date.now();
              for (const product of products) {
                  if (product.warehouseStock > 0) {
                      logWarehouseHistory({
                          productId: product.id,
                          productName: product.name,
                          action: WarehouseHistoryAction.STOCK_RESET,
                          timestamp: resetTimestamp,
                          performedBy: getPerformedBy(),
                          quantityChange: -product.warehouseStock,
                          quantityBefore: product.warehouseStock,
                          quantityAfter: 0,
                          description: `Day Zero Reset — stock reset from ${product.warehouseStock} to 0`,
                      });
                  }
              }

              logWarehouseHistory({
                  productId: 'system',
                  productName: 'System',
                  action: WarehouseHistoryAction.DAY_ZERO,
                  timestamp: Date.now(),
                  performedBy: getPerformedBy(),
                  quantityChange: 0,
                  quantityBefore: 0,
                  quantityAfter: 0,
                  description: `Day Zero performed — all warehouse and store stock reset to 0`,
              });
              showToast("Day Zero complete! All stock reset. Enter actual counts now.", "success");
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const timestamp = Date.now();

          // Save the Day Zero timestamp
          await dbInstance.collection('settings').doc('dayZero').set({ timestamp });

          // Reset all store stocks to 0
          const stockSnap = await dbInstance.collection('storeStocks').get();
          const batch = dbInstance.batch();
          stockSnap.docs.forEach(doc => batch.update(doc.ref, { quantity: 0 }));
          await batch.commit();

          // Reset all product warehouse stock to 0
          const prodSnap = await dbInstance.collection('products').get();
          const batch2 = dbInstance.batch();
          prodSnap.docs.forEach(doc => batch2.update(doc.ref, { warehouseStock: 0 }));
          await batch2.commit();

          // Log warehouse history for the reset
          const resetTimestamp = Date.now();
          for (const product of products) {
              if (product.warehouseStock > 0) {
                  logWarehouseHistory({
                      productId: product.id,
                      productName: product.name,
                      action: WarehouseHistoryAction.STOCK_RESET,
                      timestamp: resetTimestamp,
                      performedBy: getPerformedBy(),
                      quantityChange: -product.warehouseStock,
                      quantityBefore: product.warehouseStock,
                      quantityAfter: 0,
                      description: `Day Zero Reset — stock reset from ${product.warehouseStock} to 0`,
                  });
              }
          }

          logWarehouseHistory({
              productId: 'system',
              productName: 'System',
              action: WarehouseHistoryAction.DAY_ZERO,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Day Zero performed — all warehouse and store stock reset to 0`,
          });
          showToast("Day Zero complete! All stock reset. Enter actual counts now.", "success");
      } catch (e) {
          showToast("Failed to perform Day Zero reset.", "error");
      }
  };

  const resetAllStoreStock = async () => {
      if (USE_SUPABASE) {
          try {
              const sb = supabase();
              const timestamp = Date.now();

              // ── 1. Collect totals to return to warehouse (stores + drivers) ──
              const returnTotals: Record<string, number> = {};
              const activeStoreStocks = storeStocks.filter(ss => ss.quantity > 0);
              for (const ss of activeStoreStocks) {
                  returnTotals[ss.productId] = (returnTotals[ss.productId] || 0) + ss.quantity;
              }
              const activeDriverStocks = driverStocks.filter(ds => ds.quantity > 0);
              for (const ds of activeDriverStocks) {
                  returnTotals[ds.productId] = (returnTotals[ds.productId] || 0) + ds.quantity;
              }

              // ── 2. Add all stock back to warehouse products ──
              for (const [productId, totalQty] of Object.entries(returnTotals)) {
                  const product = products.find(p => p.id === productId);
                  if (product) {
                      const { error } = await sb.from('products').update({
                          warehouse_stock: product.warehouseStock + totalQty,
                          is_active: true,
                      }).eq('id', productId);
                      if (error) throw new Error(`products update: ${error.message}`);
                  }
              }

              // ── 3. Zero store + driver stocks ──
              // The Firestore path also stamps movedToWarehouse/movedAt/
              // previousQuantity on each doc; store_stocks/driver_stocks have
              // no such columns and nothing reads them — the warehouse history
              // entries below are the audit trail.
              const { error: ssErr } = await sb.from('store_stocks').update({ quantity: 0 }).neq('store_id', '');
              if (ssErr) throw new Error(`store_stocks update: ${ssErr.message}`);
              const { error: dsErr } = await sb.from('driver_stocks').update({ quantity: 0 }).neq('driver_id', '');
              if (dsErr) throw new Error(`driver_stocks update: ${dsErr.message}`);

              // ── 4. Set new Day Zero timestamp ──
              const { error: setErr } = await sb.from('settings')
                  .upsert({ key: 'dayZero', value: { timestamp } });
              if (setErr) throw new Error(`settings upsert: ${setErr.message}`);

              // ── 5. Log warehouse history — store stock returned ──
              const storeReturnTotals: Record<string, number> = {};
              for (const ss of activeStoreStocks) {
                  storeReturnTotals[ss.productId] = (storeReturnTotals[ss.productId] || 0) + ss.quantity;
              }
              for (const [productId, totalQty] of Object.entries(storeReturnTotals)) {
                  const product = products.find(p => p.id === productId);
                  if (product) {
                      logWarehouseHistory({
                          productId,
                          productName: product.name,
                          action: WarehouseHistoryAction.STOCK_IN_FROM_STORE,
                          timestamp,
                          performedBy: getPerformedBy(),
                          quantityChange: totalQty,
                          quantityBefore: product.warehouseStock,
                          quantityAfter: product.warehouseStock + totalQty,
                          description: `Stock Reset — ${totalQty} units returned from stores to warehouse`,
                      });
                  }
              }

              // ── 6. Log warehouse history — driver stock returned ──
              const driverReturnTotals: Record<string, number> = {};
              for (const ds of activeDriverStocks) {
                  driverReturnTotals[ds.productId] = (driverReturnTotals[ds.productId] || 0) + ds.quantity;
              }
              for (const [productId, totalQty] of Object.entries(driverReturnTotals)) {
                  const product = products.find(p => p.id === productId);
                  if (product) {
                      logWarehouseHistory({
                          productId,
                          productName: product.name,
                          action: WarehouseHistoryAction.STOCK_IN_FROM_DRIVER,
                          timestamp,
                          performedBy: getPerformedBy(),
                          quantityChange: totalQty,
                          quantityBefore: product.warehouseStock,
                          quantityAfter: product.warehouseStock + totalQty,
                          description: `Stock Reset — ${totalQty} units returned from drivers to warehouse`,
                      });
                  }
              }

              // ── 7. System-level reset log ──
              const totalStoreUnits = activeStoreStocks.reduce((s, ss) => s + ss.quantity, 0);
              const totalDriverUnits = activeDriverStocks.reduce((s, ds) => s + ds.quantity, 0);
              logWarehouseHistory({
                  productId: 'system',
                  productName: 'System',
                  action: WarehouseHistoryAction.STOCK_RESET,
                  timestamp,
                  performedBy: getPerformedBy(),
                  quantityChange: 0,
                  quantityBefore: 0,
                  quantityAfter: 0,
                  description: `Full Stock Reset — ${totalStoreUnits} units from stores + ${totalDriverUnits} units from drivers moved back to warehouse.`,
              });

              showToast(`All stock moved to warehouse (${totalStoreUnits} from stores, ${totalDriverUnits} from drivers). Fresh start!`, "success");
          } catch (e) {
              showToast("Failed to reset stock.", "error");
          }
          return;
      }
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const timestamp = Date.now();

          // ── 1. Collect totals to return to warehouse (stores + drivers) ──
          const returnTotals: Record<string, number> = {};

          // From stores
          const activeStoreStocks = storeStocks.filter(ss => ss.quantity > 0);
          for (const ss of activeStoreStocks) {
              returnTotals[ss.productId] = (returnTotals[ss.productId] || 0) + ss.quantity;
          }

          // From drivers
          const activeDriverStocks = driverStocks.filter(ds => ds.quantity > 0);
          for (const ds of activeDriverStocks) {
              returnTotals[ds.productId] = (returnTotals[ds.productId] || 0) + ds.quantity;
          }

          // ── 2. Add all stock back to warehouse products (chunked) ──
          const prodEntries = Object.entries(returnTotals);
          for (let i = 0; i < prodEntries.length; i += 450) {
              const chunk = prodEntries.slice(i, i + 450);
              const prodBatch = dbInstance.batch();
              for (const [productId, totalQty] of chunk) {
                  const product = products.find(p => p.id === productId);
                  if (product) {
                      const newStock = product.warehouseStock + totalQty;
                      prodBatch.update(dbInstance.collection('products').doc(productId), {
                          warehouseStock: newStock,
                          isActive: true,
                      });
                  }
              }
              await prodBatch.commit();
          }

          // ── 3. Flag & zero store stocks (movedToWarehouse flag) ──
          const storeStockSnap = await dbInstance.collection('storeStocks').get();
          const storeStockDocs = storeStockSnap.docs;
          for (let i = 0; i < storeStockDocs.length; i += 450) {
              const chunk = storeStockDocs.slice(i, i + 450);
              const stockBatch = dbInstance.batch();
              chunk.forEach(doc => {
                  const data = doc.data();
                  if (data.quantity > 0) {
                      stockBatch.update(doc.ref, { quantity: 0, movedToWarehouse: true, movedAt: timestamp, previousQuantity: data.quantity });
                  } else {
                      stockBatch.update(doc.ref, { quantity: 0 });
                  }
              });
              await stockBatch.commit();
          }

          // ── 4. Flag & zero driver stocks (movedToWarehouse flag) ──
          const driverStockSnap = await dbInstance.collection('driverStocks').get();
          const driverStockDocs = driverStockSnap.docs;
          for (let i = 0; i < driverStockDocs.length; i += 450) {
              const chunk = driverStockDocs.slice(i, i + 450);
              const dsBatch = dbInstance.batch();
              chunk.forEach(doc => {
                  const data = doc.data();
                  if (data.quantity > 0) {
                      dsBatch.update(doc.ref, { quantity: 0, movedToWarehouse: true, movedAt: timestamp, previousQuantity: data.quantity });
                  } else {
                      dsBatch.update(doc.ref, { quantity: 0 });
                  }
              });
              await dsBatch.commit();
          }

          // ── 5. Set new Day Zero timestamp ──
          await dbInstance.collection('settings').doc('dayZero').set({ timestamp });

          // ── 6. Log warehouse history — store stock returned ──
          const storeReturnTotals: Record<string, number> = {};
          for (const ss of activeStoreStocks) {
              storeReturnTotals[ss.productId] = (storeReturnTotals[ss.productId] || 0) + ss.quantity;
          }
          for (const [productId, totalQty] of Object.entries(storeReturnTotals)) {
              const product = products.find(p => p.id === productId);
              if (product) {
                  logWarehouseHistory({
                      productId,
                      productName: product.name,
                      action: WarehouseHistoryAction.STOCK_IN_FROM_STORE,
                      timestamp,
                      performedBy: getPerformedBy(),
                      quantityChange: totalQty,
                      quantityBefore: product.warehouseStock,
                      quantityAfter: product.warehouseStock + totalQty,
                      description: `Stock Reset — ${totalQty} units returned from stores to warehouse`,
                  });
              }
          }

          // ── 7. Log warehouse history — driver stock returned ──
          const driverReturnTotals: Record<string, number> = {};
          for (const ds of activeDriverStocks) {
              driverReturnTotals[ds.productId] = (driverReturnTotals[ds.productId] || 0) + ds.quantity;
          }
          for (const [productId, totalQty] of Object.entries(driverReturnTotals)) {
              const product = products.find(p => p.id === productId);
              if (product) {
                  logWarehouseHistory({
                      productId,
                      productName: product.name,
                      action: WarehouseHistoryAction.STOCK_IN_FROM_DRIVER,
                      timestamp,
                      performedBy: getPerformedBy(),
                      quantityChange: totalQty,
                      quantityBefore: product.warehouseStock,
                      quantityAfter: product.warehouseStock + totalQty,
                      description: `Stock Reset — ${totalQty} units returned from drivers to warehouse`,
                  });
              }
          }

          // ── 8. System-level reset log ──
          const totalStoreUnits = activeStoreStocks.reduce((s, ss) => s + ss.quantity, 0);
          const totalDriverUnits = activeDriverStocks.reduce((s, ds) => s + ds.quantity, 0);
          logWarehouseHistory({
              productId: 'system',
              productName: 'System',
              action: WarehouseHistoryAction.STOCK_RESET,
              timestamp,
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Full Stock Reset — ${totalStoreUnits} units from stores + ${totalDriverUnits} units from drivers moved back to warehouse.`,
          });

          showToast(`All stock moved to warehouse (${totalStoreUnits} from stores, ${totalDriverUnits} from drivers). Fresh start!`, "success");
      } catch (e) {
          showToast("Failed to reset stock.", "error");
      }
  };

  const getStoreStock = (storeId: string, productId: string) => {
      return storeStockMap.get(`${storeId}_${productId}`) || 0;
  };

  const getDriverStock = (driverId: string, productId: string) => {
      return driverStockMap.get(`${driverId}_${productId}`) || 0;
  };

  // Cash on hand counts from the cash start only (services/cashStart.ts — client, 2026-10-13: show zero,
  // keep history). A delivery or a turn-in from before that moment is still in every report; it just no
  // longer adds to, or takes from, a balance.
  const getStoreCashBalance = (storeId: string) => {
      const totalCollected = orders
        .filter(o => o.storeId === storeId && o.status === OrderStatus.DELIVERED && o.requiresCash && sinceCashStart(o.deliveredAt ?? o.createdAt))
        .reduce((sum, o) => sum + o.cashAmount, 0);
      const totalTurnedIn = transactions
        .filter(t => t.storeId === storeId && t.type === TransactionType.CASH_TURN_IN && t.status === TransactionStatus.APPROVED && sinceCashStart(t.timestamp))
        .reduce((sum, t) => sum + (t.cashAmount || 0), 0);
      const totalPending = transactions
        .filter(t => t.storeId === storeId && t.type === TransactionType.CASH_TURN_IN && sinceCashStart(t.timestamp) &&
                (t.status === TransactionStatus.PENDING_DISPATCH || t.status === TransactionStatus.PENDING_ADMIN || t.status === TransactionStatus.PENDING_STORE))
        .reduce((sum, t) => sum + (t.cashAmount || 0), 0);
      return totalCollected - totalTurnedIn - totalPending;
  };

  const getDriverCashBalance = (driverId: string) => {
      const totalCollected = orders
        .filter(o => o.driverId === driverId && o.status === OrderStatus.DELIVERED && o.requiresCash && sinceCashStart(o.deliveredAt ?? o.createdAt))
        .reduce((sum, o) => sum + o.cashAmount, 0);
      const totalTurnedIn = transactions
        .filter(t => t.driverId === driverId && t.type === TransactionType.CASH_TURN_IN && t.status === TransactionStatus.APPROVED && sinceCashStart(t.timestamp))
        .reduce((sum, t) => sum + (t.cashAmount || 0), 0);
      const totalPending = transactions
        .filter(t => t.driverId === driverId && t.type === TransactionType.CASH_TURN_IN && sinceCashStart(t.timestamp) &&
                (t.status === TransactionStatus.PENDING_DISPATCH || t.status === TransactionStatus.PENDING_ADMIN))
        .reduce((sum, t) => sum + (t.cashAmount || 0), 0);
      return totalCollected - totalTurnedIn - totalPending;
  };

  const confirmStorePickup = async (orderId: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const order = orders.find(o => o.id === orderId);

          // Promote ASSIGNED → CONFIRMED so the badge reflects "driver
          // holds the goods" (parity with the direct-fulfill path which
          // writes CONFIRMED at assign time). Defensive guard: only
          // promote when status is currently ASSIGNED so a stray Override
          // on a DELIVERED / RETURNING order can't regress the lifecycle.
          const updates: Record<string, unknown> = { storeConfirmedPickup: true };
          if (order?.status === OrderStatus.ASSIGNED) {
              updates.status = OrderStatus.CONFIRMED;
          }
          if (USE_SUPABASE) {
              // The handoff lands once (Phase 1, 2026-09-30): only while the store has not confirmed it yet —
              // a second tap, or the store's own Confirm a moment earlier, logs and texts nothing twice.
              await sbUpdateOrderFields(orderId, updates, { notTrue: ['store_confirmed_pickup'], notSaved: 'This handoff is confirmed already.' });
          } else {
          await dbInstance.collection('orders').doc(orderId).update(updates as any);
          }

          if (order) {
              const store = users.find(u => u.id === order.storeId);
              const driver = users.find(u => u.id === order.driverId);
              // Who packed: the current user when they are one of the store's
              // temp managers, otherwise the most recent manager (if any).
              const managerIds = tempManagerIdsForStore(order.storeId, users, storeTempManagers);
              const packerId = currentUser && managerIds.includes(currentUser.id) ? currentUser.id : managerIds[0];
              const packer = packerId ? users.find(u => u.id === packerId) : null;
              const orderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
              const now = Date.now();

              // Log store history: handoff confirmed
              if (order.storeId) {
                  logStoreHistory({
                      storeId: order.storeId,
                      storeName: store?.name || 'Unknown Store',
                      action: StoreHistoryAction.ORDER_DELIVERED,
                      timestamp: now,
                      orderId,
                      orderNumber: orderNum,
                      performedBy: getPerformedBy(),
                      description: `Order #${orderNum} handed off to ${driver?.name || 'driver'}${packer ? ` (packed by ${packer.name})` : ''}`,
                  });
              }

              // Log driver history: stock received from store
              if (order.driverId) {
                  for (const item of order.items) {
                      const product = products.find(p => p.id === item.productId);
                      logDriverHistory({
                          driverId: order.driverId,
                          driverName: driver?.name || 'Unknown Driver',
                          action: DriverHistoryAction.STOCK_RECEIVED,
                          timestamp: now,
                          orderId,
                          orderNumber: orderNum,
                          productId: item.productId,
                          productName: product?.name || 'Unknown Product',
                          quantityChange: item.quantity,
                          performedBy: getPerformedBy(),
                          description: `Picked up ${item.quantity}x ${product?.name || 'Unknown'} from ${store?.name || 'store'} — Order #${orderNum}`,
                      });
                      // Inventory Tracker: movement ledger (store → driver handoff)
                      createMovement({
                          date: getBusinessDate(),
                          productId: item.productId,
                          productName: product?.name || 'Unknown Product',
                          type: MovementType.STORE_TO_DRIVER,
                          fromEntity: store?.name || 'Unknown Store',
                          toEntity: driver?.name || 'Unknown Driver',
                          storeId: order.storeId || null,
                          driverId: order.driverId,
                          toDriverId: null,
                          orderId: order.id,
                          orderNumber: orderNum,
                          qty: item.quantity,
                          reason: null,
                          timestamp: now,
                      });
                  }
              }
          }

          showToast("Order marked as ready for driver pickup.", "success");
          // SMS: notify driver that store confirmed pickup
          if (order) {
              const pickupDriver = users.find(u => u.id === order.driverId);
              const pickupOrderNum = order.orderNumber || orderId.slice(-6).toUpperCase();
              if (pickupDriver?.phone) {
                  sendSMS(pickupDriver.phone, `Order #${pickupOrderNum}: Store confirmed pickup. Order details are now unlocked. Check your app.`).catch(() => {});
              }
          }
      } catch (e) {
          showToast(e instanceof OrderNotSavedError ? e.message : "Failed to confirm pickup", "error");
          // A failed handoff is a failure for the caller too (Phase 1): StoreHub used to say "Handoff confirmed!"
          // right after this error.
          throw e;
      }
  };

  // ─── Twilio number pool management ─────────────────────────────────────────

  // Normalize a phone input (strip non-digits, ensure +1 prefix for 10-digit NA numbers)
  const toE164 = (raw: string): string => {
      const digits = raw.replace(/\D/g, '');
      if (digits.length === 10) return `+1${digits}`;
      if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
      return raw.startsWith('+') ? raw : `+${digits}`;
  };

  const addTwilioNumber = async (number: string, label?: string) => {
      try {
          const { db: dbInstance } = ensureFirebaseInitialized();
          const e164 = toE164(number);
          const id = e164.replace(/\D/g, '');
          if (!id) {
              showToast('Invalid phone number', 'error');
              return;
          }
          if (USE_SUPABASE) {
              const sb = supabase();
              const { data: existingRow, error: exErr } = await sb.from('twilio_numbers').select('id').eq('id', id).maybeSingle();
              if (exErr) throw new Error(`twilio_numbers read: ${exErr.message}`);
              if (existingRow) {
                  showToast('This number is already in the pool', 'error');
                  return;
              }
              const { error } = await sb.from('twilio_numbers').insert({
                  id,
                  number: e164,
                  label: label || '',
                  assigned_driver_id: null,
                  is_default: false,
                  added_at: isoFromMs(Date.now()),
                  added_by: currentUser?.id || '',
              });
              if (error) throw new Error(`twilio_numbers insert: ${error.message}`);
              showToast(`Added ${e164} to the pool`, 'success');
              return;
          }
          const existing = await dbInstance.collection('twilioNumbers').doc(id).get();
          if (existing.exists) {
              showToast('This number is already in the pool', 'error');
              return;
          }
          const doc: Omit<TwilioNumber, 'id'> = {
              number: e164,
              label: label || '',
              assignedDriverId: null,
              isDefault: false,
              addedAt: Date.now(),
              addedBy: currentUser?.id || '',
          };
          await dbInstance.collection('twilioNumbers').doc(id).set(doc);
          showToast(`Added ${e164} to the pool`, 'success');
      } catch (e) {
          console.error(e);
          showToast('Failed to add number', 'error');
      }
  };

  const assignTwilioNumberToDriver = async (numberId: string, driverId: string | null) => {
      try {
          if (USE_SUPABASE) {
              const sb = supabase();
              const { data: numData, error: numErr } = await sb.from('twilio_numbers').select('*').eq('id', numberId).maybeSingle();
              if (numErr) throw new Error(`twilio_numbers read: ${numErr.message}`);
              if (!numData) {
                  showToast('Number not found', 'error');
                  return;
              }
              // Default numbers are the pool-wide fallback — never assignable.
              if (numData.is_default && driverId) {
                  showToast('The default number cannot be assigned to a specific driver. Unset it as default first.', 'error');
                  return;
              }
              const prevDriverId = numData.assigned_driver_id;
              // Previously assigned to someone else → clear their users.twilioNumber
              if (prevDriverId && prevDriverId !== driverId) {
                  const { error } = await sb.from('users').update({ twilio_number: null }).eq('id', prevDriverId);
                  if (error) throw new Error(`users update: ${error.message}`);
              }
              // Target driver already holds a different number → unassign it first
              if (driverId) {
                  const targetDriver = users.find(u => u.id === driverId);
                  if (targetDriver?.twilioNumber && targetDriver.twilioNumber !== numData.number) {
                      const oldId = targetDriver.twilioNumber.replace(/\D/g, '');
                      const { error } = await sb.from('twilio_numbers').update({ assigned_driver_id: null }).eq('id', oldId);
                      if (error) throw new Error(`twilio_numbers update: ${error.message}`);
                  }
              }
              const { error: asnErr } = await sb.from('twilio_numbers').update({ assigned_driver_id: driverId }).eq('id', numberId);
              if (asnErr) throw new Error(`twilio_numbers update: ${asnErr.message}`);
              if (driverId) {
                  const { error } = await sb.from('users').update({ twilio_number: numData.number }).eq('id', driverId);
                  if (error) throw new Error(`users update: ${error.message}`);
                  showToast('Number assigned to driver', 'success');
              } else {
                  showToast('Number unassigned', 'info');
              }
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const numDoc = await dbInstance.collection('twilioNumbers').doc(numberId).get();
          if (!numDoc.exists) {
              showToast('Number not found', 'error');
              return;
          }
          const numData = numDoc.data() as TwilioNumber;

          // Default numbers are reserved as the pool-wide fallback and cannot be
          // assigned to any single driver.
          if (numData.isDefault && driverId) {
              showToast('The default number cannot be assigned to a specific driver. Unset it as default first.', 'error');
              return;
          }

          const prevDriverId = numData.assignedDriverId;

          // If this number was previously assigned to someone else, clear their users.twilioNumber
          if (prevDriverId && prevDriverId !== driverId) {
              await dbInstance.collection('users').doc(prevDriverId).update({ twilioNumber: firebase.firestore.FieldValue.delete() } as any);
          }

          // If the target driver already has a different number, unassign that one first
          if (driverId) {
              const targetDriver = users.find(u => u.id === driverId);
              if (targetDriver?.twilioNumber && targetDriver.twilioNumber !== numData.number) {
                  const oldId = targetDriver.twilioNumber.replace(/\D/g, '');
                  await dbInstance.collection('twilioNumbers').doc(oldId).update({ assignedDriverId: null });
              }
          }

          await dbInstance.collection('twilioNumbers').doc(numberId).update({ assignedDriverId: driverId });

          if (driverId) {
              await dbInstance.collection('users').doc(driverId).update({ twilioNumber: numData.number });
              showToast('Number assigned to driver', 'success');
          } else {
              showToast('Number unassigned', 'info');
          }
      } catch (e) {
          console.error(e);
          showToast('Failed to assign number', 'error');
      }
  };

  const setDefaultTwilioNumber = async (numberId: string) => {
      try {
          if (USE_SUPABASE) {
              const sb = supabase();
              const { data: targetData, error: tErr } = await sb.from('twilio_numbers').select('*').eq('id', numberId).maybeSingle();
              if (tErr) throw new Error(`twilio_numbers read: ${tErr.message}`);
              if (!targetData) {
                  showToast('Number not found', 'error');
                  return;
              }
              // Unassign from any driver first — a default can't also belong to one.
              if (targetData.assigned_driver_id) {
                  const { error } = await sb.from('users').update({ twilio_number: null }).eq('id', targetData.assigned_driver_id);
                  if (error) throw new Error(`users update: ${error.message}`);
              }
              // Clear any existing default, then set the new one.
              const { error: clrErr } = await sb.from('twilio_numbers').update({ is_default: false }).eq('is_default', true);
              if (clrErr) throw new Error(`twilio_numbers update: ${clrErr.message}`);
              const { error: setErr } = await sb.from('twilio_numbers').update({ is_default: true, assigned_driver_id: null }).eq('id', numberId);
              if (setErr) throw new Error(`twilio_numbers update: ${setErr.message}`);
              showToast('Default number updated', 'success');
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const targetDoc = await dbInstance.collection('twilioNumbers').doc(numberId).get();
          if (!targetDoc.exists) {
              showToast('Number not found', 'error');
              return;
          }
          const targetData = targetDoc.data() as TwilioNumber;

          // If the target is currently assigned to a driver, unassign them first —
          // default numbers are the pool-wide fallback and can't also belong to one driver.
          if (targetData.assignedDriverId) {
              await dbInstance.collection('users').doc(targetData.assignedDriverId).update({ twilioNumber: firebase.firestore.FieldValue.delete() } as any);
          }

          // Clear any existing default
          const existingDefaults = await dbInstance.collection('twilioNumbers').where('isDefault', '==', true).get();
          const batch = dbInstance.batch();
          existingDefaults.docs.forEach(d => {
              batch.update(d.ref, { isDefault: false });
          });
          batch.update(dbInstance.collection('twilioNumbers').doc(numberId), { isDefault: true, assignedDriverId: null });
          await batch.commit();
          showToast('Default number updated', 'success');
      } catch (e) {
          console.error(e);
          showToast('Failed to set default', 'error');
      }
  };

  const removeTwilioNumber = async (numberId: string) => {
      try {
          if (USE_SUPABASE) {
              const sb = supabase();
              const { data: numData, error: numErr } = await sb.from('twilio_numbers').select('*').eq('id', numberId).maybeSingle();
              if (numErr) throw new Error(`twilio_numbers read: ${numErr.message}`);
              if (!numData) return;
              if (numData.assigned_driver_id) {
                  const { error } = await sb.from('users').update({ twilio_number: null }).eq('id', numData.assigned_driver_id);
                  if (error) throw new Error(`users update: ${error.message}`);
              }
              const { error } = await sb.from('twilio_numbers').delete().eq('id', numberId);
              if (error) throw new Error(`twilio_numbers delete: ${error.message}`);
              showToast('Number removed from pool', 'success');
              return;
          }
          const { db: dbInstance } = ensureFirebaseInitialized();
          const numDoc = await dbInstance.collection('twilioNumbers').doc(numberId).get();
          if (!numDoc.exists) return;
          const numData = numDoc.data() as TwilioNumber;
          if (numData.assignedDriverId) {
              await dbInstance.collection('users').doc(numData.assignedDriverId).update({ twilioNumber: firebase.firestore.FieldValue.delete() } as any);
          }
          await dbInstance.collection('twilioNumbers').doc(numberId).delete();
          showToast('Number removed from pool', 'success');
      } catch (e) {
          console.error(e);
          showToast('Failed to remove number', 'error');
      }
  };

  // ─── Warehouse Manufacturing (WM + Worker + Packager flow) ────────────────
  // Data model:
  //   InboundLot         — WM logs bulk from truck.
  //   LotAssignment      — WM grants N grams of Lot X to Worker Y.
  //   ProductionRun      — Worker consumes from own holdings (any mix of
  //                        bulk assignments + accumulated crumbs), records
  //                        outputs + new crumbs + waste. PENDING_PACKAGER.
  //   ProductionRun.confirmedAt — Packager confirms → warehouseStock credited.
  //   LotReturn          — Optional Worker end-of-shift handback to WM.
  //
  // Worker "holdings" are DERIVED from state (assignments + runs + returns),
  // not stored on separate docs. See getWorkerHoldings below.

  const createInboundLot = async (input: { name: string; totalGrams: number; notes?: string; packCount?: number; packSizeGrams?: number; countUnit?: string; boxCount?: number; unitsPerBox?: number; skipsPackaging?: boolean }): Promise<string | null> => {
      try {
          if (!input.name.trim()) { showToast('Lot name required', 'error'); return null; }
          if (!(input.totalGrams > 0)) { showToast(input.countUnit ? 'Quantity must be positive' : 'Total grams must be positive', 'error'); return null; }
          const id = `lot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const trimmedNotes = input.notes?.trim();
          // Pack breakdown is stored only when both halves are valid — it's
          // display/audit metadata; totalGrams remains the source of truth.
          const hasPacks = (input.packCount ?? 0) > 0 && (input.packSizeGrams ?? 0) > 0;
          // Count-based intake (Quantity / Boxes): totalGrams holds the count;
          // countUnit flips displays. Boxes stores its breakdown like packs.
          const countUnit = input.countUnit?.trim();
          const hasBoxes = !!countUnit && (input.boxCount ?? 0) > 0 && (input.unitsPerBox ?? 0) > 0;
          const doc: InboundLot = {
              id,
              name: input.name.trim(),
              totalGrams: input.totalGrams,
              remainingGrams: input.totalGrams,
              status: LotStatus.RECEIVED,
              receivedAt: Date.now(),
              receivedBy: getPerformedBy(),
              ...(trimmedNotes ? { notes: trimmedNotes } : {}),
              ...(hasPacks ? { packCount: input.packCount, packSizeGrams: input.packSizeGrams } : {}),
              ...(countUnit ? { countUnit } : {}),
              ...(hasBoxes ? { boxCount: input.boxCount, unitsPerBox: input.unitsPerBox } : {}),
              // Vapes, cartridges — already in their retail packaging, so the
              // Packaging House hand-off is hidden for this lot (Umer).
              ...(input.skipsPackaging ? { skipsPackaging: true } : {}),
          };
          if (USE_SUPABASE) {
              const { error } = await supabase().from('inbound_lots').insert(sbLotRow(doc));
              if (error) throw new Error(`inbound_lots insert: ${error.message}`);
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              await dbInstance.collection('inboundLots').doc(id).set(doc);
          }
          await logWarehouseHistory({
              productId: 'lot',
              productName: doc.name,
              action: WarehouseHistoryAction.PRODUCT_CREATED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: input.totalGrams,
              quantityBefore: 0,
              quantityAfter: input.totalGrams,
              description: `New shipment "${doc.name}" arrived — ${countUnit ? `${input.totalGrams} ${countUnit}` : fmtLb(input.totalGrams)}${hasPacks ? ` (${input.packCount} × ${input.packSizeGrams}g packs)` : ''}${hasBoxes ? ` (${input.boxCount} boxes × ${input.unitsPerBox} ${countUnit})` : ''}`,
          });
          showToast(`Lot logged: ${doc.name}`, 'success');
          return id;
      } catch (e) {
          console.error(e);
          showToast('Failed to log inbound lot', 'error');
          return null;
      }
  };

  // WM grants N grams of Lot X → Worker Y. Transactionally debits the lot's
  // remainingGrams and creates the assignment. Atomic under concurrency.
  const assignBulkToWorker = async (input: {
      lotId: string;
      workerId?: string;
      houseId?: string;
      grams: number;
      notes?: string;
      targetProductIds?: string[];
  }): Promise<string | null> => {
      if (!(input.grams > 0)) { showToast('Grams must be positive', 'error'); return null; }
      // House mode (0080): the material goes to a Packing House pool. The
      // holder columns carry the house's id and name so every ledger line
      // ("on X's table") names the house.
      const house = input.houseId ? packingHouses.find(h => h.id === input.houseId) : undefined;
      if (input.houseId && !house) { showToast('Packing House not found', 'error'); return null; }
      if (house && !house.isActive) { showToast(`${house.name} is archived`, 'error'); return null; }
      const worker = house ? undefined : users.find(u => u.id === input.workerId);
      if (!house && !worker) { showToast('Worker not found', 'error'); return null; }
      // Anyone who can open the Packaging House may receive material — staff,
      // legacy packagers, and the WM / Admin / Super Admin sending to
      // themselves ("Me"). See services/warehousePolicy.ts.
      if (worker && !canReceivePackagingMaterial(worker.role)) {
          showToast(`${worker.name} can't receive Packaging House material`, 'error');
          return null;
      }
      const holder = house ? { id: house.id, name: house.name } : { id: worker!.id, name: worker!.name };

      const assignmentId = `asn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const trimmedNotes = input.notes?.trim();

      try {
          let lotName = '';
          let lotCountUnit: string | undefined;

          if (USE_SUPABASE) {
              const res = await sbWarehouseRpc<{ lotName: string; countUnit: string | null }>('warehouse_assign_bulk', {
                  p_assignment_id: assignmentId,
                  p_lot_id: input.lotId,
                  p_worker_id: house ? null : holder.id,
                  p_worker_name: house ? null : holder.name,
                  p_grams: input.grams,
                  p_notes: trimmedNotes || null,
                  p_assigned_by: getPerformedBy(),
                  p_assigned_at: isoFromMs(Date.now()),
                  p_house_id: house?.id ?? null,
                  p_house_name: house?.name ?? null,
              });
              lotName = res.lotName;
              lotCountUnit = res.countUnit ?? undefined;
              // The products to make (0117) — a plain column on the grant, written after the hand-off.
              if (input.targetProductIds?.length) {
                  const { error: tErr } = await supabase().from('lot_assignments')
                      .update({ target_product_ids: input.targetProductIds }).eq('id', assignmentId);
                  if (tErr) console.warn('target products not saved:', tErr.message);
              }
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const lotRef = dbInstance.collection('inboundLots').doc(input.lotId);
              const asnRef = dbInstance.collection('lotAssignments').doc(assignmentId);

              await dbInstance.runTransaction(async (tx) => {
                  const lotSnap = await tx.get(lotRef);
                  if (!lotSnap.exists) throw new Error('Lot no longer exists');
                  const lot = lotSnap.data() as InboundLot;
                  if (lot.status === LotStatus.DEPLETED || lot.status === LotStatus.CLOSED_WITH_VARIANCE) {
                      throw new Error('Lot is closed');
                  }
                  if (input.grams > lot.remainingGrams + 0.001) {
                      throw new Error(lot.countUnit
                          ? `Only ${Math.round(lot.remainingGrams)} ${lot.countUnit} remaining unassigned`
                          : `Only ${fmtLb(lot.remainingGrams)} remaining unassigned`);
                  }
                  lotName = lot.name;
                  lotCountUnit = lot.countUnit;

                  tx.update(lotRef, {
                      remainingGrams: firebase.firestore.FieldValue.increment(-input.grams),
                      status: LotStatus.IN_PRODUCTION,
                  });
                  const doc: LotAssignment = {
                      id: assignmentId,
                      lotId: lot.id,
                      lotName: lot.name,
                      workerId: holder.id,
                      workerName: holder.name,
                      ...(house ? { houseId: house.id, houseName: house.name } : {}),
                      grantedGrams: input.grams,
                      remainingGrams: input.grams,
                      ...(lot.countUnit ? { countUnit: lot.countUnit } : {}),
                      status: AssignmentStatus.ACTIVE,
                      assignedAt: Date.now(),
                      assignedBy: getPerformedBy(),
                      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
                  };
                  tx.set(asnRef, doc);
              });
          }

          const qtyLabel = lotCountUnit ? `${input.grams} ${lotCountUnit}` : fmtLb(input.grams);
          await logWarehouseHistory({
              productId: 'assignment',
              productName: `${lotName} → ${holder.name}`,
              action: WarehouseHistoryAction.STOCK_EDITED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: -input.grams,
              quantityBefore: 0,
              quantityAfter: 0,
              // Same words the Warehouse Reports activity log uses, so the Full
              // Activity Log reads as one story (pounds, not raw grams).
              description: `Warehouse gave ${holder.name} ${qtyLabel} of ${lotName} to work on`,
          });
          showToast(`Gave ${qtyLabel} to ${holder.name}`, 'success');
          return assignmentId;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to assign bulk', 'error');
          return null;
      }
  };

  // Count-based run: every source drawn from one count-based lot family —
  // history lines then read "20 bundles", not "20.00g".
  const runCountUnitFor = (sources: InputSource[]): string | undefined => {
      const cu = (id: string) => inboundLots.find(l => l.id === id)?.countUnit;
      return sources.length > 0 && sources.every(s => cu(s.lotId) && cu(s.lotId) === cu(sources[0].lotId))
          ? cu(sources[0].lotId) : undefined;
  };

  // Worker records a production run. Multi-source input: any mix of bulk
  // (from an active assignment) + crumbs (accumulated from prior runs, per
  // source lot). Outputs are PENDING_PACKAGER — nothing hits warehouseStock
  // until a Packager confirms via confirmProductionRun.
  // ─── Direct warehouse assignment (migration 0056) ────────────────────────
  // "Send to Store / Driver" on the Warehouse page. The packs are built in the
  // same pack-making step the Packaging House uses, then ONE Postgres call
  // (warehouse_direct_assign) debits the lot by exactly what the packs + waste
  // used, inserts the run already CONFIRMED, credits Product.warehouseStock
  // and creates the pending transfer the store / driver confirms — the same
  // rows a Packaging House batch + Inventory Ops transfer would have written,
  // so nothing downstream can tell the routes apart except the stamps.
  // Partial assignment is the default: whatever the packs don't use never
  // leaves the shelf. The function refuses more than the lot holds.
  const directAssignFromWarehouse = async (input: {
      lotId: string;
      destinationType: 'store' | 'driver';
      destinationId: string;
      outputs: ProductionOutput[];
      wasteGrams: number;
      notes?: string;
  }): Promise<string | null> => {
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }
      if (!canAssignDirectFromWarehouse(currentUser.role)) {
          showToast('Only a warehouse manager, manager or admin can assign stock directly from the warehouse', 'error');
          return null;
      }
      if (!USE_SUPABASE) {
          // The Firestore fallback line has no atomic multi-collection write for
          // this; the Packaging House route stays available there.
          showToast('Direct assignment needs the Postgres backend — use Send to Packaging House here', 'error');
          return null;
      }
      const lot = inboundLots.find(l => l.id === input.lotId);
      if (!lot) { showToast('Lot not found', 'error'); return null; }
      const dest = users.find(u => u.id === input.destinationId);
      if (!dest) { showToast(`Pick a ${input.destinationType} first`, 'error'); return null; }
      if (input.outputs.length === 0) { showToast('Stock cannot be assigned unpacked — add at least one pack row', 'error'); return null; }
      for (const o of input.outputs) {
          if (!(o.qty > 0) || !(o.unitGrams > 0)) {
              showToast('Each pack row needs a positive count and a positive size', 'error'); return null;
          }
      }
      if (!(input.wasteGrams >= 0)) { showToast('Waste cannot be negative', 'error'); return null; }

      const packGrams = input.outputs.reduce((s, o) => s + o.qty * o.unitGrams, 0);
      const used = packGrams + input.wasteGrams;
      if (used > lot.remainingGrams + 0.001) {
          showToast(`${lot.name}: only ${lot.countUnit ? `${lot.remainingGrams} ${lot.countUnit}` : fmtLb(lot.remainingGrams)} on shelf`, 'error');
          return null;
      }

      const now = Date.now();
      const runId = `run_${now}_${Math.random().toString(36).substr(2, 9)}`;
      const txId = `t${now}-${Math.floor(Math.random() * 10000)}`;
      const trimmedNotes = input.notes?.trim();
      const isStore = input.destinationType === 'store';
      const performedBy = getPerformedBy();

      // The run consumes exactly packs + waste straight off the shelf
      // (`bulk_direct`), so every lot ledger — Lot detail, Warehouse Reports,
      // family tree — subtracts the same number the function debits.
      const runDoc: ProductionRun = {
          id: runId,
          workerId: currentUser.id,
          workerName: currentUser.name,
          performedAt: now,
          performedBy,
          inputSources: [{ kind: 'bulk_direct', lotId: lot.id, lotName: lot.name, grams: used }],
          totalGramsConsumed: used,
          outputs: input.outputs,
          crumbsGrams: 0,
          wasteGrams: input.wasteGrams,
          variance: 0,
          status: RunStatus.CONFIRMED,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      };
      // Merged per product, the way sbWriteTransaction stores items.
      const merged = new Map<string, number>();
      for (const o of input.outputs) merged.set(o.productId, (merged.get(o.productId) || 0) + o.qty);
      const items = Array.from(merged, ([productId, quantity]) => ({
          productId, quantity, productName: input.outputs.find(o => o.productId === productId)?.productName,
      }));

      try {
          const res = await sbWarehouseRpc<{ transactionId: string; destinationName: string; used: number; remaining: number }>(
              'warehouse_direct_assign',
              {
                  p_run: sbRunRow(runDoc),
                  p_lot_id: lot.id,
                  p_tx: {
                      id: txId,
                      type: isStore ? TransactionType.ASSIGN_TO_STORE : TransactionType.ASSIGN_TO_DRIVER,
                      status: isStore ? TransactionStatus.PENDING_STORE : TransactionStatus.PENDING_DRIVER,
                      store_id: isStore ? dest.id : null,
                      driver_id: isStore ? null : dest.id,
                  },
                  p_items: items,
                  p_at: isoFromMs(now),
                  p_by: performedBy,
              },
          );
          const packCount = items.reduce((s, i) => s + i.quantity, 0);
          showToast(`${packCount} pack${packCount === 1 ? '' : 's'} sent to ${res.destinationName} — waiting for them to confirm receipt`, 'success');
          return res.transactionId;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Direct assignment failed', 'error');
          return null;
      }
  };

  // Pack a lot straight onto the Storage House shelf (migration 0065). Same
  // move as a direct assignment minus the destination: the packs are made and
  // become available stock on the spot, because nothing left the building and
  // so there is nobody to confirm receipt.
  const packIntoStorageHouse = async (input: {
      lotId: string;
      outputs: ProductionOutput[];
      wasteGrams: number;
      notes?: string;
  }): Promise<string | null> => {
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }
      if (!canAssignDirectFromWarehouse(currentUser.role)) {
          showToast('Only a warehouse manager, manager or admin can pack straight into the Storage House', 'error');
          return null;
      }
      if (!USE_SUPABASE) {
          showToast('The Storage House needs the Postgres backend — use Send to Packaging House here', 'error');
          return null;
      }
      const lot = inboundLots.find(l => l.id === input.lotId);
      if (!lot) { showToast('Lot not found', 'error'); return null; }
      if (input.outputs.length === 0) { showToast('Stock cannot be stored unpacked — add at least one pack row', 'error'); return null; }
      for (const o of input.outputs) {
          if (!(o.qty > 0) || !(o.unitGrams > 0)) {
              showToast('Each pack row needs a positive count and a positive size', 'error'); return null;
          }
      }
      if (!(input.wasteGrams >= 0)) { showToast('Waste cannot be negative', 'error'); return null; }

      const used = input.outputs.reduce((s, o) => s + o.qty * o.unitGrams, 0) + input.wasteGrams;
      if (used > lot.remainingGrams + 0.001) {
          showToast(`${lot.name}: only ${lot.countUnit ? `${lot.remainingGrams} ${lot.countUnit}` : fmtLb(lot.remainingGrams)} on shelf`, 'error');
          return null;
      }

      const now = Date.now();
      const runId = `run_${now}_${Math.random().toString(36).substr(2, 9)}`;
      const trimmedNotes = input.notes?.trim();
      const performedBy = getPerformedBy();
      const runDoc: ProductionRun = {
          id: runId,
          workerId: currentUser.id,
          workerName: currentUser.name,
          performedAt: now,
          performedBy,
          inputSources: [{ kind: 'bulk_direct', lotId: lot.id, lotName: lot.name, grams: used }],
          totalGramsConsumed: used,
          outputs: input.outputs,
          crumbsGrams: 0,
          wasteGrams: input.wasteGrams,
          variance: 0,
          status: RunStatus.CONFIRMED,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      };
      const merged = new Map<string, number>();
      for (const o of input.outputs) merged.set(o.productId, (merged.get(o.productId) || 0) + o.qty);
      const items = Array.from(merged, ([productId, quantity]) => ({
          productId, quantity, productName: input.outputs.find(o => o.productId === productId)?.productName,
      }));

      try {
          const res = await sbWarehouseRpc<{ runId: string; summary: string; remaining: number }>(
              'warehouse_direct_to_storage',
              {
                  p_run: sbRunRow(runDoc),
                  p_lot_id: lot.id,
                  p_items: items,
                  p_at: isoFromMs(now),
                  p_by: performedBy,
              },
          );
          const packCount = items.reduce((s, i) => s + i.quantity, 0);
          showToast(`${packCount} pack${packCount === 1 ? '' : 's'} stored — available in the Storage House now`, 'success');
          return res.runId;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Could not store these packs', 'error');
          return null;
      }
  };

  // Re-send packs a store / driver refused (migration 0057). The packs already
  // exist on the warehouse balance — nothing is packed again — so this only
  // creates the transfer, with the ceiling ("a batch can never have more packs
  // out than it made") enforced inside the function where it cannot go stale.
  const resendDirectPacks = async (input: {
      runId: string;
      destinationType: 'store' | 'driver';
      destinationId: string;
      items: { productId: string; quantity: number }[];
  }): Promise<string | null> => {
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }
      if (!canAssignDirectFromWarehouse(currentUser.role)) {
          showToast('Only a warehouse manager, manager or admin can re-send warehouse packs', 'error');
          return null;
      }
      if (!USE_SUPABASE) {
          showToast('Re-sending packs needs the Postgres backend', 'error');
          return null;
      }
      const items = input.items.filter(i => i.quantity > 0);
      if (items.length === 0) { showToast('Nothing to send', 'error'); return null; }
      const isStore = input.destinationType === 'store';
      const now = Date.now();
      const txId = `t${now}-${Math.floor(Math.random() * 10000)}`;
      try {
          const res = await sbWarehouseRpc<{ transactionId: string; packs: number; destinationName: string }>(
              'warehouse_resend_packs',
              {
                  p_run_id: input.runId,
                  p_tx: {
                      id: txId,
                      type: isStore ? TransactionType.ASSIGN_TO_STORE : TransactionType.ASSIGN_TO_DRIVER,
                      store_id: isStore ? input.destinationId : null,
                      driver_id: isStore ? null : input.destinationId,
                  },
                  p_items: items,
                  p_at: isoFromMs(now),
                  p_by: getPerformedBy(),
              },
          );
          showToast(`${res.packs} pack${res.packs === 1 ? '' : 's'} sent to ${res.destinationName} — waiting for them to confirm`, 'success');
          return res.transactionId;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Could not re-send these packs', 'error');
          return null;
      }
  };

  const createProductionRun = async (input: {
      inputSources: InputSource[];
      outputs: ProductionOutput[];
      crumbsGrams: number;
      wasteGrams: number;
      notes?: string;
      // Optional — set when a WM/admin/super-admin is logging a batch on
      // behalf of a specific worker. Ignored when the current user IS the
      // worker (they act as themselves). Requires the caller to have a
      // higher-tier role than Worker for this to take effect.
      actingAsWorkerId?: string;
      // House mode (0080): the Packing House pool the batch draws from.
      houseId?: string;
  }): Promise<string | null> => {
      if (input.inputSources.length === 0) { showToast('At least one input source required', 'error'); return null; }
      for (const s of input.inputSources) {
          if (!(s.grams > 0)) { showToast('Every input source needs positive grams', 'error'); return null; }
      }
      if (input.outputs.length === 0) { showToast('At least one output SKU required', 'error'); return null; }
      for (const o of input.outputs) {
          if (!(o.qty > 0) || !(o.unitGrams > 0)) {
              showToast('Each output needs positive qty and unit grams', 'error'); return null;
          }
      }
      if (!(input.crumbsGrams >= 0) || !(input.wasteGrams >= 0)) {
          showToast('Crumbs and waste cannot be negative', 'error'); return null;
      }
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }

      // Resolve which worker this run belongs to. Non-Worker roles may act
      // on a picked worker's behalf. A Worker always acts as themselves.
      const canActAsOthers = currentUser.role !== UserRole.WORKER
          && currentUser.role !== UserRole.PACKAGER;
      const targetWorker = (canActAsOthers && input.actingAsWorkerId)
          ? users.find(u => u.id === input.actingAsWorkerId)
          : currentUser;
      if (!targetWorker) { showToast('Selected worker not found', 'error'); return null; }
      // House mode (0080): a packager must be a member of the house; the WM
      // tier may log a batch on any house's behalf. The run keeps the person
      // in workerId and the pool in houseId.
      const house = input.houseId ? packingHouses.find(h => h.id === input.houseId) : undefined;
      if (input.houseId && !house) { showToast('Packing House not found', 'error'); return null; }
      if (house && !canActAsOthers && !isHouseMember(currentUser.id, house.id, packingHouseMembers)) {
          showToast(`You are not a member of ${house.name}`, 'error'); return null;
      }

      const totalGramsConsumed = input.inputSources.reduce((s, i) => s + i.grams, 0);
      const productGrams = input.outputs.reduce((s, o) => s + o.qty * o.unitGrams, 0);
      // Variance stays for schema compat but is always 0 with the new
      // leftover-inference model — the leftover soaks up whatever's unaccounted.
      const variance = 0;

      const runId = `run_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const trimmedNotes = input.notes?.trim();

      try {
          // Group inputs by kind so the transaction can debit the right doc
          // for each. Crumbs inputs don't touch any doc — the run itself is
          // the audit event that "consumed" them (holdings are derived).
          const bulkByAssignment = new Map<string, number>();
          const bulkDirectByLot = new Map<string, number>();
          for (const s of input.inputSources) {
              if (s.kind === 'bulk') {
                  bulkByAssignment.set(s.assignmentId, (bulkByAssignment.get(s.assignmentId) || 0) + s.grams);
              } else if (s.kind === 'bulk_direct') {
                  bulkDirectByLot.set(s.lotId, (bulkDirectByLot.get(s.lotId) || 0) + s.grams);
              }
          }

          // Leftover is always INFERRED from consumed − produced − waste.
          // There is no "crumbs" concept exposed to the user anymore.
          //   Single source (1 lot) → leftover goes back to the source bulk.
          //   Multi source        → leftover becomes a new named material
          //                         (derived lot, named after the primary
          //                         output product).
          // A negative "leftover" means the user claimed to make + waste more
          // than they consumed — that's an error, not a variance.
          const nCrumbSources = input.inputSources.filter(s => s.kind === 'crumbs').length;
          const nBulkSources = bulkByAssignment.size + bulkDirectByLot.size;
          const totalSources = nCrumbSources + nBulkSources;
          const isSingleSource = nCrumbSources === 0 && nBulkSources === 1;
          const rawLeftover = totalGramsConsumed - productGrams - input.wasteGrams;
          if (rawLeftover < -0.01) {
              throw new Error(`Products + waste (${fmtLb(productGrams + input.wasteGrams)}) exceed material used (${fmtLb(totalGramsConsumed)}). Please recheck.`);
          }
          const inferredLeftover = Math.max(0, rawLeftover);
          const collapseCrumbsBackToBulk = isSingleSource && inferredLeftover > 0.01;
          const createDerivedFromMixedCrumbs =
              !isSingleSource && totalSources > 1 && inferredLeftover > 0.01;

          // Pre-generate ids for the auto-derived lot / assignment so the
          // whole thing stays inside one transaction.
          const derivedLotId = createDerivedFromMixedCrumbs ? `lot_derived_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : '';
          const derivedAsnId = createDerivedFromMixedCrumbs ? `asn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : '';
          // Derived lot's name comes from the first output product — e.g.
          // "Pink Kush leftover". If no output product (edge case), fall back
          // to a generic "Mixed leftover".
          const firstOutputName = input.outputs[0]?.productName;
          const derivedLotName = firstOutputName ? `${firstOutputName} leftover` : 'Mixed leftover';
          // Unique source lots (union of bulk assignments' lots + direct lots + crumb sources).
          const sourceLotById = new Map<string, string>();
          for (const s of input.inputSources) sourceLotById.set(s.lotId, s.lotName);

          // Shelf-draw is PARKED (services/warehousePolicy.ts): the Packaging
          // House only consumes material that was SENT to the actor, so
          // bulk_direct (straight off the shelf) and other people's
          // assignments are refused for everyone — Admin / Super Admin / WM
          // included. With the flag on, privileged roles regain the old path:
          // consume from ANY assignment (that worker's remainingGrams is
          // debited, the run still stamped with the admin) or from the shelf.
          const isPrivilegedActor = PACKAGING_HOUSE_SHELF_DRAW && (
              isSuperTier(currentUser.role)
              || currentUser.role === UserRole.ADMIN
              || currentUser.role === UserRole.WAREHOUSE_MANAGER);
          if (!isPrivilegedActor && bulkDirectByLot.size > 0) {
              throw new Error("Material still on the warehouse shelf can't be used here — send it to the Packaging House first");
          }

          // Multi-source with positive leftover: a derived lot named after the
          // primary output product.
          //   Worker actor  → new lot's remainingGrams = 0 and it's routed to
          //                    the worker via a fresh assignment.
          //   Admin/WM actor → (shelf-draw flag ON only) new lot stays on the
          //                    shelf (unassigned) so it can be assigned to a
          //                    worker later or drawn from directly next time.
          //                    With the flag off they are routed like a worker
          //                    — the leftover stays on their own table.
          // Per-parent grams in the leftover = (parent's share of the run's
          // consumption) × inferredLeftover. Preserves the proportional
          // attribution so lineage totals add up.
          const derivedParents = Array.from(sourceLotById.entries()).map(([id, name]) => {
              const fromParent = input.inputSources
                  .filter(s => s.lotId === id)
                  .reduce((acc, s) => acc + (s.grams || 0), 0);
              const share = totalGramsConsumed > 0 ? fromParent / totalGramsConsumed : 0;
              return { id, name, grams: share * inferredLeftover };
          });
          const derivedLot: InboundLot | null = createDerivedFromMixedCrumbs ? {
              id: derivedLotId,
              name: derivedLotName,
              totalGrams: inferredLeftover,
              remainingGrams: isPrivilegedActor ? inferredLeftover : 0,
              status: LotStatus.IN_PRODUCTION,
              receivedAt: Date.now(),
              receivedBy: getPerformedBy(),
              origin: 'worker_derived',
              derivedFrom: {
                  parents: derivedParents,
                  workerId: targetWorker.id,
                  workerName: targetWorker.name,
                  ...(house ? { houseId: house.id, houseName: house.name } : {}),
                  createdAt: Date.now(),
                  fromProductionRunId: runId,
                  ...(firstOutputName ? { fromProductName: firstOutputName } : {}),
              },
          } : null;
          const derivedAsn: LotAssignment | null = (derivedLot && !isPrivilegedActor) ? {
              id: derivedAsnId,
              lotId: derivedLotId,
              lotName: derivedLotName,
              // The leftover goes back to the POOL: on a house grant the
              // holder is the house, exactly like the WM's hand-off.
              workerId: house ? house.id : targetWorker.id,
              workerName: house ? house.name : targetWorker.name,
              ...(house ? { houseId: house.id, houseName: house.name } : {}),
              grantedGrams: inferredLeftover,
              remainingGrams: inferredLeftover,
              status: AssignmentStatus.ACTIVE,
              assignedAt: Date.now(),
              assignedBy: getPerformedBy(),
          } : null;

          // Leftover is inferred, not stored as crumbs. crumbsGrams stays at 0
          // for every new run — the leftover has already been accounted for
          // (single-source → back to bulk, multi-source → the derived lot).
          const runDoc: ProductionRun = {
              id: runId,
              workerId: targetWorker.id,
              workerName: targetWorker.name,
              ...(house ? { houseId: house.id, houseName: house.name } : {}),
              performedAt: Date.now(),
              performedBy: getPerformedBy(),
              inputSources: input.inputSources,
              totalGramsConsumed,
              outputs: input.outputs,
              crumbsGrams: 0,
              wasteGrams: input.wasteGrams,
              variance,
              status: RunStatus.PENDING_PACKAGER,
              ...(trimmedNotes ? { notes: trimmedNotes } : {}),
          };

          if (USE_SUPABASE) {
              await sbWarehouseRpc<null>('warehouse_create_run', {
                  p_run: sbRunRow(runDoc),
                  p_bulk_by_assignment: Object.fromEntries(bulkByAssignment),
                  p_bulk_direct_by_lot: Object.fromEntries(bulkDirectByLot),
                  p_leftover: inferredLeftover,
                  p_collapse_back: collapseCrumbsBackToBulk,
                  p_target_worker_id: targetWorker.id,
                  p_is_privileged: isPrivilegedActor,
                  p_derived_lot: derivedLot ? sbLotRow(derivedLot) : null,
                  p_derived_assignment: derivedAsn ? sbAssignmentRow(derivedAsn) : null,
                  p_house_id: house?.id ?? null,
              });
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              await dbInstance.runTransaction(async (tx) => {
                  // Read every assignment being consumed from.
                  const asnRefs = Array.from(bulkByAssignment.keys()).map(id => dbInstance.collection('lotAssignments').doc(id));
                  const asnSnaps = await Promise.all(asnRefs.map(r => tx.get(r)));
                  for (let i = 0; i < asnSnaps.length; i++) {
                      if (!asnSnaps[i].exists) throw new Error('Assignment no longer exists');
                      const asn = asnSnaps[i].data() as LotAssignment;
                      if (!isPrivilegedActor && asn.workerId !== targetWorker.id) {
                          throw new Error(`${asn.lotName} wasn't sent to ${targetWorker.name} — only material sent to you can be used`);
                      }
                      if (asn.status !== AssignmentStatus.ACTIVE) throw new Error(`Assignment ${asn.lotName} is closed`);
                      const consumeAmount = bulkByAssignment.get(asnRefs[i].id)!;
                      if (consumeAmount > asn.remainingGrams + 0.001) {
                          throw new Error(`Assignment ${asn.lotName}: only ${asn.remainingGrams.toFixed(1)}g remaining`);
                      }
                  }

                  // Read every lot being consumed from directly (admin/WM mode).
                  const directLotIds = Array.from(bulkDirectByLot.keys());
                  const lotRefs = directLotIds.map(id => dbInstance.collection('inboundLots').doc(id));
                  const lotSnaps = await Promise.all(lotRefs.map(r => tx.get(r)));
                  for (let i = 0; i < lotSnaps.length; i++) {
                      if (!lotSnaps[i].exists) throw new Error('Lot no longer exists');
                      const lot = lotSnaps[i].data() as InboundLot;
                      if (lot.status === LotStatus.DEPLETED || lot.status === LotStatus.CLOSED_WITH_VARIANCE) {
                          throw new Error(`Lot ${lot.name} is closed`);
                      }
                      const consumeAmount = bulkDirectByLot.get(directLotIds[i])!;
                      if (consumeAmount > lot.remainingGrams + 0.001) {
                          throw new Error(`Lot ${lot.name}: only ${lot.remainingGrams.toFixed(1)}g on shelf`);
                      }
                  }

                  // Debit each assignment.
                  for (let i = 0; i < asnRefs.length; i++) {
                      const consumeAmount = bulkByAssignment.get(asnRefs[i].id)!;
                      // Single-source-from-assignment: leftover goes back to this same assignment.
                      const creditBack = (collapseCrumbsBackToBulk && bulkByAssignment.size === 1) ? inferredLeftover : 0;
                      tx.update(asnRefs[i], {
                          remainingGrams: firebase.firestore.FieldValue.increment(-consumeAmount + creditBack),
                      });
                  }

                  // Debit each lot (direct mode).
                  for (let i = 0; i < lotRefs.length; i++) {
                      const consumeAmount = bulkDirectByLot.get(directLotIds[i])!;
                      // Single-source-from-lot-direct: leftover goes back to this same lot.
                      const creditBack = (collapseCrumbsBackToBulk && bulkDirectByLot.size === 1) ? inferredLeftover : 0;
                      tx.update(lotRefs[i], {
                          remainingGrams: firebase.firestore.FieldValue.increment(-consumeAmount + creditBack),
                          status: LotStatus.IN_PRODUCTION,
                      });
                  }

                  if (derivedLot) {
                      tx.set(dbInstance.collection('inboundLots').doc(derivedLotId), derivedLot);
                      if (derivedAsn) tx.set(dbInstance.collection('lotAssignments').doc(derivedAsnId), derivedAsn);
                  }

                  // Write the run — PENDING_PACKAGER.
                  tx.set(dbInstance.collection('productionRuns').doc(runId), runDoc);
              });
          }

          const onBehalf = targetWorker.id !== currentUser.id ? ` (logged by ${currentUser.name})` : '';
          const atHouse = house ? ` at ${house.name}` : '';
          await logWarehouseHistory({
              productId: 'run',
              productName: `Run by ${targetWorker.name}${atHouse}${onBehalf}`,
              action: WarehouseHistoryAction.STOCK_EDITED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: -totalGramsConsumed,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Production run for ${targetWorker.name}${onBehalf}: consumed ${(() => { const cu = runCountUnitFor(input.inputSources); return cu ? `${totalGramsConsumed} ${cu}` : fmtLb(totalGramsConsumed); })()} across ${input.inputSources.length} source(s), produced ${input.outputs.length} SKU(s) — awaiting packager confirmation`,
          });
          showToast('Production run recorded', 'success');
          return runId;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to record production run', 'error');
          return null;
      }
  };

  // Resolve a product's doc ref by its `id` field. Handles the legacy
  // inconsistency where ~9 products have `data.id !== docId` (older CSV
  // import used auto-generated docIds but wrote a separate `id` field, and
  // the codebase-wide `{ id: d.id, ...d.data() }` spread ends up using the
  // field value). Fast path tries the docId directly; slow path queries.
  const resolveProductRef = async (productId: string) => {
      const { db: dbInstance } = ensureFirebaseInitialized();
      const byDoc = dbInstance.collection('products').doc(productId);
      const snap = await byDoc.get();
      if (snap.exists) return byDoc;
      const q = await dbInstance.collection('products').where('id', '==', productId).limit(1).get();
      return q.docs[0]?.ref || null;
  };

  // Packager reviews finished products from a Worker and approves them. This
  // used to be a two-step (PENDING → PACKED → CONFIRMED) with a separate
  // "transfer to inventory" gate. Per Stephen's feedback the packager check
  // IS the final gate — approval now goes straight to stock in a single txn.
  //
  // We still keep the fields `confirmedAt` and `transferredAt` populated (both
  // set to the same timestamp) so the Recently-added-to-stock timeline and any
  // reports that read `transferredAt` keep working without changes. Legacy
  // rows already in PACKED state are cleaned up separately via
  // transferPackedToInventory (still exported for that one-shot migration).
  const confirmProductionRun = async (runId: string) => {
      try {
          let inputSources: InputSource[] = [];
          let outputs: ProductionOutput[] = [];
          let workerName = '';

          if (USE_SUPABASE) {
              const res = await sbWarehouseRpc<{ inputSources: InputSource[]; outputs: ProductionOutput[]; workerName: string }>(
                  'warehouse_confirm_run',
                  { p_run_id: runId, p_at: isoFromMs(Date.now()), p_by: getPerformedBy() },
              );
              inputSources = res.inputSources;
              outputs = res.outputs;
              workerName = res.workerName;
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const runRef = dbInstance.collection('productionRuns').doc(runId);

              // Validate products still exist before approving. Doing it outside
              // the txn is fine because the txn re-checks existence with tx.get().
              const runSnap = await runRef.get();
              if (!runSnap.exists) throw new Error('Run not found');
              const runData = runSnap.data() as ProductionRun;
              const prodRefs = await Promise.all(runData.outputs.map(o => resolveProductRef(o.productId)));
              for (let i = 0; i < prodRefs.length; i++) {
                  if (!prodRefs[i]) throw new Error(`Product no longer exists: ${runData.outputs[i].productName}`);
              }

              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(runRef);
                  if (!snap.exists) throw new Error('Run not found');
                  const run = snap.data() as ProductionRun;
                  if (run.status !== RunStatus.PENDING_PACKAGER) {
                      throw new Error(`Run is already ${run.status}`);
                  }

                  const prodSnaps = await Promise.all(prodRefs.map(r => tx.get(r!)));
                  for (let i = 0; i < prodSnaps.length; i++) {
                      if (!prodSnaps[i].exists) throw new Error(`Product no longer exists: ${run.outputs[i].productName}`);
                  }

                  // Credit warehouseStock for every output. FieldValue.increment
                  // keeps this safe under concurrent approves on the same product.
                  for (let i = 0; i < prodRefs.length; i++) {
                      tx.update(prodRefs[i]!, {
                          warehouseStock: firebase.firestore.FieldValue.increment(run.outputs[i].qty),
                      });
                  }

                  const now = Date.now();
                  const by = getPerformedBy();
                  tx.update(runRef, {
                      status: RunStatus.CONFIRMED,
                      confirmedAt: now,
                      confirmedBy: by,
                      transferredAt: now,
                      transferredBy: by,
                  });

                  outputs = run.outputs;
                  workerName = run.workerName;
              });
              inputSources = runData.inputSources;
          }

          const approvedUnit = runCountUnitFor(inputSources);
          for (const o of outputs) {
              const product = products.find(p => p.id === o.productId);
              await logWarehouseHistory({
                  productId: o.productId,
                  productName: o.productName || product?.name || 'Unknown',
                  action: WarehouseHistoryAction.STOCK_EDITED,
                  timestamp: Date.now(),
                  performedBy: getPerformedBy(),
                  quantityChange: o.qty,
                  quantityBefore: product?.warehouseStock || 0,
                  quantityAfter: (product?.warehouseStock || 0) + o.qty,
                  description: `Packager approved ${o.qty} × ${approvedUnit ? `${o.unitGrams} ${approvedUnit}` : fmtLb(o.unitGrams)} ${o.productName} from Worker ${workerName} — added to stock`,
              });
          }
          showToast('Approved — added to stock', 'success');
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to confirm run', 'error');
      }
  };

  // Move a PACKED run into Product.warehouseStock. This is the final gate —
  // only after this action do finished goods become visible in the app's
  // inventory (Products page, StoreStockSearch, etc.). Any authenticated
  // packager or admin can perform this step (usually the same packager who
  // did the confirm, in one workflow session).
  const transferPackedToInventory = async (runId: string) => {
      try {
          let outputs: ProductionOutput[] = [];
          let workerName = '';

          if (USE_SUPABASE) {
              const res = await sbWarehouseRpc<{ outputs: ProductionOutput[]; workerName: string }>(
                  'warehouse_transfer_run',
                  { p_run_id: runId, p_at: isoFromMs(Date.now()), p_by: getPerformedBy() },
              );
              outputs = res.outputs;
              workerName = res.workerName;
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const runRef = dbInstance.collection('productionRuns').doc(runId);

              const runSnap = await runRef.get();
              if (!runSnap.exists) throw new Error('Run not found');
              const runData = runSnap.data() as ProductionRun;
              const prodRefs = await Promise.all(runData.outputs.map(o => resolveProductRef(o.productId)));
              for (let i = 0; i < prodRefs.length; i++) {
                  if (!prodRefs[i]) throw new Error(`Product no longer exists: ${runData.outputs[i].productName}`);
              }

              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(runRef);
                  if (!snap.exists) throw new Error('Run not found');
                  const run = snap.data() as ProductionRun;
                  if (run.status !== RunStatus.PACKED) {
                      throw new Error(`Only PACKED runs can be transferred (this is ${run.status})`);
                  }

                  const prodSnaps = await Promise.all(prodRefs.map(r => tx.get(r!)));
                  for (let i = 0; i < prodSnaps.length; i++) {
                      if (!prodSnaps[i].exists) throw new Error(`Product no longer exists: ${run.outputs[i].productName}`);
                  }

                  // Credit warehouseStock for every output. This is the moment
                  // finished goods become inventory. FieldValue.increment keeps
                  // this safe under concurrent transfers on the same product.
                  for (let i = 0; i < prodRefs.length; i++) {
                      tx.update(prodRefs[i]!, {
                          warehouseStock: firebase.firestore.FieldValue.increment(run.outputs[i].qty),
                      });
                  }

                  tx.update(runRef, {
                      status: RunStatus.CONFIRMED,
                      transferredAt: Date.now(),
                      transferredBy: getPerformedBy(),
                  });

                  outputs = run.outputs;
                  workerName = run.workerName;
              });
          }

          for (const o of outputs) {
              const product = products.find(p => p.id === o.productId);
              await logWarehouseHistory({
                  productId: o.productId,
                  productName: o.productName || product?.name || 'Unknown',
                  action: WarehouseHistoryAction.STOCK_EDITED,
                  timestamp: Date.now(),
                  performedBy: getPerformedBy(),
                  quantityChange: o.qty,
                  quantityBefore: product?.warehouseStock || 0,
                  quantityAfter: (product?.warehouseStock || 0) + o.qty,
                  description: `Transferred ${o.qty} × ${fmtLb(o.unitGrams)} ${o.productName} to warehouse inventory (from Worker ${workerName})`,
              });
          }
          showToast('Transferred to inventory — finished goods credited', 'success');
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to transfer run', 'error');
      }
  };

  // Packager rejects a run with a reason. The bulk stays consumed (already
  // debited from the assignment when the Worker recorded it — you can't
  // un-consume physical material). Worker can then edit the run to fix
  // whatever the Packager flagged and resubmit via updateProductionRun.
  const rejectProductionRun = async (runId: string, reason: string) => {
      try {
          if (USE_SUPABASE) {
              const sb = supabase();
              // Status guard rides on the UPDATE itself, so this stays a single
              // compare-and-set the way the Firestore transaction is. A miss is
              // re-read only to name the status the same way Firestore does.
              const { data, error } = await sb.from('production_runs').update({
                  status: RunStatus.REJECTED,
                  confirmed_at: isoFromMs(Date.now()),
                  confirmed_by: getPerformedBy(),
                  rejection_reason: reason.trim() || 'no reason given',
              }).eq('id', runId).eq('status', RunStatus.PENDING_PACKAGER).select('id');
              if (error) throw new Error(`production_runs update: ${error.message}`);
              if (!data?.length) {
                  const { data: row } = await sb.from('production_runs').select('status').eq('id', runId).maybeSingle();
                  throw new Error(row ? `Run is already ${row.status}` : 'Run not found');
              }
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const runRef = dbInstance.collection('productionRuns').doc(runId);
              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(runRef);
                  if (!snap.exists) throw new Error('Run not found');
                  const run = snap.data() as ProductionRun;
                  if (run.status !== RunStatus.PENDING_PACKAGER) throw new Error(`Run is already ${run.status}`);
                  tx.update(runRef, {
                      status: RunStatus.REJECTED,
                      confirmedAt: Date.now(),
                      confirmedBy: getPerformedBy(),
                      rejectionReason: reason.trim() || 'no reason given',
                  });
              });
          }
          showToast('Run rejected — worker can edit and resubmit', 'info');
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to reject run', 'error');
      }
  };

  // Worker edits their own run (typically after rejection). Inputs are
  // immutable — bulk was physically consumed. Only outputs / crumbs / waste
  // / notes can change. Variance recomputed. Status flips to PENDING_PACKAGER.
  const updateProductionRun = async (runId: string, edits: {
      outputs: ProductionOutput[];
      crumbsGrams: number;
      wasteGrams: number;
      notes?: string;
  }) => {
      if (edits.outputs.length === 0) { showToast('At least one output SKU required', 'error'); return; }
      for (const o of edits.outputs) {
          if (!(o.qty > 0) || !(o.unitGrams > 0)) {
              showToast('Each output needs positive qty and unit grams', 'error'); return;
          }
      }
      if (!(edits.crumbsGrams >= 0) || !(edits.wasteGrams >= 0)) {
          showToast('Crumbs and waste cannot be negative', 'error'); return;
      }
      if (!currentUser) { showToast('Not signed in', 'error'); return; }

      try {
          const trimmedNotes = edits.notes?.trim();

          // Postgres (0082): the edit SETTLES THE POOL. A single-material
          // batch had its leftover credited back to the grant at create time;
          // warehouse_update_run moves the grant by (new leftover − old
          // leftover) in the same transaction as the run rewrite, and refuses
          // when the pool no longer holds enough or when the batch drew from
          // several materials (its leftover is a separate material by then).
          // Firestore keeps the old rule: the edit only rewrites the run.
          let settled: { delta: number; remaining: number | null } | null = null;
          if (USE_SUPABASE) {
              const res = await sbWarehouseRpc<{ delta: number; remaining: number | null }>('warehouse_update_run', {
                  p_run_id: runId,
                  p_outputs: edits.outputs,
                  p_waste: edits.wasteGrams,
                  p_notes: trimmedNotes || null,
                  p_actor_id: currentUser.id,
              });
              settled = { delta: Number(res?.delta ?? 0), remaining: res?.remaining == null ? null : Number(res.remaining) };
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const runRef = dbInstance.collection('productionRuns').doc(runId);

              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(runRef);
                  if (!snap.exists) throw new Error('Run not found');
                  const run = snap.data() as ProductionRun;
                  if (run.workerId !== currentUser.id) throw new Error('You can only edit your own runs');
                  if (run.status !== RunStatus.PENDING_PACKAGER && run.status !== RunStatus.REJECTED) {
                      throw new Error(`Cannot edit a ${run.status} run`);
                  }

                  const update: any = {
                      outputs: edits.outputs,
                      crumbsGrams: 0,
                      wasteGrams: edits.wasteGrams,
                      variance: 0,
                      status: RunStatus.PENDING_PACKAGER,
                      confirmedAt: firebase.firestore.FieldValue.delete(),
                      confirmedBy: firebase.firestore.FieldValue.delete(),
                      rejectionReason: firebase.firestore.FieldValue.delete(),
                  };
                  if (trimmedNotes) update.notes = trimmedNotes;
                  else update.notes = firebase.firestore.FieldValue.delete();

                  tx.update(runRef, update);
              });
          }

          await logWarehouseHistory({
              productId: 'run',
              productName: `Run by ${currentUser.name}`,
              action: WarehouseHistoryAction.STOCK_EDITED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Worker ${currentUser.name} edited run ${runId} — resubmitted for packager confirmation`
                  + (settled && Math.abs(settled.delta) > 0.001
                      ? ` (pool ${settled.delta < 0 ? 'debited' : 'credited'} ${fmtLb(Math.abs(settled.delta))} to match the new packs)`
                      : ''),
          });
          showToast(settled && Math.abs(settled.delta) > 0.001
              ? `Run updated — ${fmtLb(Math.abs(settled.delta))} ${settled.delta < 0 ? 'taken from' : 'returned to'} the pool`
              : 'Run updated — awaiting packager review', 'success');
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to update run', 'error');
      }
  };

  // Optional end-of-shift handback. Worker gives back unused bulk OR
  // accumulated crumbs to WM. Bulk returns credit the lot's remainingGrams;
  // crumb returns are audit-only (crumbs are already attributed to the lot
  // via the run history).
  // ─── Claims (0117): my table ─────────────────────────────────────────────
  // Each one is a single database call; the lists update through the subscription.
  const takePackingClaim = async (input: { assignmentId: string; grams: number }): Promise<PackingClaim | null> => {
      if (!(input.grams > 0)) { showToast('Say how much you are taking', 'error'); return null; }
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }
      try {
          const id = `clm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const row = await sbWarehouseRpc<any>('packing_claim_take', {
              p_id: id, p_assignment_id: input.assignmentId, p_grams: input.grams,
              p_worker_name: currentUser.name, p_at: isoFromMs(Date.now()),
          });
          const claim = sbClaimFromRow(row);
          setPackingClaims(prev => [claim, ...prev.filter(c => c.id !== claim.id)]);
          return claim;
      } catch (e: any) {
          const unit = lotAssignments.find(a => a.id === input.assignmentId)?.countUnit;
          showToast(claimRefusalText(e?.message || 'Could not take the material', g => fmtQty(g, unit)), 'error');
          return null;
      }
  };

  const returnPackingClaim = async (input: { claimId: string; grams: number }): Promise<boolean> => {
      if (!(input.grams > 0)) { showToast('Say how much you are returning', 'error'); return false; }
      try {
          const row = await sbWarehouseRpc<any>('packing_claim_return', {
              p_claim_id: input.claimId, p_grams: input.grams, p_at: isoFromMs(Date.now()),
          });
          const claim = sbClaimFromRow(row);
          setPackingClaims(prev => prev.map(c => (c.id === claim.id ? claim : c)));
          return true;
      } catch (e: any) {
          const unit = packingClaims.find(c => c.id === input.claimId)?.countUnit;
          showToast(claimRefusalText(e?.message || 'Could not return the material', g => fmtQty(g, unit)), 'error');
          return false;
      }
  };

  // Give back from my table (0122, Umer 2026-10-08): one database call lowers the claim and writes the
  // pending return the Warehouse confirms or refuses — the shared pile's Give Material Back, from a claim.
  const givePackingClaimBack = async (input: { claimId: string; grams: number; notes?: string }): Promise<boolean> => {
      if (!(input.grams > 0)) { showToast('Say how much you are giving back', 'error'); return false; }
      if (!currentUser) { showToast('Not signed in', 'error'); return false; }
      const claim = packingClaims.find(c => c.id === input.claimId);
      try {
          const res = await sbWarehouseRpc<{ claim: any; return: any }>('packing_claim_give_back', {
              p_claim_id: input.claimId, p_grams: input.grams, p_notes: input.notes?.trim() || null,
          });
          const next = sbClaimFromRow(res.claim);
          setPackingClaims(prev => prev.map(c => (c.id === next.id ? next : c)));
          const rt = sbLotReturnFromRow(res.return);
          setLotReturns(prev => [rt, ...prev.filter(r => r.id !== rt.id)]);
          const house = packingHouses.find(h => h.id === next.houseId);
          await logWarehouseHistory({
              productId: 'return',
              productName: next.lotName,
              action: WarehouseHistoryAction.STOCK_EDITED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,  // Nothing changes at lot level until the Warehouse confirms.
              quantityBefore: 0,
              quantityAfter: 0,
              description: `${next.workerName}${house ? ` (${house.name})` : ''} sent ${fmtQty(input.grams, next.countUnit)} bulk of ${next.lotName} back from their table — waiting for WM to confirm receipt`,
          });
          showToast('Sent back — waiting for the Warehouse to confirm', 'success');
          return true;
      } catch (e: any) {
          console.error(e);
          showToast(claimRefusalText(e?.message || 'Could not give the material back', g => fmtQty(g, claim?.countUnit)), 'error');
          return false;
      }
  };

  // A product's size, and nothing else (Umer, 2026-10-08): the Make products dialog used updateProduct, which
  // writes the whole row from this tab's copy — stock included — so an approval landing meanwhile was undone.
  const setProductUnit = async (productId: string, unit: { amount: number; kind: ProductUnitKind }): Promise<boolean> => {
      if (!(unit.amount > 0)) { showToast('Enter what one package is', 'error'); return false; }
      if (!USE_SUPABASE) { showToast('Setting a size needs the Postgres backend', 'error'); return false; }
      try {
          await mustAffectRows('products size', () =>
              supabase().from('products').update({ unit_amount: unit.amount, unit_kind: unit.kind }).eq('id', productId));
          setProducts(prev => prev.map(p => (p.id === productId ? { ...p, unitAmount: unit.amount, unitKind: unit.kind } : p)));
          showToast('Size saved', 'success');
          return true;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Could not save the size', 'error');
          return false;
      }
  };

  const makeBatchFromClaim = async (input: {
      claimId: string; usedGrams: number; outputs: ProductionOutput[]; notes?: string; warnings?: RunWarning[]; adjustLot?: boolean;
  }): Promise<string | null> => {
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }
      if (!(input.usedGrams > 0)) { showToast('Say how much material you used', 'error'); return null; }
      if (input.outputs.length === 0) { showToast('Add at least one product', 'error'); return null; }
      for (const o of input.outputs) {
          if (!(o.qty > 0)) { showToast(`How many packs of ${o.productName}?`, 'error'); return null; }
      }
      const claim = packingClaims.find(c => c.id === input.claimId);
      const house = claim ? packingHouses.find(h => h.id === claim.houseId) : undefined;
      const runId = `run_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      try {
          const res = await sbWarehouseRpc<{ runId: string; variance: number; claimRemaining: number; lotAdjust?: number | null }>('packing_claim_run', {
              p_run: {
                  id: runId,
                  worker_id: currentUser.id,
                  worker_name: currentUser.name,
                  house_id: claim?.houseId ?? null,
                  house_name: house?.name ?? null,
                  performed_at: isoFromMs(Date.now()),
                  performed_by: getPerformedBy(),
                  input_sources: [],
                  total_grams_consumed: 0,
                  outputs: input.outputs,
                  crumbs_grams: 0, waste_grams: 0, variance: 0,
                  status: RunStatus.PENDING_PACKAGER,
                  notes: input.notes?.trim() || null,
                  count_unit: claim?.countUnit ?? null,
                  warnings: input.warnings ?? [],
              },
              p_claim_id: input.claimId,
              p_used: input.usedGrams,
              // Sent only when ticked: a database before 0123 keeps working for every batch without it.
              ...(input.adjustLot ? { p_adjust: true } : {}),
          });
          setPackingClaims(prev => prev.map(c => (c.id === input.claimId
              ? { ...c, gramsRemaining: res.claimRemaining, lastActivityAt: Date.now(), status: res.claimRemaining > 0.001 ? c.status : 'done' }
              : c)));
          await logWarehouseHistory({
              productId: 'run',
              productName: `Batch by ${currentUser.name}${house ? ` at ${house.name}` : ''}`,
              action: WarehouseHistoryAction.STOCK_EDITED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: -input.usedGrams,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `Batch from ${claim?.lotName ?? 'material'}: used ${claim?.countUnit ? `${input.usedGrams} ${claim.countUnit}` : fmtLb(input.usedGrams)}, ${input.outputs.reduce((s, o) => s + o.qty, 0)} packs across ${input.outputs.length} product(s) — waiting for approval${input.warnings?.length ? ` (${input.warnings.length} warning(s) accepted)` : ''}${res.lotAdjust ? `; lot ${res.lotAdjust > 0 ? 'raised' : 'lowered'} by ${claim?.countUnit ? `${Math.abs(res.lotAdjust)} ${claim.countUnit}` : fmtLb(Math.abs(res.lotAdjust))} to what was packed` : ''}`,
          });
          showToast('Batch made — confirm it in Confirm & Stock', 'success');
          return res.runId;
      } catch (e: any) {
          console.error(e);
          showToast(claimRefusalText(e?.message || 'Could not save the batch', g => fmtQty(g, claim?.countUnit)), 'error');
          return null;
      }
  };

  const editBatchFromClaim = async (input: { runId: string; outputs: ProductionOutput[]; notes?: string; warnings?: RunWarning[] }): Promise<boolean> => {
      if (input.outputs.length === 0) { showToast('Add at least one product', 'error'); return false; }
      try {
          await sbWarehouseRpc<any>('packing_claim_run_edit', {
              p_run_id: input.runId, p_outputs: input.outputs, p_notes: input.notes?.trim() || null, p_warnings: input.warnings ?? [],
          });
          showToast('Batch updated — waiting for approval again', 'success');
          return true;
      } catch (e: any) {
          showToast(e?.message || 'Could not update the batch', 'error');
          return false;
      }
  };

  const returnToWarehouse = async (input: {
      lotId: string;
      kind: ReturnKind;
      grams: number;
      notes?: string;
      // Optional — set when a higher-tier user is handing material back on
      // behalf of a specific worker.
      actingAsWorkerId?: string;
      // House mode (0080): the pool the material comes back from.
      houseId?: string;
  }): Promise<string | null> => {
      if (!(input.grams > 0)) { showToast('Grams must be positive', 'error'); return null; }
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }
      const house = input.houseId ? packingHouses.find(h => h.id === input.houseId) : undefined;
      if (input.houseId && !house) { showToast('Packing House not found', 'error'); return null; }

      const canActAsOthers = currentUser.role !== UserRole.WORKER
          && currentUser.role !== UserRole.PACKAGER;
      const targetWorker = (canActAsOthers && input.actingAsWorkerId)
          ? users.find(u => u.id === input.actingAsWorkerId)
          : currentUser;
      if (!targetWorker) { showToast('Selected worker not found', 'error'); return null; }

      const returnId = `rt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const trimmedNotes = input.notes?.trim();

      try {
          let lotName = '';

          // Return is created as PENDING — WM must confirm receipt before the
          // lot gets credited (see confirmLotReturn). Worker holdings drop
          // immediately since the material is physically off their table.
          if (USE_SUPABASE) {
              const sb = supabase();
              const { data: lot, error: lotErr } = await sb.from('inbound_lots').select('id, name').eq('id', input.lotId).maybeSingle();
              if (lotErr) throw new Error(`inbound_lots: ${lotErr.message}`);
              if (!lot) throw new Error('Lot not found');
              lotName = lot.name;
              const doc: LotReturn = {
                  id: returnId,
                  workerId: targetWorker.id,
                  workerName: targetWorker.name,
                  ...(house ? { houseId: house.id, houseName: house.name } : {}),
                  lotId: lot.id,
                  lotName: lot.name,
                  kind: input.kind,
                  grams: input.grams,
                  returnedAt: Date.now(),
                  status: ReturnStatus.PENDING,
                  ...(trimmedNotes ? { notes: trimmedNotes } : {}),
              };
              const { error } = await sb.from('lot_returns').insert(sbLotReturnRow(doc));
              if (error) throw new Error(`lot_returns insert: ${error.message}`);
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const lotRef = dbInstance.collection('inboundLots').doc(input.lotId);
              const rtRef = dbInstance.collection('lotReturns').doc(returnId);

              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(lotRef);
                  if (!snap.exists) throw new Error('Lot not found');
                  const lot = snap.data() as InboundLot;
                  lotName = lot.name;
                  const doc: LotReturn = {
                      id: returnId,
                      workerId: targetWorker.id,
                      workerName: targetWorker.name,
                      lotId: lot.id,
                      lotName: lot.name,
                      kind: input.kind,
                      grams: input.grams,
                      returnedAt: Date.now(),
                      status: ReturnStatus.PENDING,
                      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
                  };
                  tx.set(rtRef, doc);
              });
          }

          const onBehalf = targetWorker.id !== currentUser.id ? ` (logged by ${currentUser.name})` : '';
          await logWarehouseHistory({
              productId: 'return',
              productName: lotName,
              action: WarehouseHistoryAction.STOCK_EDITED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,  // Nothing changes at lot level until WM confirms.
              quantityBefore: 0,
              quantityAfter: 0,
              description: `${targetWorker.name}${house ? ` (${house.name})` : ''}${onBehalf} sent ${fmtLb(input.grams)} ${input.kind} of ${lotName} back — waiting for WM to confirm receipt`,
          });
          showToast('Sent back — waiting for WM to confirm', 'success');
          return returnId;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to return to warehouse', 'error');
          return null;
      }
  };

  // WM confirms they physically received the material. For bulk returns this
  // credits the lot's remainingGrams so it can be re-assigned. Crumb returns
  // just get marked confirmed — nothing else moves.
  const confirmLotReturn = async (returnId: string) => {
      try {
          if (USE_SUPABASE) {
              await sbWarehouseRpc<null>('warehouse_confirm_return',
                  { p_return_id: returnId, p_at: isoFromMs(Date.now()), p_by: getPerformedBy() });
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const rtRef = dbInstance.collection('lotReturns').doc(returnId);
              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(rtRef);
                  if (!snap.exists) throw new Error('Return not found');
                  const rt = snap.data() as LotReturn;
                  const status = rt.status ?? ReturnStatus.CONFIRMED; // legacy default
                  if (status !== ReturnStatus.PENDING) throw new Error(`Return is already ${status}`);
                  if (rt.kind === ReturnKind.BULK) {
                      const lotRef = dbInstance.collection('inboundLots').doc(rt.lotId);
                      tx.update(lotRef, {
                          remainingGrams: firebase.firestore.FieldValue.increment(rt.grams),
                      });
                  }
                  tx.update(rtRef, {
                      status: ReturnStatus.CONFIRMED,
                      confirmedAt: Date.now(),
                      confirmedBy: getPerformedBy(),
                  });
              });
          }
          showToast('Return confirmed', 'success');
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to confirm return', 'error');
      }
  };

  // WM rejects the return — material stays with the worker (their holdings
  // pop back up since the derivation only subtracts non-rejected returns).
  const rejectLotReturn = async (returnId: string, reason: string) => {
      try {
          const trimmed = reason.trim();
          if (!trimmed) { showToast('Rejection reason required', 'error'); return; }
          if (USE_SUPABASE) {
              const sb = supabase();
              const { data, error } = await sb.from('lot_returns').update({
                  status: ReturnStatus.REJECTED,
                  confirmed_at: isoFromMs(Date.now()),
                  confirmed_by: getPerformedBy(),
                  rejection_reason: trimmed,
              }).eq('id', returnId).eq('status', ReturnStatus.PENDING).select('id');
              if (error) throw new Error(`lot_returns update: ${error.message}`);
              if (!data?.length) {
                  const { data: row } = await sb.from('lot_returns').select('status').eq('id', returnId).maybeSingle();
                  if (!row) throw new Error('Return not found');
                  throw new Error(`Return is already ${row.status ?? ReturnStatus.CONFIRMED}`);
              }
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const rtRef = dbInstance.collection('lotReturns').doc(returnId);
              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(rtRef);
                  if (!snap.exists) throw new Error('Return not found');
                  const rt = snap.data() as LotReturn;
                  const status = rt.status ?? ReturnStatus.CONFIRMED;
                  if (status !== ReturnStatus.PENDING) throw new Error(`Return is already ${status}`);
                  tx.update(rtRef, {
                      status: ReturnStatus.REJECTED,
                      confirmedAt: Date.now(),
                      confirmedBy: getPerformedBy(),
                      rejectionReason: trimmed,
                  });
              });
          }
          showToast('Return sent back to worker', 'success');
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to reject return', 'error');
      }
  };

  // Combine one or more chunks of bulk into a single new named material.
  //   - Single source = Split (e.g. worker splitting off part of a lot)
  //   - Multiple sources = Mix (worker combining pieces of different lots,
  //     or admin/WM combining pieces of different lots on the shelf)
  //
  // Two routing modes based on source type:
  //   Worker sources (sourceAssignmentId): every source must belong to the
  //     same worker; the new lot is assigned back to that worker as fresh
  //     bulk. Their assignments are debited by their contributions.
  //   Shelf sources (sourceLotId, no assignment): admin/WM path — sources
  //     are lots' on-shelf remainingGrams. The new lot stays on the shelf
  //     unassigned so it can be assigned out or drawn from later.
  //
  // Mixing worker + shelf sources in one call is rejected — the routing
  // would be ambiguous.
  const combineIntoNewMaterial = async (input: {
      sources: Array<{ sourceAssignmentId?: string; sourceLotId?: string; grams: number }>;
      newLotName: string;
      notes?: string;
  }): Promise<string | null> => {
      if (!currentUser) { showToast('Not signed in', 'error'); return null; }
      if (input.sources.length === 0) { showToast('Pick at least one source', 'error'); return null; }
      for (const s of input.sources) {
          if (!(s.grams > 0)) { showToast('Every source needs positive grams', 'error'); return null; }
          if (!s.sourceAssignmentId && !s.sourceLotId) { showToast('Every source needs a lot or assignment', 'error'); return null; }
          if (s.sourceAssignmentId && s.sourceLotId) { showToast('Source has both assignment and lot — pick one', 'error'); return null; }
      }
      const trimmedName = input.newLotName.trim();
      if (!trimmedName) { showToast('New material needs a name', 'error'); return null; }

      const workerSources = input.sources.filter(s => !!s.sourceAssignmentId);
      const shelfSources = input.sources.filter(s => !!s.sourceLotId);
      if (workerSources.length > 0 && shelfSources.length > 0) {
          showToast('Cannot mix worker material with shelf material in one step', 'error');
          return null;
      }

      const newLotId = `lot_derived_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newAsnId = workerSources.length > 0 ? `asn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : '';
      const trimmedNotes = input.notes?.trim();
      const totalGrams = input.sources.reduce((s, x) => s + x.grams, 0);

      try {
          const parents: Array<{ id: string; name: string; grams: number }> = [];
          let ownerWorker: { id: string; name: string } | null = null;
          let humanParentList = '';

          if (USE_SUPABASE) {
              // The source names + owning worker are only known after the reads,
              // so the new lot/assignment rows are assembled inside the function.
              const res = await sbWarehouseRpc<{ parents: Array<{ id: string; name: string; grams: number }> }>(
                  'warehouse_combine_material',
                  {
                      p_new_lot_id: newLotId,
                      p_new_asn_id: newAsnId || null,
                      p_name: trimmedName,
                      p_notes: trimmedNotes || null,
                      p_total_grams: totalGrams,
                      p_worker_sources: workerSources.map(s => ({ assignmentId: s.sourceAssignmentId, grams: s.grams })),
                      p_shelf_sources: shelfSources.map(s => ({ lotId: s.sourceLotId, grams: s.grams })),
                      p_at: isoFromMs(Date.now()),
                      p_by: getPerformedBy(),
                      p_actor_id: currentUser.id,
                      p_actor_name: currentUser.name,
                  },
              );
              parents.push(...res.parents);
              humanParentList = parents.map(p => p.name).join(' + ');
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const newLotRef = dbInstance.collection('inboundLots').doc(newLotId);

              await dbInstance.runTransaction(async (tx) => {
                  if (workerSources.length > 0) {
                      // Worker path — read + debit each assignment.
                      const asnRefs = workerSources.map(s => dbInstance.collection('lotAssignments').doc(s.sourceAssignmentId!));
                      const asnSnaps = await Promise.all(asnRefs.map(r => tx.get(r)));
                      for (let i = 0; i < asnSnaps.length; i++) {
                          if (!asnSnaps[i].exists) throw new Error('One of the source assignments no longer exists');
                          const asn = asnSnaps[i].data() as LotAssignment;
                          if (asn.status !== AssignmentStatus.ACTIVE) throw new Error(`Source ${asn.lotName} is closed`);
                          const take = workerSources[i].grams;
                          if (take > asn.remainingGrams + 0.001) {
                              throw new Error(`${asn.lotName}: only ${asn.remainingGrams.toFixed(1)}g remaining`);
                          }
                          if (!ownerWorker) ownerWorker = { id: asn.workerId, name: asn.workerName };
                          else if (ownerWorker.id !== asn.workerId) throw new Error('All sources must belong to the same worker');
                          parents.push({ id: asn.lotId, name: asn.lotName, grams: take });
                      }
                      for (let i = 0; i < asnRefs.length; i++) {
                          tx.update(asnRefs[i], { remainingGrams: firebase.firestore.FieldValue.increment(-workerSources[i].grams) });
                      }
                  } else {
                      // Shelf path — admin/WM only. Debit each lot's remainingGrams.
                      const isPrivileged = isSuperTier(currentUser.role)
                          || currentUser.role === UserRole.ADMIN
                          || currentUser.role === UserRole.WAREHOUSE_MANAGER;
                      if (!isPrivileged) throw new Error('Only warehouse manager or admin can mix from the shelf');
                      const lotRefs = shelfSources.map(s => dbInstance.collection('inboundLots').doc(s.sourceLotId!));
                      const lotSnaps = await Promise.all(lotRefs.map(r => tx.get(r)));
                      for (let i = 0; i < lotSnaps.length; i++) {
                          if (!lotSnaps[i].exists) throw new Error('One of the source lots no longer exists');
                          const lot = lotSnaps[i].data() as InboundLot;
                          if (lot.status === LotStatus.DEPLETED || lot.status === LotStatus.CLOSED_WITH_VARIANCE) {
                              throw new Error(`Source ${lot.name} is closed`);
                          }
                          const take = shelfSources[i].grams;
                          if (take > lot.remainingGrams + 0.001) {
                              throw new Error(`${lot.name}: only ${lot.remainingGrams.toFixed(1)}g on shelf`);
                          }
                          parents.push({ id: lot.id, name: lot.name, grams: take });
                      }
                      for (let i = 0; i < lotRefs.length; i++) {
                          tx.update(lotRefs[i], {
                              remainingGrams: firebase.firestore.FieldValue.increment(-shelfSources[i].grams),
                              status: LotStatus.IN_PRODUCTION,
                          });
                      }
                  }

                  humanParentList = parents.map(p => p.name).join(' + ');

                  // For worker path, all of the new lot's material is immediately
                  // routed to the worker via a new assignment, so remainingGrams=0.
                  // For shelf path, no assignment is created and the material sits
                  // on the shelf ready for reassignment/draw.
                  const newLot: InboundLot = {
                      id: newLotId,
                      name: trimmedName,
                      totalGrams,
                      remainingGrams: workerSources.length > 0 ? 0 : totalGrams,
                      status: LotStatus.IN_PRODUCTION,
                      receivedAt: Date.now(),
                      receivedBy: getPerformedBy(),
                      origin: 'worker_derived',
                      derivedFrom: {
                          parents,
                          workerId: ownerWorker?.id || currentUser.id,
                          workerName: ownerWorker?.name || currentUser.name,
                          createdAt: Date.now(),
                      },
                      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
                  };
                  tx.set(newLotRef, newLot);

                  if (workerSources.length > 0 && ownerWorker) {
                      const owner = ownerWorker as { id: string; name: string };
                      const newAsn: LotAssignment = {
                          id: newAsnId,
                          lotId: newLotId,
                          lotName: trimmedName,
                          workerId: owner.id,
                          workerName: owner.name,
                          grantedGrams: totalGrams,
                          remainingGrams: totalGrams,
                          status: AssignmentStatus.ACTIVE,
                          assignedAt: Date.now(),
                          assignedBy: getPerformedBy(),
                          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
                      };
                      tx.set(dbInstance.collection('lotAssignments').doc(newAsnId), newAsn);
                  }
              });
          }

          await logWarehouseHistory({
              productId: 'derived_lot',
              productName: `${trimmedName} (from ${humanParentList})`,
              action: WarehouseHistoryAction.STOCK_EDITED,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: 0,
              quantityBefore: 0,
              quantityAfter: 0,
              description: `${currentUser.name} combined ${fmtLb(totalGrams)} from ${parents.length} source(s) into new material "${trimmedName}"`,
          });
          showToast(`Created new material: ${trimmedName}`, 'success');
          return newLotId;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to create new material', 'error');
          return null;
      }
  };

  const closeLot = async (lotId: string, _acceptedVariance: number) => {
      try {
          let closedRemaining = 0;
          let lotName = '';

          if (USE_SUPABASE) {
              const res = await sbWarehouseRpc<{ name: string; remainingGrams: number }>('warehouse_close_lot',
                  { p_lot_id: lotId, p_at: isoFromMs(Date.now()), p_by: getPerformedBy() });
              lotName = res.name;
              closedRemaining = numOr0(res.remainingGrams);
          } else {
              const { db: dbInstance } = ensureFirebaseInitialized();
              const lotRef = dbInstance.collection('inboundLots').doc(lotId);

              await dbInstance.runTransaction(async (tx) => {
                  const snap = await tx.get(lotRef);
                  if (!snap.exists) throw new Error('Lot not found');
                  const lot = snap.data() as InboundLot;
                  if (lot.status === LotStatus.DEPLETED || lot.status === LotStatus.CLOSED_WITH_VARIANCE) {
                      throw new Error('Lot is already closed');
                  }
                  closedRemaining = lot.remainingGrams;
                  lotName = lot.name;
                  // If nothing is left on the shelf, this is a clean depletion —
                  // not a variance write-off. Reserve CLOSED_WITH_VARIANCE for
                  // when the WM is actually writing off unaccounted material.
                  const isCleanDepletion = closedRemaining < 0.01;
                  tx.update(lotRef, {
                      status: isCleanDepletion ? LotStatus.DEPLETED : LotStatus.CLOSED_WITH_VARIANCE,
                      closedAt: Date.now(),
                      closedBy: getPerformedBy(),
                      varianceGrams: closedRemaining,
                      remainingGrams: 0,
                  });
              });
          }

          await logWarehouseHistory({
              productId: 'lot',
              productName: lotName,
              action: WarehouseHistoryAction.STOCK_RESET,
              timestamp: Date.now(),
              performedBy: getPerformedBy(),
              quantityChange: -closedRemaining,
              quantityBefore: closedRemaining,
              quantityAfter: 0,
              description: `Lot closed — ${fmtLb(closedRemaining)} unaccounted written off as variance`,
          });
          showToast('Lot closed', 'success');
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to close lot', 'error');
      }
  };

  // Derived: per-lot bulk + crumbs balance for a worker.
  //   bulkGrams  = sum(assignment.remainingGrams for that worker+lot)
  //                (assignments are auto-debited by runs)
  //   crumbsGrams= crumbs generated by this worker attributed to this lot,
  //                minus crumbs consumed by this worker from this lot,
  //                minus crumbs returned to WM.
  // Crumb attribution on runs: when a mixed-input run generates C grams of
  // crumbs, each input lot gets a proportional share (grams from lot / total).
  // One ledger, two keys: a person (legacy grants) or a Packing House pool
  // (0080) — `owns` decides which rows count.
  const computeHoldings = (owns: (row: { workerId: string; houseId?: string }) => boolean) => {
      const holdings = new Map<string, { lotId: string; lotName: string; bulkGrams: number; crumbsGrams: number; assignmentId?: string }>();

      // Bulk from active assignments.
      for (const a of lotAssignments) {
          if (!owns(a)) continue;
          const key = a.lotId;
          const prev = holdings.get(key) || { lotId: a.lotId, lotName: a.lotName, bulkGrams: 0, crumbsGrams: 0, assignmentId: a.id };
          prev.bulkGrams += a.remainingGrams;
          if (!prev.assignmentId) prev.assignmentId = a.id;
          holdings.set(key, prev);
      }

      // Crumbs: sum generated − consumed. Attribution is proportional to the
      // per-lot share of the mixed input on the run that generated them.
      for (const r of productionRuns) {
          if (!owns(r)) continue;
          if (r.status === RunStatus.REJECTED) continue;

          // Crumbs generated → attributed proportionally to input lots.
          if (r.crumbsGrams > 0 && r.totalGramsConsumed > 0) {
              const perLotInput = new Map<string, { name: string; grams: number }>();
              for (const s of r.inputSources) {
                  const prev = perLotInput.get(s.lotId) || { name: s.lotName, grams: 0 };
                  prev.grams += s.grams;
                  perLotInput.set(s.lotId, prev);
              }
              for (const [lotId, info] of perLotInput) {
                  const share = (info.grams / r.totalGramsConsumed) * r.crumbsGrams;
                  const key = lotId;
                  const prev = holdings.get(key) || { lotId, lotName: info.name, bulkGrams: 0, crumbsGrams: 0 };
                  prev.crumbsGrams += share;
                  holdings.set(key, prev);
              }
          }

          // Crumbs consumed on this run.
          for (const s of r.inputSources) {
              if (s.kind === 'crumbs') {
                  const key = s.lotId;
                  const prev = holdings.get(key) || { lotId: s.lotId, lotName: s.lotName, bulkGrams: 0, crumbsGrams: 0 };
                  prev.crumbsGrams -= s.grams;
                  holdings.set(key, prev);
              }
          }
      }

      // Returns to the warehouse — subtract both bulk and crumb returns.
      // Rejected returns (WM refused) count as "worker still holds it", so
      // they're skipped. Both PENDING and CONFIRMED returns reduce holdings
      // — the material physically left the worker's table the moment they
      // recorded the return; WM just hasn't rubber-stamped receipt yet.
      // Legacy returns without a status field are treated as confirmed.
      for (const rt of lotReturns) {
          if (!owns(rt)) continue;
          const status = rt.status ?? ReturnStatus.CONFIRMED;
          if (status === ReturnStatus.REJECTED) continue;
          const key = rt.lotId;
          const prev = holdings.get(key) || { lotId: rt.lotId, lotName: rt.lotName, bulkGrams: 0, crumbsGrams: 0 };
          if (rt.kind === ReturnKind.BULK) prev.bulkGrams -= rt.grams;
          else prev.crumbsGrams -= rt.grams;
          holdings.set(key, prev);
      }

      return Array.from(holdings.values())
          .map(h => ({ ...h, bulkGrams: Math.max(0, h.bulkGrams), crumbsGrams: Math.max(0, h.crumbsGrams) }))
          .filter(h => h.bulkGrams > 0.001 || h.crumbsGrams > 0.001);
  };
  const getWorkerHoldings = (workerId: string) => computeHoldings(r => r.workerId === workerId);
  const getHouseHoldings = (houseId: string) => computeHoldings(r => r.houseId === houseId);

  // ─── Packing Houses (migration 0080) ─────────────────────────────────────
  // Supabase only: in Firestore mode the arrays stay empty and every page
  // keeps the one-person-per-grant behaviour (services/packingHouses.ts).
  const createPackingHouse = async (input: { name: string; address?: string; notes?: string }): Promise<string | null> => {
      const name = input.name.trim();
      if (!name) { showToast('The Packing House needs a name', 'error'); return null; }
      if (!USE_SUPABASE) { showToast('Packing Houses need the Supabase backend', 'error'); return null; }
      const id = `ph_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      try {
          const { error } = await supabase().from('packing_houses').insert({
              id, name,
              address: input.address?.trim() || null,
              notes: input.notes?.trim() || null,
              is_active: true,
              created_by: currentUser?.id ?? null,
          });
          if (error) throw new Error(`packing_houses insert: ${error.message}`);
          showToast(`Packing House ${name} created`, 'success');
          return id;
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to create Packing House', 'error');
          return null;
      }
  };
  const updatePackingHouse = async (house: PackingHouse): Promise<void> => {
      if (!USE_SUPABASE) return;
      const name = house.name.trim();
      if (!name) { showToast('The Packing House needs a name', 'error'); return; }
      try {
          const { error } = await supabase().from('packing_houses').update({
              name,
              address: house.address?.trim() || null,
              notes: house.notes?.trim() || null,
              is_active: house.isActive,
          }).eq('id', house.id);
          if (error) throw new Error(`packing_houses update: ${error.message}`);
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to update Packing House', 'error');
          throw e;
      }
  };
  const addPackingHouseMember = async (houseId: string, userId: string): Promise<void> => {
      if (!USE_SUPABASE) return;
      const user = users.find(u => u.id === userId);
      if (!user || !canJoinPackingHouse(user.role)) { showToast('Only Packager accounts can join a Packing House', 'error'); return; }
      if (isHouseMember(userId, houseId, packingHouseMembers)) return;
      try {
          const { error } = await supabase().from('packing_house_members').insert({
              house_id: houseId, user_id: userId, assigned_by: currentUser?.id ?? null,
          });
          if (error && error.code !== '23505') throw new Error(`packing_house_members insert: ${error.message}`);
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to add the packager', 'error');
          throw e;
      }
  };
  const removePackingHouseMember = async (houseId: string, userId: string): Promise<void> => {
      if (!USE_SUPABASE) return;
      try {
          const { error } = await supabase().from('packing_house_members').delete()
              .eq('house_id', houseId).eq('user_id', userId);
          if (error) throw new Error(`packing_house_members delete: ${error.message}`);
      } catch (e: any) {
          console.error(e);
          showToast(e?.message || 'Failed to remove the packager', 'error');
          throw e;
      }
  };

  const value: AppState = {
      // `orders` is role-scoped for DISPATCH sessions (services/orderScope);
      // `allOrders` is the unscoped array for admin tooling.
      currentUser, setCurrentUser, users, products, storeStocks, driverStocks, transactions, orders: scopedOrders, allOrders: orders, twilioNumbers, storeTempManagers, packingHouses, packingHouseMembers, rooms,
      inboundLots, lotAssignments, productionRuns, lotReturns,
      createPackingHouse, updatePackingHouse, addPackingHouseMember, removePackingHouseMember, getHouseHoldings,
      packingClaims, packingClaimsReady, takePackingClaim, returnPackingClaim, givePackingClaimBack, setProductUnit, makeBatchFromClaim, editBatchFromClaim,
      updateUser, deleteUser, adminResetUserPassword, changePassword,
      addProduct, updateProduct, deleteProduct,
      createTransaction, updateTransactionStatus, deleteTransaction,
      createOrder, updateOrder, patchOrder, updateOrderDetails, assignOrder, assignOrderToDriver, assignDriver, unassignOrder, unassignStore, initiateDriverReturn, cancelOrder, restoreOrder,
      driverConfirmReturn, officeTakeBack, initiateDriverTransfer, approveDriverTransfer, assignTempPacker, removeTempPacker,
      markOrderRead, markChatRead, markChatReadBatch, getUnreadChatCounts, markTabOpened,
      getStoreStock, getDriverStock, getStoreCashBalance, getDriverCashBalance, confirmStorePickup, markOrderDelivered, reverseOrder,
      login, signup, adminCreateUser, logout, resetData, seedDatabase, sendSMS, sendCustomerMessage, callCustomer, clearSystemData, seedWarehouseHistory, seedStoreHistory, seedDriverHistory,
      addTwilioNumber, assignTwilioNumberToDriver, setDefaultTwilioNumber, removeTwilioNumber,
      dayZeroTimestamp, performDayZero, resetAllStoreStock,
      loadOrdersByDateRange, loadTransactionsByDateRange, ordersDateRange, transactionsDateRange,
      createInboundLot, assignBulkToWorker, directAssignFromWarehouse, packIntoStorageHouse, resendDirectPacks, createProductionRun, confirmProductionRun, transferPackedToInventory, rejectProductionRun, updateProductionRun, returnToWarehouse, confirmLotReturn, rejectLotReturn, closeLot, combineIntoNewMaterial, getWorkerHoldings,
      loading, sessionPending, retrySession, ordersLoaded, toasts, showToast, removeToast
  };
  // Publish the latest value to selective subscribers (useAppSelector / useAppActions).
  storeRef.current = value;

  return (
    <AppSelectorContext.Provider value={selectorStore}>
      <AppActionsContext.Provider value={stableActions}>
        <AppContext.Provider value={value}>
          {children}
        </AppContext.Provider>
      </AppActionsContext.Provider>
    </AppSelectorContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};

// Subscribe to ONE slice of app state. Re-renders the caller only when the
// selected value changes (Object.is) — not on every Firestore snapshot the way
// useApp() does. The selector MUST return a stable reference for unchanged data
// (a state slice, not a freshly-built object).
export function useAppSelector<T>(selector: (s: AppState) => T): T {
  const store = useContext(AppSelectorContext);
  if (!store) throw new Error('useAppSelector must be used within AppProvider');
  const read = () => selector(store.getSnapshot());
  return useSyncExternalStore(store.subscribe, read, read);
}

// Access the app's action functions with a STABLE reference — using these never
// triggers a re-render on data changes. For actions only; read data with
// useAppSelector or useApp.
export function useAppActions(): AppState {
  const actions = useContext(AppActionsContext);
  if (!actions) throw new Error('useAppActions must be used within AppProvider');
  return actions;
}

// Live driver positions keyed by driver id, on a dedicated channel: GPS pings
// re-render ONLY the callers of this hook (the delivery map), never the users
// array or the provider. The map is replaced wholesale when any position
// changes, so the reference is stable between changes.
export function useDriverLocations(): DriverLocationMap {
  const read = () => driverLocationsRef.current;
  return useSyncExternalStore(subscribeDriverLocations, read, read);
}
